/**
 * Client types for sector operations.
 * R39 A2.a (plan docs/plans/sector-operations.md §4).
 * Mirrored from functions-common/src/ops/places.ts.
 */

import { formatZonedDateKey, zonedTimeToUtc } from '../charts/engine/bucket'
import { dayKeyToUtcMs, resolveWorkspaceTimeZone, utcMsToDayKey } from '../services/repeat/keys'
import type { OpsRoomState } from '../../functions-common/src/ops/roomStates'
import type { BookingSeriesPattern } from './bookingsCore'

export const OPS_SECTOR_KEYS = [
  'construction',
  'hospital',
  'sites',
  'delivery',
  'shops',
  'stays',
  'jobs',
  'clinics',
  'plant',
] as const
export type OpsSector = (typeof OPS_SECTOR_KEYS)[number]

export const SITES_PRESETS = ['GUARDING', 'FACILITY'] as const
export type SitesPreset = (typeof SITES_PRESETS)[number]

export const SHOPS_PRESETS = [
  'GENERAL',
  'APPAREL_ELECTRONICS',
  'FOOD',
  'PHARMACY_OPS',
  'RESTAURANT',
] as const
export type ShopsPreset = (typeof SHOPS_PRESETS)[number]

export const STAYS_PRESETS = [
  'BUDGET_HOTEL',
  'BUSINESS_HOTEL',
  'RESORT_HERITAGE',
  'HOMESTAY_VILLA',
  'SERVICED_APARTMENT',
  'HOSTEL',
] as const
export type StaysPreset = (typeof STAYS_PRESETS)[number]

export const CLINICS_SITE_KINDS = [
  'CLINIC',
  'LAB',
  'HUB',
  'COLLECTION_CENTRE',
  'PHARMACY',
  'WAREHOUSE',
  'CAMP',
] as const
export type ClinicsSiteKind = (typeof CLINICS_SITE_KINDS)[number]

export const CLINICS_PRESET_FAMILIES = [
  'CONSULT',
  'PROCEDURE',
  'LABS',
  'IMAGING',
  'BLOOD_BANK',
  'PHARMACY',
  'VET',
] as const
export type ClinicsPresetFamily = (typeof CLINICS_PRESET_FAMILIES)[number]

export const CLINICS_PRESETS = [
  'SINGLE_DOCTOR_FAMILY',
  'POLYCLINIC',
  'MATERNITY_CHILD',
  'MENTAL_HEALTH',
  'AYUSH_WELLNESS',
  'VACCINATION',
  'DENTAL',
  'EYE_OPTICAL',
  'SKIN_COSMETIC_HAIR',
  'IVF',
  'PHYSIO_REHAB',
  'DIALYSIS',
  'DAY_CARE_NURSING_HOME',
  'PATHOLOGY_LAB',
  'COLLECTION_CENTRE',
  'HOME_COLLECTION',
  'IMAGING_CENTRE',
  'BLOOD_BANK',
  'PHARMACY_CHAIN',
  'PHARMA_DEVICE_DISTRIBUTOR',
  'VET_CLINIC',
] as const
export type ClinicsPreset = (typeof CLINICS_PRESETS)[number]

export interface ClinicsPresetMetadata {
  readonly key: ClinicsPreset
  readonly family: ClinicsPresetFamily
  readonly defaultSiteKind: ClinicsSiteKind
  readonly kitSlug: string
}

export const CLINICS_PRESET_METADATA: Record<ClinicsPreset, ClinicsPresetMetadata> = {
  SINGLE_DOCTOR_FAMILY: {
    key: 'SINGLE_DOCTOR_FAMILY',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'single-doctor-family-clinic',
  },
  POLYCLINIC: {
    key: 'POLYCLINIC',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'polyclinic',
  },
  MATERNITY_CHILD: {
    key: 'MATERNITY_CHILD',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'maternity-child-clinic',
  },
  MENTAL_HEALTH: {
    key: 'MENTAL_HEALTH',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'mental-health-clinic',
  },
  AYUSH_WELLNESS: {
    key: 'AYUSH_WELLNESS',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'ayush-wellness',
  },
  VACCINATION: {
    key: 'VACCINATION',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'vaccination-centre',
  },
  DENTAL: {
    key: 'DENTAL',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'dental-clinic',
  },
  EYE_OPTICAL: {
    key: 'EYE_OPTICAL',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'eye-optical',
  },
  SKIN_COSMETIC_HAIR: {
    key: 'SKIN_COSMETIC_HAIR',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'skin-cosmetic-hair',
  },
  IVF: {
    key: 'IVF',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'ivf-centre',
  },
  PHYSIO_REHAB: {
    key: 'PHYSIO_REHAB',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'physio-rehab',
  },
  DIALYSIS: {
    key: 'DIALYSIS',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'dialysis-centre',
  },
  DAY_CARE_NURSING_HOME: {
    key: 'DAY_CARE_NURSING_HOME',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'day-care-nursing-home',
  },
  PATHOLOGY_LAB: {
    key: 'PATHOLOGY_LAB',
    family: 'LABS',
    defaultSiteKind: 'LAB',
    kitSlug: 'pathology-lab',
  },
  COLLECTION_CENTRE: {
    key: 'COLLECTION_CENTRE',
    family: 'LABS',
    defaultSiteKind: 'COLLECTION_CENTRE',
    kitSlug: 'collection-centre',
  },
  HOME_COLLECTION: {
    key: 'HOME_COLLECTION',
    family: 'LABS',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'home-collection',
  },
  IMAGING_CENTRE: {
    key: 'IMAGING_CENTRE',
    family: 'IMAGING',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'imaging-centre',
  },
  BLOOD_BANK: {
    key: 'BLOOD_BANK',
    family: 'BLOOD_BANK',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'blood-bank',
  },
  PHARMACY_CHAIN: {
    key: 'PHARMACY_CHAIN',
    family: 'PHARMACY',
    defaultSiteKind: 'PHARMACY',
    kitSlug: 'pharmacy-chain',
  },
  PHARMA_DEVICE_DISTRIBUTOR: {
    key: 'PHARMA_DEVICE_DISTRIBUTOR',
    family: 'PHARMACY',
    defaultSiteKind: 'WAREHOUSE',
    kitSlug: 'pharma-device-distributor',
  },
  VET_CLINIC: {
    key: 'VET_CLINIC',
    family: 'VET',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'vet-clinic',
  },
}

export const STAYS_STARTER_DEPARTMENTS = [
  { key: 'HOUSEKEEPING', label: 'Housekeeping', archived: false },
  { key: 'FRONT_OFFICE', label: 'Front office', archived: false },
  { key: 'MAINTENANCE', label: 'Maintenance', archived: false },
] as const

export const STAYS_STARTER_DUTY_ROLES = [
  { key: 'room_attendant', label: 'Room attendant', sector: 'stays', department: 'HOUSEKEEPING' },
  { key: 'hk_supervisor', label: 'Housekeeping supervisor', sector: 'stays', department: 'HOUSEKEEPING' },
  { key: 'front_desk', label: 'Front desk', sector: 'stays', department: 'FRONT_OFFICE' },
  { key: 'runner', label: 'Runner', sector: 'stays', department: 'FRONT_OFFICE' },
  { key: 'maintenance_tech', label: 'Maintenance technician', sector: 'stays', department: 'MAINTENANCE' },
] as const

export const JOBS_STARTER_DEPARTMENTS = [
  { key: 'FIELD', label: 'Field', archived: false },
  { key: 'OFFICE', label: 'Office', archived: false },
] as const

export const JOBS_STARTER_DUTY_ROLES = [
  { key: 'technician', label: 'Technician', sector: 'jobs', department: 'FIELD' },
  { key: 'technician_helper', label: 'Technician helper', sector: 'jobs', department: 'FIELD' },
  { key: 'dispatcher', label: 'Dispatcher', sector: 'jobs', department: 'OFFICE' },
] as const

export const HOSPITAL_STARTER_DEPARTMENTS = [
  { key: 'EMERGENCY', label: 'Emergency', archived: false },
  { key: 'OPD', label: 'OPD', archived: false },
  { key: 'IPD_WARDS', label: 'Inpatient wards', archived: false },
  { key: 'ICU', label: 'ICU', archived: false },
  { key: 'OT', label: 'Operation theatre', archived: false },
  { key: 'LABOUR_ROOM', label: 'Labour room', archived: false },
  { key: 'RADIOLOGY', label: 'Radiology', archived: false },
  { key: 'LAB', label: 'Laboratory', archived: false },
  { key: 'PHARMACY_STORES', label: 'Pharmacy & stores', archived: false },
  { key: 'HOUSEKEEPING', label: 'Housekeeping', archived: false },
  { key: 'BIOMEDICAL', label: 'Biomedical engineering', archived: false },
  { key: 'FRONT_DESK', label: 'Front desk', archived: false },
  { key: 'ADMIN', label: 'Administration', archived: false },
] as const

export const HOSPITAL_STARTER_DUTY_ROLES = [
  { key: 'unit_in_charge', label: 'Unit in-charge', sector: 'hospital', department: 'IPD_WARDS' },
  { key: 'unit_backup', label: 'Unit backup', sector: 'hospital', department: 'IPD_WARDS' },
  { key: 'unit_on_call', label: 'Unit on-call', sector: 'hospital', department: 'IPD_WARDS' },
  { key: 'hk_supervisor', label: 'Housekeeping supervisor', sector: 'hospital', department: 'HOUSEKEEPING' },
  { key: 'housekeeping_staff', label: 'Housekeeping staff', sector: 'hospital', department: 'HOUSEKEEPING' },
  { key: 'biomedical_engineer', label: 'Biomedical engineer', sector: 'hospital', department: 'BIOMEDICAL' },
  { key: 'front_desk', label: 'Front desk', sector: 'hospital', department: 'FRONT_DESK' },
  { key: 'duty_manager', label: 'Duty manager', sector: 'hospital', department: 'ADMIN' },
] as const

export interface StaysPresetStarterSchedule {
  key: string
  title: string
  templateSlug: string
  targetLevel: 'ROOM' | 'AREA'
  cadence: { kind: 'DAILY' }
  window: { startMinute: number; durationMinutes: number }
  dutyRoleKey: string
}

export const STAYS_STARTER_SCHEDULES: StaysPresetStarterSchedule[] = [
  {
    key: 'daily_room_checklist',
    title: 'Daily room checklist',
    templateSlug: 'default',
    targetLevel: 'ROOM',
    cadence: { kind: 'DAILY' },
    window: { startMinute: 540, durationMinutes: 480 },
    dutyRoleKey: 'room_attendant',
  },
  {
    key: 'daily_public_area_checklist',
    title: 'Daily public-area checklist',
    templateSlug: 'public-area-daily-cleaning',
    targetLevel: 'AREA',
    cadence: { kind: 'DAILY' },
    window: { startMinute: 480, durationMinutes: 600 },
    dutyRoleKey: 'room_attendant',
  },
]

export const PLANT_PRESETS = [
  'GARMENTS_TEXTILES',
  'FOOD_PROCESSING',
  'ENGINEERING_METAL',
  'GENERIC_MSME',
] as const
export type PlantPreset = (typeof PLANT_PRESETS)[number]

export type OpsPreset = SitesPreset | ShopsPreset | StaysPreset | ClinicsPreset | PlantPreset

export const SHOPS_ZONE_KINDS = [
  'FLOOR',
  'COUNTER',
  'GODOWN',
  'COLD_ROOM',
  'STATION',
] as const
export type ShopsZoneKind = (typeof SHOPS_ZONE_KINDS)[number]

export const STAYS_BLOCK_KINDS = [
  'FLOOR',
  'BLOCK',
  'WING',
  'VILLA',
] as const
export type StaysBlockKind = (typeof STAYS_BLOCK_KINDS)[number]

export const STAYS_AREA_KINDS = [
  'LOBBY',
  'CORRIDOR',
  'WASHROOM',
  'POOL',
  'GYM',
  'SPA',
  'KITCHEN',
  'LAUNDRY',
  'STORE',
  'GARDEN',
  'OTHER',
] as const
export type StaysAreaKind = (typeof STAYS_AREA_KINDS)[number]

export const STAYS_PLACE_WRITE_KEYS = [
  'level',
  'name',
  'code',
  'preset',
  'blockKind',
  'areaKind',
  'children',
  'roomTypes',
  'roomTypeId',
  'minibarCheckOnCheckout',
] as const
export type StaysPlaceWriteKey = (typeof STAYS_PLACE_WRITE_KEYS)[number]

export const JOBS_PLACE_WRITE_KEYS = [
  'level',
  'name',
  'code',
  'children',
  'customerId',
  'address',
  'geo',
  'accessNotes',
] as const
export type JobsPlaceWriteKey = (typeof JOBS_PLACE_WRITE_KEYS)[number]

export const CLINICS_PLACE_WRITE_KEYS = [
  'level',
  'name',
  'code',
  'preset',
  'siteKind',
  'children',
  'hubPlaceId',
] as const
export type ClinicsPlaceWriteKey = (typeof CLINICS_PLACE_WRITE_KEYS)[number]

export type OpsPlaceLevelConstruction =
  | 'SITE'
  | 'BUILDING'
  | 'FLOOR'
  | 'ROOM'
  | 'CHAINAGE'

export type OpsPlaceLevelHospital =
  | 'FACILITY'
  | 'DEPARTMENT'
  | 'UNIT'
  | 'WARD'
  | 'ROOM'
  | 'BED'

export type OpsPlaceLevelSites =
  | 'CLIENT'
  | 'SITE'
  | 'POST'
  | 'AREA'
  | 'CHECKPOINT'

export type OpsPlaceLevelShops =
  | 'GROUP'
  | 'OUTLET'
  | 'ZONE'

export type OpsPlaceLevelStays =
  | 'GROUP'
  | 'PROPERTY'
  | 'BLOCK'
  | 'ROOM'
  | 'AREA'
  | 'BED'

export type OpsPlaceLevelJobs =
  | 'SERVICE_AREA'
  | 'SITE'

export type OpsPlaceLevelClinics =
  | 'ORG'
  | 'SITE'
  | 'FLOOR'
  | 'ZONE'
  | 'ROOM'
  | 'CHAIR'
  | 'BAY'

export type OpsPlaceLevelPlant =
  | 'PLANT'
  | 'LINE'
  | 'FLOOR'
  | 'MACHINE'

export type OpsPlaceLevel =
  | OpsPlaceLevelConstruction
  | OpsPlaceLevelHospital
  | OpsPlaceLevelSites
  | OpsPlaceLevelShops
  | OpsPlaceLevelStays
  | OpsPlaceLevelJobs
  | OpsPlaceLevelClinics
  | OpsPlaceLevelPlant

export const CONSTRUCTION_PLACE_LEVELS: readonly OpsPlaceLevelConstruction[] = [
  'SITE',
  'BUILDING',
  'FLOOR',
  'ROOM',
  'CHAINAGE',
] as const

export const HOSPITAL_PLACE_LEVELS: readonly OpsPlaceLevelHospital[] = [
  'FACILITY',
  'DEPARTMENT',
  'UNIT',
  'WARD',
  'ROOM',
  'BED',
] as const

export const SITES_PLACE_LEVELS: readonly OpsPlaceLevelSites[] = [
  'CLIENT',
  'SITE',
  'POST',
  'AREA',
  'CHECKPOINT',
] as const

export const SHOPS_PLACE_LEVELS: readonly OpsPlaceLevelShops[] = [
  'GROUP',
  'OUTLET',
  'ZONE',
] as const

export const STAYS_PLACE_LEVELS: readonly OpsPlaceLevelStays[] = [
  'GROUP',
  'PROPERTY',
  'BLOCK',
  'ROOM',
  'AREA',
  'BED',
] as const

export const JOBS_PLACE_LEVELS: readonly OpsPlaceLevelJobs[] = [
  'SERVICE_AREA',
  'SITE',
] as const

export const DUE_CHECK_UNIT_FILTER_LEVELS = [
  'DEPARTMENT',
  'UNIT',
  'WARD',
  'SITE',
  'BUILDING',
] as const

export type DueCheckUnitFilterLevel = (typeof DUE_CHECK_UNIT_FILTER_LEVELS)[number]

export const CLINICS_PLACE_LEVELS: readonly OpsPlaceLevelClinics[] = [
  'ORG',
  'SITE',
  'FLOOR',
  'ZONE',
  'ROOM',
  'CHAIR',
  'BAY',
] as const

export const PLANT_PLACE_LEVELS: readonly OpsPlaceLevelPlant[] = [
  'PLANT',
  'LINE',
  'FLOOR',
  'MACHINE',
] as const

export interface OpsSectorDefinition {
  readonly key: OpsSector
  readonly moduleKey: 'construction' | 'hospital' | 'sites' | 'delivery' | 'shops' | 'stays' | 'jobs' | 'clinics' | 'plant'
  readonly routeSegment: string
  readonly view: 'CONSTRUCTION' | 'HOSPITAL' | 'SITES' | 'DELIVERY' | 'SHOPS' | 'STAYS' | 'JOBS' | 'CLINICS' | 'PLANT'
  readonly levels: readonly string[]
  readonly rootLevels: readonly string[]
  readonly allowedChildren: Readonly<Record<string, readonly string[]>>
  readonly defaultChild: Readonly<Record<string, string>>
  readonly allowsPatientLinked: boolean
  readonly requestsEnabled: boolean
  readonly assetsEnabled: boolean
  readonly bookingsEnabled: boolean
  readonly bookingsMultiResource?: boolean
  readonly anonymousBookingReminders?: boolean
  readonly sampleCustodyEnabled?: boolean
  readonly sampleStageMinutes?: Readonly<Record<string, number>>
  readonly queueHoldDefaults?: Readonly<{ DILATION: number; OBSERVATION: number }>
  readonly customersEnabled?: boolean
  readonly reliefOnAbsence?: boolean
  readonly staffSkillsEnabled?: boolean
  readonly onCallEnabled?: boolean
  readonly patientDirectoryAvailable?: boolean
  readonly presetLevel?: string
  readonly presets?: readonly OpsPreset[]
  readonly presetDefaultChild?: Readonly<Record<string, Readonly<Record<string, string>>>>
  readonly qrLeafLevel?: string
  readonly qrLevels?: readonly string[]
  readonly assetPlaceLevel?: string
  readonly oneAssetPerPlace?: boolean
  readonly bmwRequirePreTreatmentPhotoDefault?: boolean
}

export const OPS_SECTORS: Record<OpsSector, OpsSectorDefinition> = {
  construction: {
    key: 'construction',
    moduleKey: 'construction',
    routeSegment: 'construction',
    view: 'CONSTRUCTION',
    levels: CONSTRUCTION_PLACE_LEVELS,
    rootLevels: ['SITE', 'CHAINAGE'],
    allowedChildren: {
      SITE: ['BUILDING', 'FLOOR', 'CHAINAGE', 'ROOM'],
      BUILDING: ['FLOOR', 'ROOM'],
      FLOOR: ['ROOM'],
      CHAINAGE: ['ROOM'],
      ROOM: [],
    },
    defaultChild: {
      SITE: 'BUILDING',
      BUILDING: 'FLOOR',
      FLOOR: 'ROOM',
      CHAINAGE: 'ROOM',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
  },
  hospital: {
    key: 'hospital',
    moduleKey: 'hospital',
    routeSegment: 'hospital',
    view: 'HOSPITAL',
    levels: HOSPITAL_PLACE_LEVELS,
    rootLevels: ['FACILITY'],
    allowedChildren: {
      FACILITY: ['DEPARTMENT', 'UNIT', 'WARD', 'ROOM', 'BED'],
      DEPARTMENT: ['UNIT', 'WARD', 'ROOM', 'BED'],
      UNIT: ['WARD', 'ROOM', 'BED'],
      WARD: ['ROOM', 'BED'],
      ROOM: ['BED'],
      BED: [],
    },
    defaultChild: {
      FACILITY: 'DEPARTMENT',
      DEPARTMENT: 'UNIT',
      UNIT: 'WARD',
      WARD: 'ROOM',
      ROOM: 'BED',
    },
    allowsPatientLinked: true,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: true,
    staffSkillsEnabled: true,
    onCallEnabled: true,
    patientDirectoryAvailable: true,
    bmwRequirePreTreatmentPhotoDefault: false,
  },
  sites: {
    key: 'sites',
    moduleKey: 'sites',
    routeSegment: 'sites',
    view: 'SITES',
    levels: SITES_PLACE_LEVELS,
    rootLevels: ['CLIENT'],
    allowedChildren: {
      CLIENT: ['SITE'],
      SITE: ['POST', 'AREA', 'CHECKPOINT'],
      POST: ['CHECKPOINT'],
      AREA: ['CHECKPOINT'],
      CHECKPOINT: [],
    },
    defaultChild: {
      CLIENT: 'SITE',
      SITE: 'POST',
      POST: 'CHECKPOINT',
      AREA: 'CHECKPOINT',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: false,
    bookingsEnabled: true,
    reliefOnAbsence: true,
    presetLevel: 'SITE',
    presets: SITES_PRESETS,
    presetDefaultChild: {
      GUARDING: { SITE: 'POST' },
      FACILITY: { SITE: 'AREA' },
    },
    qrLeafLevel: 'CHECKPOINT',
    qrLevels: ['CHECKPOINT'],
  },
  delivery: {
    key: 'delivery',
    moduleKey: 'delivery',
    routeSegment: 'delivery',
    view: 'DELIVERY',
    levels: [],
    rootLevels: [],
    allowedChildren: {},
    defaultChild: {},
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: false,
    bookingsEnabled: false,
  },
  shops: {
    key: 'shops',
    moduleKey: 'shops',
    routeSegment: 'shops',
    view: 'SHOPS',
    levels: SHOPS_PLACE_LEVELS,
    rootLevels: ['GROUP'],
    allowedChildren: {
      GROUP: ['OUTLET'],
      OUTLET: ['ZONE'],
      ZONE: [],
    },
    defaultChild: {
      GROUP: 'OUTLET',
      OUTLET: 'ZONE',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
    qrLeafLevel: 'OUTLET',
    qrLevels: ['OUTLET'],
    presetLevel: 'OUTLET',
    presets: SHOPS_PRESETS,
  },
  stays: {
    key: 'stays',
    moduleKey: 'stays',
    routeSegment: 'stays',
    view: 'STAYS',
    levels: STAYS_PLACE_LEVELS,
    rootLevels: ['GROUP'],
    allowedChildren: {
      GROUP: ['PROPERTY'],
      PROPERTY: ['BLOCK', 'ROOM', 'AREA'],
      BLOCK: ['ROOM', 'AREA'],
      ROOM: ['BED'],
      AREA: [],
      BED: [],
    },
    defaultChild: {
      GROUP: 'PROPERTY',
      PROPERTY: 'BLOCK',
      BLOCK: 'ROOM',
      ROOM: 'BED',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: true,
    qrLevels: ['ROOM', 'AREA', 'BED'],
    presetLevel: 'PROPERTY',
    presets: STAYS_PRESETS,
  },
  jobs: {
    key: 'jobs',
    moduleKey: 'jobs',
    routeSegment: 'jobs',
    view: 'JOBS',
    levels: JOBS_PLACE_LEVELS,
    rootLevels: ['SERVICE_AREA', 'SITE'],
    allowedChildren: {
      SERVICE_AREA: ['SITE'],
      SITE: [],
    },
    defaultChild: {
      SERVICE_AREA: 'SITE',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
    customersEnabled: true,
  },
  clinics: {
    key: 'clinics',
    moduleKey: 'clinics',
    routeSegment: 'clinics',
    view: 'CLINICS',
    levels: CLINICS_PLACE_LEVELS,
    rootLevels: ['ORG'],
    allowedChildren: {
      ORG: ['SITE'],
      SITE: ['FLOOR', 'ZONE', 'ROOM', 'CHAIR', 'BAY'],
      FLOOR: ['ZONE', 'ROOM', 'CHAIR', 'BAY'],
      ZONE: ['ROOM', 'CHAIR', 'BAY'],
      ROOM: ['CHAIR', 'BAY'],
      CHAIR: [],
      BAY: [],
    },
    defaultChild: {
      ORG: 'SITE',
      SITE: 'FLOOR',
      FLOOR: 'ROOM',
      ZONE: 'ROOM',
      ROOM: 'CHAIR',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: true,
    qrLevels: ['ROOM', 'CHAIR', 'BAY'],
    presetLevel: 'SITE',
    presets: CLINICS_PRESETS,
    presetDefaultChild: {
      COLLECTION_CENTRE: { SITE: 'ROOM' },
      HOME_COLLECTION: { SITE: 'ROOM' },
      DENTAL: { ROOM: 'CHAIR' },
      DIALYSIS: { ROOM: 'CHAIR' },
      DAY_CARE_NURSING_HOME: { ROOM: 'BAY' },
    },
    bmwRequirePreTreatmentPhotoDefault: true,
    bookingsMultiResource: true,
    anonymousBookingReminders: true,
    sampleCustodyEnabled: true,
    sampleStageMinutes: {
      COLLECTED: 60,
      AT_CENTRE: 120,
      WITH_RUNNER: 180,
      AT_LAB: 240,
    },
    queueHoldDefaults: {
      DILATION: 30,
      OBSERVATION: 15,
    },
  },
  plant: {
    key: 'plant',
    moduleKey: 'plant',
    routeSegment: 'plant',
    view: 'PLANT',
    levels: PLANT_PLACE_LEVELS,
    rootLevels: ['PLANT'],
    allowedChildren: {
      PLANT: ['LINE', 'FLOOR', 'MACHINE'],
      FLOOR: ['LINE', 'MACHINE'],
      LINE: ['MACHINE'],
      MACHINE: [],
    },
    defaultChild: {
      PLANT: 'LINE',
      FLOOR: 'LINE',
      LINE: 'MACHINE',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
    qrLevels: ['LINE', 'FLOOR', 'MACHINE'],
    presetLevel: 'PLANT',
    presets: PLANT_PRESETS,
    assetPlaceLevel: 'MACHINE',
    oneAssetPerPlace: true,
  },
}

export function isOpsSector(x: unknown): x is OpsSector {
  return typeof x === 'string' && (OPS_SECTOR_KEYS as readonly string[]).includes(x)
}

export function isRequestSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].requestsEnabled)
}

export function isAssetSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].assetsEnabled)
}

export function isBookingSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].bookingsEnabled)
}

export function isMultiResourceBookingSector(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].bookingsMultiResource)
}

export function isSampleCustodySector(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].sampleCustodyEnabled)
}

export function isAnonymousBookingReminderSector(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].anonymousBookingReminders)
}

export function isCustomerSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].bookingsEnabled || OPS_SECTORS[x].customersEnabled)
}

export function sectorSupportsReliefOnAbsence(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].reliefOnAbsence)
}

export function sectorBmwRequirePreTreatmentPhotoDefault(x: unknown): boolean {
  if (typeof x !== 'string' || !isOpsSector(x)) return false
  return Boolean(OPS_SECTORS[x].bmwRequirePreTreatmentPhotoDefault)
}

export function findOpsSectorByRouteSegment(segment: string): OpsSector | undefined {
  return OPS_SECTOR_KEYS.find((s) => OPS_SECTORS[s].routeSegment === segment)
}

export function findOpsSectorByView(view: string): OpsSector | undefined {
  return OPS_SECTOR_KEYS.find((s) => OPS_SECTORS[s].view === view)
}

export function isHospitalSector(x: unknown): boolean {
  return x === OPS_SECTORS.hospital.key
}

export function isClinicsSector(x: unknown): boolean {
  return x === OPS_SECTORS.clinics.key
}

export function isPlantSector(x: unknown): boolean {
  return x === OPS_SECTORS.plant.key
}

export function sectorRootLevels(sector: OpsSector): readonly string[] {
  return OPS_SECTORS[sector]?.rootLevels ?? []
}

export const DEFAULT_CHILD_LEVEL: Record<OpsSector, Record<string, OpsPlaceLevel>> = {
  construction: OPS_SECTORS.construction.defaultChild as Record<string, OpsPlaceLevel>,
  hospital: OPS_SECTORS.hospital.defaultChild as Record<string, OpsPlaceLevel>,
  sites: OPS_SECTORS.sites.defaultChild as Record<string, OpsPlaceLevel>,
  delivery: {},
  shops: OPS_SECTORS.shops.defaultChild as Record<string, OpsPlaceLevel>,
  stays: OPS_SECTORS.stays.defaultChild as Record<string, OpsPlaceLevel>,
  jobs: OPS_SECTORS.jobs.defaultChild as Record<string, OpsPlaceLevel>,
  clinics: OPS_SECTORS.clinics.defaultChild as Record<string, OpsPlaceLevel>,
  plant: OPS_SECTORS.plant.defaultChild as Record<string, OpsPlaceLevel>,
}

export const ROOT_LEVEL: Partial<Record<OpsSector, OpsPlaceLevel>> = {
  construction: 'SITE',
  hospital: 'FACILITY',
  sites: 'CLIENT',
  shops: 'GROUP',
  stays: 'GROUP',
  clinics: 'ORG',
  plant: 'PLANT',
}

export const ALLOWED_CHILD_LEVELS: Record<OpsSector, Record<string, readonly OpsPlaceLevel[]>> = {
  construction: OPS_SECTORS.construction.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  hospital: OPS_SECTORS.hospital.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  sites: OPS_SECTORS.sites.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  delivery: {},
  shops: OPS_SECTORS.shops.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  stays: OPS_SECTORS.stays.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  jobs: OPS_SECTORS.jobs.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  clinics: OPS_SECTORS.clinics.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  plant: OPS_SECTORS.plant.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
}

export function isValidChildLevel(
  sector: OpsSector,
  parentLevel: OpsPlaceLevel | null,
  childLevel: OpsPlaceLevel,
): boolean {
  if (!parentLevel) {
    const rootLevels = sectorRootLevels(sector)
    return rootLevels.includes(childLevel)
  }

  const def = OPS_SECTORS[sector]
  if (!def) return false
  const allowed = def.allowedChildren[parentLevel] ?? []
  return allowed.includes(childLevel)
}

/** Checks whether a target level sits strictly below a root level in a given sector hierarchy. */
export function isLevelBelowRoot(
  sector: OpsSector,
  rootLevel: OpsPlaceLevel,
  targetLevel: OpsPlaceLevel,
): boolean {
  if (rootLevel === targetLevel) return false
  const allowed = ALLOWED_CHILD_LEVELS[sector]
  if (!allowed) return false
  const visited = new Set<string>()
  const queue: string[] = [rootLevel]
  while (queue.length > 0) {
    const current = queue.shift()!
    const children = allowed[current] || []
    for (const child of children) {
      if (child === targetLevel) return true
      if (!visited.has(child)) {
        visited.add(child)
        queue.push(child)
      }
    }
  }
  return false
}

export function sectorAllowsPresetOnLevel(sector: OpsSector, level: string): boolean {
  const def = OPS_SECTORS[sector]
  if (!def?.presetLevel || !def?.presets || !def.presets.length) return false
  return def.presetLevel === level
}

export function defaultChildLevel(
  sector: OpsSector,
  parentLevel: string | null,
  preset?: string | null,
): string | undefined {
  const def = OPS_SECTORS[sector]
  if (!def) return undefined

  if (!parentLevel) {
    return def.rootLevels[0]
  }

  if (preset && def.presetDefaultChild && def.presetDefaultChild[preset as SitesPreset]) {
    const override = def.presetDefaultChild[preset as SitesPreset][parentLevel]
    if (override) return override
  }

  return def.defaultChild[parentLevel]
}

export const MAX_PLACES_PER_BULK_CALL = 2000

export interface ParsedOutlineItem {
  depth: number
  level: OpsPlaceLevel
  name: string
  code: string
  parentIndex: number | null
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  ownerRoleKey?: string | null
  dutyChain?: OpsPlaceDutyChain | null
  departmentKey?: string | null
}

export function flattenBulkPlaceItems(
  items: BulkPlaceInputItem[],
  sector: OpsSector,
  parentLevel?: OpsPlaceLevel | null,
): ParsedOutlineItem[] {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Items list is empty.')
  }

  const result: ParsedOutlineItem[] = []
  const allowedLevelsSet = new Set<string>(OPS_SECTORS[sector]?.levels ?? [])

  function walk(
    nodes: BulkPlaceInputItem[],
    depth: number,
    parentIdx: number | null,
    currentParentLevel: OpsPlaceLevel | null,
  ) {
    for (const node of nodes) {
      if (result.length >= MAX_PLACES_PER_BULK_CALL) {
        throw new Error(`Places count exceeds maximum of ${MAX_PLACES_PER_BULK_CALL} per call.`)
      }

      const name = typeof node.name === 'string' ? node.name.trim() : ''
      if (!name) {
        throw new Error('Place name cannot be empty.')
      }
      if (name.length > 120) {
        throw new Error('Place name exceeds 120 characters.')
      }

      const level = node.level
      if (!level || !allowedLevelsSet.has(level)) {
        throw new Error(`Level "${level}" is not valid for sector ${sector}.`)
      }

      if (!isValidChildLevel(sector, currentParentLevel, level)) {
        const parentLabel = currentParentLevel ?? 'root'
        throw new Error(
          `Invalid level order. Level "${level}" cannot be a child of "${parentLabel}" in sector ${sector}.`,
        )
      }

      let code = typeof node.code === 'string' ? node.code.trim() : ''
      if (!code) {
        code = `${level.slice(0, 3)}-${result.length + 1}`
      }

      if (node.preset !== undefined && node.preset !== null) {
        if (!sectorAllowsPresetOnLevel(sector, level)) {
          throw new Error(`Preset is only allowed on a place level that accepts presets in sector ${sector}.`)
        }
        const def = OPS_SECTORS[sector]
        if (!def?.presets || !(def.presets as readonly string[]).includes(node.preset)) {
          throw new Error(`Invalid preset "${node.preset}" for sector ${sector}.`)
        }
      }

      if (node.siteKind !== undefined && node.siteKind !== null) {
        if (!isClinicsSector(sector) || level !== 'SITE') {
          throw new Error('Site kind is only allowed on SITE places in the clinics sector.')
        }
        if (!(CLINICS_SITE_KINDS as readonly string[]).includes(node.siteKind)) {
          throw new Error(`Invalid site kind "${node.siteKind}".`)
        }
      }

      if (node.zoneKind !== undefined && node.zoneKind !== null) {
        if (sector !== 'shops' || level !== 'ZONE') {
          throw new Error('Zone kind is only allowed on ZONE places in the shops sector.')
        }
        if (!(SHOPS_ZONE_KINDS as readonly string[]).includes(node.zoneKind)) {
          throw new Error(`Invalid zone kind "${node.zoneKind}".`)
        }
      }

      if (node.blockKind !== undefined && node.blockKind !== null) {
        if (sector !== 'stays' || level !== 'BLOCK') {
          throw new Error('Block kind is only allowed on BLOCK places in the stays sector.')
        }
        if (!(STAYS_BLOCK_KINDS as readonly string[]).includes(node.blockKind)) {
          throw new Error(`Invalid block kind "${node.blockKind}".`)
        }
      }

      if (node.areaKind !== undefined && node.areaKind !== null) {
        if (sector !== 'stays' || level !== 'AREA') {
          throw new Error('Area kind is only allowed on AREA places in the stays sector.')
        }
        if (!(STAYS_AREA_KINDS as readonly string[]).includes(node.areaKind)) {
          throw new Error(`Invalid area kind "${node.areaKind}".`)
        }
      }

      if (sector === 'stays' && level === 'BED') {
        let currIdx = parentIdx
        let ancestorPropertyPreset: string | null = null
        while (currIdx !== null) {
          const anc = result[currIdx]
          if (anc.level === 'PROPERTY') {
            ancestorPropertyPreset = anc.preset ?? null
            break
          }
          currIdx = anc.parentIndex
        }
        if (ancestorPropertyPreset !== null && ancestorPropertyPreset !== 'HOSTEL') {
          throw new Error('BED places are only allowed under a property with HOSTEL preset.')
        }
      }

      if (sector === 'jobs') {
        if (level === 'SITE') {
          if (!node.customerId) {
            throw new Error('customerId is required on Jobs SITE.')
          }
          if (!node.address || !node.address.line1 || !node.address.city) {
            throw new Error('address (line1 and city) is required on Jobs SITE.')
          }
        } else if (level === 'SERVICE_AREA') {
          if (node.customerId || node.address || node.geo || node.accessNotes) {
            throw new Error('Jobs site fields are not allowed on Jobs SERVICE_AREA.')
          }
        }
      } else {
        if (node.customerId || node.address || node.geo || node.accessNotes) {
          throw new Error('Jobs fields are only allowed in the jobs sector.')
        }
      }

      const itemIdx = result.length
      result.push({
        depth,
        level,
        name,
        code,
        parentIndex: parentIdx,
        preset: node.preset ?? null,
        zoneKind: node.zoneKind ?? null,
        blockKind: node.blockKind ?? null,
        areaKind: node.areaKind ?? null,
        roomTypes: node.roomTypes ?? null,
        roomTypeId: node.roomTypeId ?? null,
        ...(sector === 'jobs' && level === 'SITE'
          ? {
              customerId: node.customerId ?? null,
              address: node.address ?? null,
              geo: node.geo ?? null,
              accessNotes: node.accessNotes ?? null,
            }
          : {}),
        ...(isClinicsSector(sector) && level === 'SITE'
          ? {
              siteKind: node.siteKind ?? null,
            }
          : {}),
      })

      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children, depth + 1, itemIdx, level)
      }
    }
  }

  walk(items, 0, null, parentLevel ?? null)
  return result
}

export interface StaysRoomType {
  id: string
  name: string
  requiredPhotos: string[]
  standardCleanMinutes: number
}

export const MAX_PLACE_ACCESS_UIDS = 200

export type OpsAccessJobState = 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED' | 'SUPERSEDED'

export interface OpsPlaceAccessJobProgress {
  jobId: string
  state: OpsAccessJobState
  updated: number
  startedAt: number
}

export interface OpsPlaceDutyChain {
  inChargeRoleKey: string | null
  backupRoleKey: string | null
  onCallRoleKey: string | null
}

export interface JobSiteAddress {
  line1: string
  line2?: string | null
  locality?: string | null
  city: string
  region?: string | null
  postcode?: string | null
}

export interface JobSiteGeo {
  lat: number
  lng: number
}

export interface CreateJobSiteInput {
  workspaceId: string
  customerId: string
  parentAreaId?: string | null
  name: string
  code?: string
  address: JobSiteAddress
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  opId?: string
}

export interface UpdateJobSiteInput {
  workspaceId: string
  placeId: string
  name?: string
  address?: JobSiteAddress
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  opId?: string
}

export interface CreateJobSiteResult {
  placeId: string
  path: string[]
  replayed?: boolean
}

export interface UpdateJobSiteResult {
  success: boolean
  placeId: string
  replayed?: boolean
}

export interface ListCustomerContactsInput {
  workspaceId: string
  customerIds: string[]
}

export interface CustomerContactEntry {
  customerId: string
  phone: string
  email?: string
  notes?: string
}

export interface ListCustomerContactsResult {
  contacts: CustomerContactEntry[]
}

export interface OpsPlace {
  id?: string
  workspaceId: string
  sector: OpsSector
  parentId: string | null
  path: string[]
  level: OpsPlaceLevel
  name: string
  code: string
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  attendanceGraceMinutes?: number | null
  ownerRoleKey?: string | null
  hubPlaceId?: string | null
  dutyChain?: OpsPlaceDutyChain | null
  departmentKey?: string | null
  accessList?: string[] | null
  readAccess?: string[] | null
  accessRevision?: number
  accessJob?: OpsPlaceAccessJobProgress | null
  archived: boolean
  createdAt: unknown
  createdBy: string
  updatedAt: unknown
  updatedBy: string
}

/**
 * Computes effective place access list from an ordered chain of access lists
 * (ancestors root-first, ending with the place itself).
 *
 * Rule:
 * - If neither the place nor any ancestor has a list (all are null/undefined), returns null.
 * - Otherwise returns the intersection of every non-null accessList along path and the place itself.
 * - An empty list [] is an allowed restriction (nobody restricted sees it).
 */
export function computeEffectivePlaceAccess(
  chain: ReadonlyArray<ReadonlyArray<string> | string[] | null | undefined>,
): string[] | null {
  let hasAnyList = false
  let currentSet: Set<string> | null = null

  for (const list of chain) {
    if (list !== null && list !== undefined) {
      hasAnyList = true
      if (currentSet === null) {
        currentSet = new Set(list)
      } else {
        const nextSet = new Set<string>()
        for (const uid of list) {
          if (currentSet.has(uid)) {
            nextSet.add(uid)
          }
        }
        currentSet = nextSet
      }
    }
  }

  if (!hasAnyList || currentSet === null) {
    return null
  }
  return Array.from(currentSet)
}

/**
 * Validates that a child place's proposed access list does not name anyone
 * outside its parent's effective readAccess list (the "narrower" rule).
 */
export function validateChildPlaceAccessList(
  childList: ReadonlyArray<string> | string[] | null | undefined,
  parentEffectiveList: ReadonlyArray<string> | string[] | null | undefined,
): { valid: boolean; extraUids: string[] } {
  if (!childList || childList.length === 0) {
    return { valid: true, extraUids: [] }
  }
  if (!parentEffectiveList) {
    // Parent has no restriction, so child can set any list
    return { valid: true, extraUids: [] }
  }
  const allowed = new Set(parentEffectiveList)
  const extraUids = childList.filter((uid) => !allowed.has(uid))
  return {
    valid: extraUids.length === 0,
    extraUids,
  }
}

export interface SetPlaceAccessInput {
  workspaceId: string
  placeId: string
  accessList: string[] | null
  opId?: string
}

export interface SetPlaceAccessResult {
  success: boolean
  placeId: string
  accessList: string[] | null
  readAccess: string[] | null
  accessRevision: number
  jobId: string
  replayed?: boolean
}

export interface OpsAccessJobCursor {
  phase: 'PLACES' | 'COPIES'
  collIndex: number
  placeIndex: number
  lastDocId: string | null
  step?: 'PLACES' | 'COLLECTIONS'
  collectionIndex?: number
}

export interface OpsAccessJobDoc {
  id: string
  workspaceId: string
  rootPlaceId: string
  accessRevision: number
  state: OpsAccessJobState
  page?: number
  leaseOwner?: string | null
  leaseUntil?: number | null
  cursor: OpsAccessJobCursor | null
  subtreePlaceIds?: string[]
  attempts?: number
  lastError?: string | null
  counts: Record<string, { scanned: number; updated: number }>
  startedAt: number | null
  finishedAt: number | null
  error?: string | null
  createdAt?: unknown
  updatedAt?: unknown
}

export interface OpsPlaceTokenDoc {
  placeId: string
  workspaceId: string
  createdAt: unknown
  revokedAt: unknown | null
}

export interface ResolvedPlaceToken {
  placeId: string
  name: string
  level: OpsPlaceLevel
  path: string[]
  workspaceId: string
  sector: OpsSector
}

export interface CreatePlaceInput {
  workspaceId: string
  sector: OpsSector
  parentId?: string | null
  level: OpsPlaceLevel
  name: string
  code: string
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  ownerRoleKey?: string | null
  hubPlaceId?: string | null
}

export interface RenamePlaceInput {
  workspaceId: string
  placeId: string
  name?: string
  code?: string
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  attendanceGraceMinutes?: number | null
  hubPlaceId?: string | null
  opId?: string
}

export interface RenamePlaceResult {
  success: boolean
  placeId: string
  name: string
  code: string
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  attendanceGraceMinutes?: number | null
  hubPlaceId?: string | null
  replayed?: boolean
}

export interface SetPlacePresetInput {
  workspaceId: string
  placeId: string
  preset: OpsPreset | null
  opId?: string
}

export interface SetPlacePresetResult {
  success: boolean
  placeId: string
  preset: OpsPreset | null
  replayed?: boolean
}

export interface SetPropertyRoomTypesInput {
  workspaceId: string
  propertyId: string
  roomTypes: StaysRoomType[]
  minibarCheckOnCheckout?: boolean | null
  opId: string
}

export interface SetPropertyRoomTypesResult {
  success: boolean
  propertyId: string
  roomTypes: StaysRoomType[]
  minibarCheckOnCheckout?: boolean | null
  replayed: boolean
}

export interface SetPropertyMinibarCheckInput {
  workspaceId: string
  propertyId: string
  minibarCheckOnCheckout: boolean
  opId: string
}

export interface SetPropertyMinibarCheckResult {
  success: boolean
  propertyId: string
  minibarCheckOnCheckout: boolean
  replayed: boolean
}

export interface SetRoomTypeInput {
  workspaceId: string
  placeIds: string[]
  roomTypeId: string
  opId: string
}

export interface SetRoomTypeResult {
  success: boolean
  count: number
  roomTypeId: string
  replayed: boolean
}

export interface MovePlaceInput {
  workspaceId: string
  placeId: string
  newParentId: string | null
}

export interface ArchivePlaceInput {
  workspaceId: string
  placeId: string
  archived?: boolean
}

export interface BulkPlaceInputItem {
  level: OpsPlaceLevel
  name: string
  code?: string
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  ownerRoleKey?: string | null
  dutyChain?: OpsPlaceDutyChain | null
  departmentKey?: string | null
  children?: BulkPlaceInputItem[]
}

export interface SetPlaceDutyChainInput {
  workspaceId: string
  placeId: string
  dutyChain?: {
    inChargeRoleKey?: string | null
    backupRoleKey?: string | null
    onCallRoleKey?: string | null
  } | null
  departmentKey?: string | null
  ownerRoleKey?: string | null
  opId?: string
}

export interface SetPlaceDutyChainResult {
  success: boolean
  placeId: string
  dutyChain: OpsPlaceDutyChain | null
  departmentKey: string | null
  ownerRoleKey: string | null
}

export interface BulkCreatePlacesInput {
  workspaceId: string
  sector: OpsSector
  outline?: string
  items?: BulkPlaceInputItem[]
  parentId?: string | null
  preset?: OpsPreset | null
  opId: string
}

export interface BulkCreatePlacesResult {
  placeIds: string[]
  count: number
  opId: string
  replayed: boolean
}

export interface CreatePlaceTokenInput {
  workspaceId: string
  placeId: string
}

export interface CreatePlaceTokenResult {
  token: string
  placeId: string
}

export interface RevokePlaceTokenInput {
  workspaceId: string
  placeId: string
  token?: string
}

export interface ResolvePlaceTokenInput {
  token: string
}

/* =======================================================================
   A3.a Workforce Types
   ======================================================================= */

export type OpsPartyKind = 'CONTRACTOR' | 'VENDOR' | 'CLIENT' | 'CONSULTANT'

export const OPS_PARTY_KINDS: readonly OpsPartyKind[] = [
  'CONTRACTOR',
  'VENDOR',
  'CLIENT',
  'CONSULTANT',
] as const

export type OpsWorkerStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED'

export const OPS_WORKER_STATUSES: readonly OpsWorkerStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
] as const

export interface OpsPartyContact {
  phone?: string
  email?: string
}

export interface OpsPartyScope {
  placeIds: string[]
}

export interface OpsParty {
  id?: string
  workspaceId: string
  sector: OpsSector
  kind: OpsPartyKind
  name: string
  contact?: OpsPartyContact
  scope?: OpsPartyScope
  externalRef?: string
  memberUids: string[]
  archived: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsPartyLink {
  workspaceId: string
  uid: string
  partyIds: string[]
  updatedAt?: unknown
}

export type HospitalStaffGroup = 'DOCTOR' | 'NURSE' | 'SUPPORT' | 'OTHER'
export type OpsStaffGroup = HospitalStaffGroup

export interface OpsWorker {
  id?: string
  workspaceId: string
  sector: OpsSector
  displayName: string
  workerCode: string
  partyId: string
  trade: string
  skills: string[]
  placeIds: string[]
  phone?: string
  memberUid?: string
  staffGroup?: OpsStaffGroup
  status: OpsWorkerStatus
  partyMemberUids: string[]
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface CreatePartyInput {
  workspaceId: string
  sector: OpsSector
  kind: OpsPartyKind
  name: string
  contact?: OpsPartyContact
  scope?: OpsPartyScope
  externalRef?: string
  memberUids?: string[]
  opId?: string
}

export interface UpdatePartyInput {
  workspaceId: string
  partyId: string
  kind?: OpsPartyKind
  name?: string
  contact?: OpsPartyContact
  scope?: OpsPartyScope
  externalRef?: string
  archived?: boolean
}

export interface SetPartyMembersInput {
  workspaceId: string
  partyId: string
  memberUids: string[]
}

export interface CreateWorkerInput {
  workspaceId: string
  sector: OpsSector
  displayName: string
  partyId: string
  trade: string
  skills?: string[]
  placeIds?: string[]
  phone?: string
  memberUid?: string
  staffGroup?: OpsStaffGroup
  workerCode?: string
  opId: string
}

export interface UpdateWorkerInput {
  workspaceId: string
  workerId: string
  displayName?: string
  partyId?: string
  trade?: string
  skills?: string[]
  placeIds?: string[]
  phone?: string | null
  memberUid?: string | null
  staffGroup?: OpsStaffGroup | null
  workerCode?: string
  status?: OpsWorkerStatus
}

export interface ArchiveWorkerInput {
  workspaceId: string
  workerId: string
}

export interface AddGangInput {
  workspaceId: string
  sector: OpsSector
  partyId: string
  count: number
  trade: string
  placeIds?: string[]
  namePrefix: string
  opId: string
}

/* =======================================================================
   A4.a Registers & Templates Types
   ======================================================================= */

export type OpsSectorRole =
  | 'TEMPLATE_APPROVER'
  | 'RECORD_REVIEWER'
  | 'RECORD_APPROVER'
  | 'ROSTER_MANAGER'
  | 'ATTENDANCE_MARKER'
  | 'INCIDENT_HANDLER'
  | 'REQUEST_VERIFIER'
  | 'COMPLIANCE_MANAGER'
  | 'ASSET_RELEASER'
  | 'EVIDENCE_EXPORTER'
  | 'FRONT_DESK'
  | 'ROOM_INSPECTOR'
  | 'BOOKING_DESK'
  | 'JOB_DISPATCHER'
  | 'MAINTENANCE'
  | 'GROUP_MANAGER'
  | 'QUALITY_RELEASER'

export const OPS_SECTOR_ROLES: readonly OpsSectorRole[] = [
  'TEMPLATE_APPROVER',
  'RECORD_REVIEWER',
  'RECORD_APPROVER',
  'ROSTER_MANAGER',
  'ATTENDANCE_MARKER',
  'INCIDENT_HANDLER',
  'REQUEST_VERIFIER',
  'COMPLIANCE_MANAGER',
  'ASSET_RELEASER',
  'EVIDENCE_EXPORTER',
  'FRONT_DESK',
  'ROOM_INSPECTOR',
  'BOOKING_DESK',
  'JOB_DISPATCHER',
  'MAINTENANCE',
  'GROUP_MANAGER',
  'QUALITY_RELEASER',
] as const

export type OpsTemplateFieldType =
  | 'NUMBER'
  | 'CHOICE'
  | 'YES_NO'
  | 'DATE_TIME'
  | 'PHOTO'
  | 'READING'
  | 'ACKNOWLEDGE'
  | 'CASH_COUNT'

export const OPS_TEMPLATE_FIELD_TYPES: readonly OpsTemplateFieldType[] = [
  'NUMBER',
  'CHOICE',
  'YES_NO',
  'DATE_TIME',
  'PHOTO',
  'READING',
  'ACKNOWLEDGE',
  'CASH_COUNT',
] as const

export type OpsTemplateVersionState = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED'

export type OpsRecordState = 'DRAFT' | 'SUBMITTED' | 'REVIEWED' | 'APPROVED' | 'RETURNED'

export interface OpsTemplateFieldBase {
  key: string
  label: string
  required?: boolean
}

export interface OpsTemplateFieldNumberField extends OpsTemplateFieldBase {
  type: 'NUMBER'
  unit?: string
  min?: number
  max?: number
}

export interface OpsTemplateFieldChoiceField extends OpsTemplateFieldBase {
  type: 'CHOICE'
  options: string[]
}

export interface OpsTemplateFieldYesNoField extends OpsTemplateFieldBase {
  type: 'YES_NO'
  exceptionOn?: 'YES' | 'NO' | null
}

export interface OpsTemplateFieldDateTimeField extends OpsTemplateFieldBase {
  type: 'DATE_TIME'
}

export interface OpsTemplateFieldPhotoField extends OpsTemplateFieldBase {
  type: 'PHOTO'
  maxCount?: number
}

export interface OpsTemplateFieldReadingField extends OpsTemplateFieldBase {
  type: 'READING'
  unit?: string
  min?: number
  max?: number
}

export interface OpsTemplateFieldAcknowledgeField extends OpsTemplateFieldBase {
  type: 'ACKNOWLEDGE'
}

export interface OpsTemplateFieldCashCountField extends OpsTemplateFieldBase {
  type: 'CASH_COUNT'
  denominations: number[] // unique positive integers, <= 20 entries
  toleranceMinor?: number // >= 0, default 0
  carryFloat?: boolean
}

export const DEFAULT_CASH_DENOMINATIONS: Record<string, number[]> = {
  INR: [50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100],
  USD: [10000, 5000, 2000, 1000, 500, 200, 100, 25, 10, 5, 1],
  EUR: [50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  GBP: [5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  AED: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 100, 50, 25],
  SGD: [10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5],
}

export function getDefaultCashDenominations(currency?: string | null): number[] | null {
  if (!currency || typeof currency !== 'string') return null
  const code = currency.trim().toUpperCase()
  return DEFAULT_CASH_DENOMINATIONS[code] ? [...DEFAULT_CASH_DENOMINATIONS[code]] : null
}

export interface OpsCashCountValue {
  counts: Record<string | number, number>
  looseMinor?: number
  expectedMinor?: number
  mismatchReason?: string
  countedMinor?: number
  differenceMinor?: number
}

export interface OpsTemplateCoSignConfig {
  required: boolean
  label: string // 1-60 chars
}

export interface OpsTemplateRaiseRequestOnException {
  kindKey: string
}

export type OpsTemplateField =
  | OpsTemplateFieldNumberField
  | OpsTemplateFieldChoiceField
  | OpsTemplateFieldYesNoField
  | OpsTemplateFieldDateTimeField
  | OpsTemplateFieldPhotoField
  | OpsTemplateFieldReadingField
  | OpsTemplateFieldAcknowledgeField
  | OpsTemplateFieldCashCountField

export interface OpsTemplate {
  id?: string
  workspaceId: string
  placeId?: string
  pushedFromGroupPlaceId?: string
  pushedTemplateVersion?: number
  sector: OpsSector
  name: string
  title?: string
  kind: string
  safetyTagged: boolean
  patientLinked: boolean
  lateAfterMinutes: number
  coSign?: OpsTemplateCoSignConfig | null
  raiseRequestOnException?: OpsTemplateRaiseRequestOnException | null
  source?: string
  edition?: string
  jurisdiction?: string
  starter: boolean
  starterKey?: string
  currentVersion: number | null
  archived: boolean
  retentionClass?: string
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsTemplateVersion {
  id?: string
  version: number
  templateId: string
  workspaceId: string
  sector: OpsSector
  fields: OpsTemplateField[]
  coSign?: OpsTemplateCoSignConfig | null
  raiseRequestOnException?: OpsTemplateRaiseRequestOnException | null
  state: OpsTemplateVersionState
  effectiveFrom?: unknown
  approvedBy?: string | null
  approvedAt?: unknown
  selfApproved?: boolean
  selfApprovalReason?: string | null
  submittedBy?: string | null
  submittedAt?: unknown
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsRecordException {
  fieldKey: string
  reason: string
}

export interface OpsRecordCorrectionDiff {
  fieldKey: string
  before: unknown
  after: unknown
}

export interface OpsRecordCorrection {
  id?: string
  recordId: string
  workspaceId: string
  reason: string
  authorUid: string
  authorName?: string
  createdAt?: unknown
  changedValues: Record<string, unknown>
  diffs: OpsRecordCorrectionDiff[]
}

export type OpsRecordCoSignState = 'AWAITING' | 'SIGNED' | 'DISPUTED' | 'PROXY_SIGNED'

export interface OpsRecordCoSign {
  state: OpsRecordCoSignState
  signerRef: OpsPersonRef
  signedAt?: unknown
  signedBy?: string
  disputedAt?: unknown
  disputedBy?: string
  disputeReason?: string
  proxySignedAt?: unknown
  proxySignedBy?: string
  proxyReason?: string
}

export interface OpsRecord {
  id?: string
  workspaceId: string
  sector: OpsSector
  templateId: string
  templateVersion: number
  placeId: string
  assetId?: string
  shiftId?: string | null
  state: OpsRecordState
  asDraft?: boolean
  occurredAt: unknown
  capturedAt: unknown
  receivedAt: unknown
  lateBy: number
  late: boolean
  opId: string
  values: Record<string, unknown>
  exceptions: OpsRecordException[]
  hasExceptions: boolean
  coSign?: OpsRecordCoSign | null
  authorUid: string
  reviewerUid?: string | null
  reviewedAt?: unknown
  approverUid?: string | null
  approvedAt?: unknown
  selfApproved?: boolean
  selfApprovalReason?: string | null
  returnedReason?: string | null
  returnedBy?: string | null
  returnedAt?: unknown
  lockedAt?: unknown
  correctionCount: number
  lastCorrectedAt?: unknown
  readAccess: string[]
  dueId?: string | null
  offWindow?: boolean
  geo?: { lat: number; lng: number; accuracy?: number } | null
  note?: string | null
  photoPath?: string | null
  createdAt?: unknown
  updatedAt?: unknown
}

export interface OpsSectorRolesDoc {
  workspaceId: string
  uid: string
  roleKeys: OpsSectorRole[]
  updatedAt?: unknown
  updatedBy?: string
}

export interface SetSectorRolesInput {
  workspaceId: string
  uid: string
  roleKeys: OpsSectorRole[]
  exportSectors?: OpsSector[]
}

export interface SaveTemplateDraftInput {
  workspaceId: string
  sector: OpsSector
  name: string
  kind: string
  fields: OpsTemplateField[]
  coSign?: OpsTemplateCoSignConfig | null
  raiseRequestOnException?: OpsTemplateRaiseRequestOnException | null
  safetyTagged?: boolean
  patientLinked?: boolean
  lateAfterMinutes?: number
  source?: string
  edition?: string
  jurisdiction?: string
  starter?: boolean
  starterKey?: string | null
  templateId?: string
}

export interface SubmitTemplateVersionInput {
  workspaceId: string
  templateId: string
  version: number
}

export interface ApproveTemplateVersionInput {
  workspaceId: string
  templateId: string
  version: number
  selfApprovalReason?: string
}

export interface SubmitRecordInput {
  workspaceId: string
  templateId: string
  placeId: string
  assetId?: string
  values: Record<string, unknown>
  occurredAt: string | number
  capturedAt: string | number
  opId: string
  dueId?: string | null
  shiftId?: string | null
  asDraft?: boolean
  recordId?: string
  coSignerRef?: OpsPersonRef | null
}

export interface CoSignRecordInput {
  workspaceId: string
  recordId: string
  agree: boolean
  reason?: string
  opId: string
}

export interface ProxyCoSignRecordInput {
  workspaceId: string
  recordId: string
  reason: string
  opId: string
}

export interface ReviewRecordInput {
  workspaceId: string
  recordId: string
}

export interface ReturnRecordInput {
  workspaceId: string
  recordId: string
  reason: string
}

export interface ApproveRecordInput {
  workspaceId: string
  recordId: string
  selfApprovalReason?: string
}

export interface AddCorrectionInput {
  workspaceId: string
  recordId: string
  reason: string
  changedValues: Record<string, unknown>
}

/* Pure Validators & Constants (Mirrored from functions-common/src/ops/registers.ts) */

export const FIELD_KEY_REGEX = /^[a-z0-9_]{1,40}$/
export const MAX_TEMPLATE_FIELDS = 60
export const MAX_LABEL_LENGTH = 120

export function validateTemplateFields(
  fields: unknown,
  options?: { sector?: OpsSector; patientLinked?: boolean },
): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!Array.isArray(fields)) {
    return { valid: false, errors: ['Template fields must be an array.'] }
  }

  if (fields.length === 0) {
    errors.push('At least one field is required in a template.')
  }

  if (fields.length > MAX_TEMPLATE_FIELDS) {
    errors.push(`Template cannot exceed ${MAX_TEMPLATE_FIELDS} fields (found ${fields.length}).`)
  }

  const seenKeys = new Set<string>()

  for (let i = 0; i < fields.length; i++) {
    const f = fields[i] as OpsTemplateField
    if (!f || typeof f !== 'object') {
      errors.push(`Field #${i + 1} is invalid.`)
      continue
    }

    // Key check
    if (typeof f.key !== 'string' || !FIELD_KEY_REGEX.test(f.key)) {
      errors.push(
        `Field #${i + 1} has invalid key "${f.key}". Keys must be lowercase alphanumeric and underscore only, 1-40 chars.`,
      )
    } else if (seenKeys.has(f.key)) {
      errors.push(`Duplicate field key "${f.key}" at field #${i + 1}.`)
    } else {
      seenKeys.add(f.key)
    }

    // Label check
    if (typeof f.label !== 'string' || !f.label.trim()) {
      errors.push(`Field #${i + 1} ("${f.key}") must have a non-empty label.`)
    } else if (f.label.trim().length > MAX_LABEL_LENGTH) {
      errors.push(
        `Field #${i + 1} ("${f.key}") label exceeds maximum length of ${MAX_LABEL_LENGTH} characters.`,
      )
    }

    // Type check (must be from the fixed list, no free-text types)
    if (!OPS_TEMPLATE_FIELD_TYPES.includes(f.type)) {
      errors.push(
        `Field #${i + 1} ("${f.key}") has unknown or disallowed type "${f.type}". Free-text field types are not allowed.`,
      )
      continue
    }

    // Hospital patientLinked templates reject PHOTO fields (SO-P53)
    if (options?.patientLinked && f.type === 'PHOTO') {
      errors.push(
        `Field #${i + 1} ("${f.key}") has type PHOTO, which is disallowed for patientLinked templates.`,
      )
    }

    if (f.type === 'CHOICE') {
      const choiceField = f as OpsTemplateFieldChoiceField
      if (!Array.isArray(choiceField.options) || choiceField.options.length === 0) {
        errors.push(`Field #${i + 1} ("${f.key}") of type CHOICE must provide non-empty options.`)
      }
    } else if (f.type === 'YES_NO') {
      const yn = f as OpsTemplateFieldYesNoField
      if (
        yn.exceptionOn !== undefined &&
        yn.exceptionOn !== null &&
        yn.exceptionOn !== 'YES' &&
        yn.exceptionOn !== 'NO'
      ) {
        errors.push(
          `Field #${i + 1} ("${f.key}") has invalid exceptionOn value "${yn.exceptionOn}". Must be 'YES', 'NO', or null.`,
        )
      }
    } else if (f.type === 'NUMBER' || f.type === 'READING') {
      const numField = f as OpsTemplateFieldNumberField | OpsTemplateFieldReadingField
      if (numField.min !== undefined && typeof numField.min !== 'number') {
        errors.push(`Field #${i + 1} ("${f.key}") min must be a number.`)
      }
      if (numField.max !== undefined && typeof numField.max !== 'number') {
        errors.push(`Field #${i + 1} ("${f.key}") max must be a number.`)
      }
      if (
        numField.min !== undefined &&
        numField.max !== undefined &&
        numField.min > numField.max
      ) {
        errors.push(`Field #${i + 1} ("${f.key}") min cannot be greater than max.`)
      }
    } else if (f.type === 'CASH_COUNT') {
      const cc = f as OpsTemplateFieldCashCountField
      if (!Array.isArray(cc.denominations) || cc.denominations.length === 0) {
        errors.push(`Field #${i + 1} ("${f.key}") of type CASH_COUNT must provide denominations.`)
      } else if (cc.denominations.length > 20) {
        errors.push(
          `Field #${i + 1} ("${f.key}") cannot have more than 20 denominations (found ${cc.denominations.length}).`,
        )
      } else {
        const seenDenoms = new Set<number>()
        for (const d of cc.denominations) {
          if (typeof d !== 'number' || !Number.isInteger(d) || d <= 0) {
            errors.push(`Field #${i + 1} ("${f.key}") denomination "${d}" must be a positive integer.`)
          } else if (seenDenoms.has(d)) {
            errors.push(`Field #${i + 1} ("${f.key}") duplicate denomination "${d}".`)
          } else {
            seenDenoms.add(d)
          }
        }
      }
      if (cc.toleranceMinor !== undefined) {
        if (typeof cc.toleranceMinor !== 'number' || !Number.isInteger(cc.toleranceMinor) || cc.toleranceMinor < 0) {
          errors.push(`Field #${i + 1} ("${f.key}") toleranceMinor must be an integer >= 0.`)
        }
      }
      if (cc.carryFloat !== undefined && typeof cc.carryFloat !== 'boolean') {
        errors.push(`Field #${i + 1} ("${f.key}") carryFloat must be a boolean.`)
      }
    }
  }

  return { valid: errors.length === 0, errors }
}

export interface SiteAuditRecordInput {
  id?: string
  placeId?: string
  state?: OpsRecordState | string
  values: Record<string, unknown>
}

export interface ComputeSiteAuditScoreOptions {
  fields?: readonly OpsTemplateField[]
  placeId?: string
}

export interface SiteAuditScoreResult {
  score: number | null
  totalRecords: number
  approvedRecordsCount: number
  scoredItemsCount: number
}

/**
 * Pure audit score computation over approved records (R39 G4.a / SO-P102).
 * Scores YES_NO and CHOICE items.
 */
export function computeSiteAuditScore(
  records: readonly SiteAuditRecordInput[],
  options?: ComputeSiteAuditScoreOptions,
): SiteAuditScoreResult {
  const approved = records.filter(
    (r) =>
      r.state === 'APPROVED' &&
      (!options?.placeId || r.placeId === options.placeId),
  )

  if (approved.length === 0) {
    return {
      score: null,
      totalRecords: records.length,
      approvedRecordsCount: 0,
      scoredItemsCount: 0,
    }
  }

  const scores: number[] = []

  for (const record of approved) {
    if (!record.values || typeof record.values !== 'object') continue

    if (options?.fields && options.fields.length > 0) {
      for (const field of options.fields) {
        const val = record.values[field.key]
        if (val === undefined || val === null) continue

        if (field.type === 'YES_NO') {
          if (typeof val === 'string') {
            const upper = val.toUpperCase()
            if (upper === 'YES' || upper === 'NO') {
              const isException = field.exceptionOn
                ? upper === field.exceptionOn
                : upper === 'NO'
              scores.push(isException ? 0 : 1)
            }
          }
        } else if (field.type === 'CHOICE') {
          if (typeof val === 'string') {
            const upper = val.toUpperCase()
            if (upper === 'GOOD' || upper === 'EXCELLENT' || upper === 'PASS') {
              scores.push(1)
            } else if (upper === 'FAIR' || upper === 'SATISFACTORY') {
              scores.push(0.5)
            } else if (upper === 'REDO' || upper === 'POOR' || upper === 'FAIL') {
              scores.push(0)
            } else if (field.options && field.options.length > 1) {
              const idx = field.options.findIndex((o) => o.toUpperCase() === upper)
              if (idx >= 0) {
                scores.push((field.options.length - 1 - idx) / (field.options.length - 1))
              }
            }
          }
        }
      }
    } else {
      for (const val of Object.values(record.values)) {
        if (typeof val === 'string') {
          const upper = val.toUpperCase()
          if (upper === 'YES') scores.push(1)
          else if (upper === 'NO') scores.push(0)
          else if (upper === 'GOOD' || upper === 'EXCELLENT' || upper === 'PASS') scores.push(1)
          else if (upper === 'FAIR' || upper === 'SATISFACTORY') scores.push(0.5)
          else if (upper === 'REDO' || upper === 'POOR' || upper === 'FAIL') scores.push(0)
        }
      }
    }
  }

  if (scores.length === 0) {
    return {
      score: null,
      totalRecords: records.length,
      approvedRecordsCount: approved.length,
      scoredItemsCount: 0,
    }
  }

  const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length
  const score = Math.round(avg * 1000) / 10

  return {
    score,
    totalRecords: records.length,
    approvedRecordsCount: approved.length,
    scoredItemsCount: scores.length,
  }
}

/* ===========================================================================
 * R39 A6.a Rosters, Shifts, Duty Roles, Changes, and Attendance
 * ===========================================================================
 */

export const OPS_ATTENDANCE_LABEL = 'operational attendance'
export const OPS_ASSIGNMENT_KINDS = [
  'SHIFT',
  'ON_CALL_PRIMARY',
  'ON_CALL_BACKUP',
] as const
export const OPS_ATTENDANCE_STATUSES = [
  'PRESENT',
  'ABSENT',
  'LATE',
  'HALF_DAY',
  'ON_LEAVE',
] as const
export const OPS_ROSTER_CHANGE_TYPES = ['LEAVE', 'SWAP', 'ON_CALL_SWAP', 'RELIEF'] as const
export const OPS_ROSTER_WARNING_CODES = [
  'OVERLAP',
  'DOUBLE_BOOKED',
  'MISSING_COVER',
  'SKILL_MISSING',
  'ON_LEAVE',
  'LEGAL_ROLE_MISSING',
] as const

export function isBlockingRosterWarning(code: unknown): boolean {
  return code === 'LEGAL_ROLE_MISSING'
}

export const MAX_ROSTER_PERIOD_DAYS = 42
export const MAX_ROSTER_ASSIGNMENTS = 2000
export const MAX_ATTENDANCE_CSV_ROWS = 2000

export type OpsAssignmentKind = (typeof OPS_ASSIGNMENT_KINDS)[number]
export type OpsAttendanceStatus = (typeof OPS_ATTENDANCE_STATUSES)[number]
export type OpsRosterChangeType = (typeof OPS_ROSTER_CHANGE_TYPES)[number]
export type OpsRosterWarningCode = (typeof OPS_ROSTER_WARNING_CODES)[number]
export type OpsWarningCode = OpsRosterWarningCode

export const DEPARTMENT_KEY_REGEX = /^[A-Z0-9_]{1,40}$/

export function validateDepartmentKey(key: unknown): string {
  if (typeof key !== 'string' || !DEPARTMENT_KEY_REGEX.test(key.trim())) {
    throw new Error(
      `Invalid department key "${String(key)}". Key must be upper snake case and <= 40 characters (^[A-Z0-9_]{1,40}$).`,
    )
  }
  return key.trim()
}

export function validateDepartmentLabel(label: unknown): string {
  if (typeof label !== 'string' || label.trim().length === 0 || label.trim().length > 100) {
    throw new Error('Department label must be between 1 and 100 characters.')
  }
  return label.trim()
}

export interface OpsDutyRole {
  id: string
  workspaceId: string
  key: string
  label: string
  sector?: OpsSector | 'core'
  department?: string | null
  archived: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsDepartmentEntry {
  key: string
  label: string
  archived: boolean
}

export interface OpsWorkspaceDepartmentsDoc {
  workspaceId: string
  departments: OpsDepartmentEntry[]
  version: number
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsShift {
  id: string
  workspaceId: string
  code: string
  name: string
  startMinute: number
  endMinute: number
  durationMinutes?: number
  dutyRoleKeys: string[]
  placeIds?: string[]
  archived: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsCoverageRule {
  id: string
  workspaceId: string
  placeId: string
  shiftId: string
  dutyRoleKey: string
  minCount: number
  requiredSkill?: string | null
  legalRoleKey?: string | null
  staffGroup?: OpsStaffGroup
  archived: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export type OpsPersonRef =
  | { kind: 'MEMBER'; uid: string }
  | { kind: 'WORKER'; workerId?: string; workerCode?: string; name?: string; partyId?: string; skills?: string[] }

export interface OpsRosterAssignment {
  id?: string
  dayKey: string
  shiftId: string
  dutyRoleKey: string
  kind: OpsAssignmentKind
  personRef: OpsPersonRef
}

export interface OpsRosterWarning {
  code: OpsWarningCode
  message: string
  personRef?: OpsPersonRef
  dayKey?: string
  shiftId?: string
  conflictingShiftId?: string
  conflictingDayKey?: string
  otherRosterId?: string
  placeId?: string
  dutyRoleKey?: string
  personKey?: string
  minCount?: number
  actualCount?: number
  requiredSkill?: string
  legalRoleKey?: string
}

export interface OpsRoster {
  id: string
  workspaceId: string
  placeId: string
  periodStart: string
  periodEnd: string
  draftAssignments: OpsRosterAssignment[]
  publishedVersion: number
  readAccess?: string[] | null
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsRosterLegalOverride {
  reason: string
  byUid: string
  at: unknown
  gaps: OpsRosterWarning[]
}

export interface OpsRosterVersion {
  version: number
  rosterId: string
  workspaceId: string
  placeId: string
  sector?: OpsSector | 'core'
  periodStart: string
  periodEnd: string
  readAccess?: string[] | null
  assignments: OpsRosterAssignment[]
  warnings: OpsRosterWarning[]
  acks: Record<string, string>
  changeId?: string | null
  legalOverride?: OpsRosterLegalOverride | null
  publishedBy: string
  publishedAt?: unknown
  createdAt?: unknown
  createdBy?: string
}

export type OpsRosterChangeState = 'REQUESTED' | 'ACCEPTED' | 'REJECTED' | 'APPROVED' | 'CANCELLED'

export interface OpsRosterChange {
  id: string
  workspaceId: string
  placeId?: string | null
  rosterId?: string | null
  type: OpsRosterChangeType
  state: OpsRosterChangeState
  requesterUid: string
  requesterPersonRef: OpsPersonRef
  targetPersonRef?: OpsPersonRef | null
  partyUids?: string[]
  dayKey: string
  endDayKey?: string | null
  shiftId?: string | null
  replacementShiftId?: string | null
  dutyRoleKey?: string | null
  requesterAssignmentId?: string | null
  targetAssignmentId?: string | null
  reliefFor?: OpsPersonRef | null
  requestId?: string | null
  reason?: string | null
  responseNote?: string | null
  respondedBy?: string | null
  respondedAt?: unknown
  decisionReason?: string | null
  selfApproved?: boolean
  selfApprovalReason?: string | null
  decidedBy?: string | null
  decidedAt?: unknown
  cancelReason?: string | null
  cancelledBy?: string | null
  cancelledAt?: unknown
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export const OPS_ATTENDANCE_SOURCES = [
  'PROXY',
  'IMPORT',
  'CLOCK',
  'QR',
  'PROXY_PEER',
] as const

export type OpsAttendanceSource = (typeof OPS_ATTENDANCE_SOURCES)[number]

export const OPS_ATTENDANCE_PROXY_STATES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
] as const

export type OpsAttendanceProxyState =
  (typeof OPS_ATTENDANCE_PROXY_STATES)[number]

export type OpsAttendanceCorrectionState = 'PENDING' | 'APPROVED' | 'REJECTED'

export interface OpsAttendanceCorrection {
  id: string
  attendanceId: string
  workspaceId: string
  personUid?: string | null
  before?: {
    status: OpsAttendanceStatus
    actualIn?: string | null
    actualOut?: string | null
  }
  after?: {
    status: OpsAttendanceStatus
    actualIn?: string | null
    actualOut?: string | null
  }
  requestedStatus: OpsAttendanceStatus
  requestedActualIn?: string | null
  requestedActualOut?: string | null
  reason: string
  requestedBy: string
  requestedAt?: unknown
  state: OpsAttendanceCorrectionState
  decisionReason?: string | null
  selfApproved?: boolean
  selfApprovalReason?: string | null
  decidedBy?: string | null
  decidedAt?: unknown
}

export interface OpsAttendance {
  id: string
  workspaceId: string
  dayKey: string
  personKey: string
  personRef: OpsPersonRef
  personUid?: string | null
  shiftId: string
  status: OpsAttendanceStatus
  source: OpsAttendanceSource
  proxyState?: OpsAttendanceProxyState | null
  rejectionReason?: string | null
  decidedBy?: string | null
  decidedAt?: unknown
  markedBy: string
  markedAt?: unknown
  actualIn?: string | null
  actualOut?: string | null
  locked: boolean
  label?: string
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsOnDutyHop {
  level: 'ON_SHIFT' | 'ON_CALL_PRIMARY' | 'ON_CALL_BACKUP' | 'PLACE_OWNER'
  personRefs?: OpsPersonRef[]
  ownerRoleKey?: string
  shiftId?: string
  placeId: string
  description?: string
}

/* Callables input types */

export interface CreateDutyRoleInput {
  workspaceId: string
  key: string
  label: string
  sector?: OpsSector | 'core'
  department?: string | null
  opId?: string
  expectedVersion?: number
}

export interface UpdateDutyRoleInput {
  workspaceId: string
  key: string
  label?: string
  sector?: OpsSector | 'core'
  department?: string | null
  archived?: boolean
  opId?: string
  expectedVersion?: number
}

export interface CreateDepartmentInput {
  workspaceId: string
  key: string
  label: string
  opId?: string
  expectedVersion?: number
}

export interface RenameDepartmentInput {
  workspaceId: string
  key: string
  label: string
  opId?: string
  expectedVersion?: number
}

export interface ArchiveDepartmentInput {
  workspaceId: string
  key: string
  archived?: boolean
  opId?: string
  expectedVersion?: number
}

export interface CreateShiftInput {
  workspaceId: string
  code: string
  name: string
  startMinute: number
  endMinute: number
  dutyRoleKeys: string[]
  placeIds?: string[]
  opId?: string
}

export interface UpdateShiftInput {
  workspaceId: string
  shiftId: string
  code?: string
  name?: string
  startMinute?: number
  endMinute?: number
  dutyRoleKeys?: string[]
  placeIds?: string[]
  archived?: boolean
  opId?: string
}

export interface CreateCoverageRuleInput {
  workspaceId: string
  placeId: string
  shiftId: string
  dutyRoleKey: string
  minCount: number
  requiredSkill?: string | null
  legalRoleKey?: string | null
  staffGroup?: OpsStaffGroup
  opId?: string
}

export interface UpdateCoverageRuleInput {
  workspaceId: string
  ruleId: string
  minCount?: number
  requiredSkill?: string | null
  legalRoleKey?: string | null
  staffGroup?: OpsStaffGroup | null
  archived?: boolean
  opId?: string
}

export interface CreateRosterInput {
  workspaceId: string
  placeId: string
  periodStart: string
  periodEnd: string
  draftAssignments?: OpsRosterAssignment[]
  opId?: string
}

export interface SaveDraftAssignmentsInput {
  workspaceId: string
  rosterId: string
  draftAssignments: OpsRosterAssignment[]
  opId?: string
}

export interface PreviewRosterWarningsInput {
  workspaceId: string
  rosterId: string
}

export interface PublishRosterInput {
  workspaceId: string
  rosterId: string
  acceptWarnings?: boolean
  changeId?: string
  opId?: string
  expectedVersion?: number
  legalOverride?: { reason: string }
}

export interface AcknowledgeRosterInput {
  workspaceId: string
  rosterId: string
  version: number
  opId?: string
}

export interface RequestRosterChangeInput {
  workspaceId: string
  type: OpsRosterChangeType
  requesterPersonRef: OpsPersonRef
  dayKey: string
  endDayKey?: string
  shiftId?: string
  replacementShiftId?: string
  dutyRoleKey?: string
  placeId?: string
  rosterId?: string
  targetPersonRef?: OpsPersonRef
  requesterAssignmentId?: string
  targetAssignmentId?: string
  reason?: string
  opId?: string
}

export interface RespondRosterChangeInput {
  workspaceId: string
  changeId: string
  decision: 'ACCEPT' | 'REJECT'
  responseNote?: string
  opId?: string
}

export interface DecideRosterChangeInput {
  workspaceId: string
  changeId: string
  decision: 'APPROVE' | 'REJECT'
  decisionReason?: string
  selfApprovalReason?: string
  overrideReason?: string
  opId?: string
}

export interface CancelRosterChangeInput {
  workspaceId: string
  changeId: string
  reason?: string
  opId?: string
}

export interface MarkAttendanceRollInput {
  workspaceId: string
  dayKey: string
  shiftId: string
  entries: Array<{
    personRef: OpsPersonRef
    status: OpsAttendanceStatus
    actualIn?: string | null
    actualOut?: string | null
  }>
  opId?: string
}

export interface RequestAttendanceCorrectionInput {
  workspaceId: string
  attendanceId: string
  requestedStatus: OpsAttendanceStatus
  requestedActualIn?: string | null
  requestedActualOut?: string | null
  reason: string
  opId?: string
}

export interface DecideAttendanceCorrectionInput {
  workspaceId: string
  attendanceId: string
  correctionId: string
  decision: 'APPROVE' | 'REJECT'
  decisionReason?: string
  selfApprovalReason?: string
  opId?: string
}

export interface AttendanceCsvRow {
  dayKey: string
  shiftId: string
  personRef: OpsPersonRef
  status: OpsAttendanceStatus
  actualIn?: string | null
  actualOut?: string | null
}

export interface ImportAttendanceCsvInput {
  workspaceId: string
  csvText: string
  dryRun?: boolean
  opId?: string
}

export interface AssignReliefInput {
  workspaceId: string
  requestId: string
  reliefPersonRef: OpsPersonRef
  opId?: string
}

export interface AssignReliefResult {
  success: true
  state: 'DONE'
  changeId: string
  newVersion: number
}

export interface OpsImportAttendanceCsvRowError {
  rowNumber: number
  field: string
  message: string
}

export interface ImportAttendanceCsvResult {
  totalRows: number
  validRows: number
  errorRows: OpsImportAttendanceCsvRowError[]
  newCount: number
  correctionRequestCount: number
}

/* ===========================================================================
 * R39 A5.a Schedules and Due Instances
 * ===========================================================================
 */

export const OPS_SCHEDULE_STATUSES = ['ACTIVE', 'PAUSED', 'ARCHIVED'] as const
export type OpsScheduleStatus = (typeof OPS_SCHEDULE_STATUSES)[number]

export const OPS_DUE_STATUSES = [
  'OPEN',
  'DONE',
  'LATE_DONE',
  'MISSED',
  'SKIPPED',
] as const
export type OpsDueStatus = (typeof OPS_DUE_STATUSES)[number]

export const OPS_SCHEDULE_CADENCE_KINDS = [
  'PER_SHIFT',
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'YEARLY',
  'EVERY_N_DAYS',
] as const
export type OpsScheduleCadenceKind = (typeof OPS_SCHEDULE_CADENCE_KINDS)[number]

export type OpsScheduleCadence =
  | { kind: 'PER_SHIFT'; shiftId: string }
  | { kind: 'DAILY' }
  | { kind: 'WEEKLY' }
  | { kind: 'MONTHLY' }
  | { kind: 'QUARTERLY' }
  | { kind: 'YEARLY' }
  | { kind: 'EVERY_N_DAYS'; intervalDays: number; anchorDayKey: string }

export const MAX_SCHEDULE_PLACES = 50
export const MAX_GRACE_MINUTES = 240
export const DEFAULT_GRACE_MINUTES = 0
export const MIN_ESCALATE_EVERY_MINUTES = 5
export const MAX_ESCALATE_EVERY_MINUTES = 1440
export const DEFAULT_ESCALATE_EVERY_MINUTES = 30
export const PATROL_DEFAULT_GRACE_MINUTES = 15
export const PATROL_DEFAULT_ESCALATE_EVERY_MINUTES = 15
export const MIN_SKIP_REASON_LENGTH = 3
export const MAX_SKIP_REASON_LENGTH = 500

export function isSitesPatrolPlaces(
  sector: OpsSector | string | undefined,
  placeLevels: readonly (string | undefined)[],
): boolean {
  const def = sector ? OPS_SECTORS[sector as OpsSector] : undefined
  const hasCheckpointQr = Boolean(
    def && (def.qrLevels ? def.qrLevels.includes('CHECKPOINT') : def.qrLeafLevel === 'CHECKPOINT'),
  )
  return (
    hasCheckpointQr &&
    placeLevels.length > 0 &&
    placeLevels.length <= MAX_SCHEDULE_PLACES &&
    placeLevels.every((lvl) => lvl === 'CHECKPOINT')
  )
}

export interface OpsScheduleWindowSpec {
  startMinute?: number
  durationMinutes?: number
  isoWeekday?: number
  dayOfMonth?: number
  monthOfQuarter?: number
  month?: number
}

export interface OpsSchedulePlaceTarget {
  rootPlaceId: string
  level: OpsPlaceLevel
}

export function validateSchedulePlaceTarget(target: unknown): OpsSchedulePlaceTarget {
  if (!target || typeof target !== 'object') {
    throw new Error('placeTarget must be an object with rootPlaceId and level')
  }
  const t = target as Record<string, unknown>
  const rootPlaceId = typeof t.rootPlaceId === 'string' ? t.rootPlaceId.trim() : ''
  const level = typeof t.level === 'string' ? t.level.trim() : ''
  if (!rootPlaceId) {
    throw new Error('placeTarget.rootPlaceId is required')
  }
  if (!level) {
    throw new Error('placeTarget.level is required')
  }
  return { rootPlaceId, level: level as OpsPlaceLevel }
}

export interface OpsSchedule {
  id: string
  workspaceId: string
  sector: OpsSector
  templateId: string
  placeIds: string[]
  placeTarget?: OpsSchedulePlaceTarget | null
  cadence: OpsScheduleCadence
  window: OpsScheduleWindowSpec
  assigneeDutyRoleKey: string
  escalationFirstDutyRoleKey?: string | null
  graceMinutes: number
  escalateEveryMinutes: number
  status: OpsScheduleStatus
  revision?: number
  generatedUntil?: number | null
  nextGenAt?: number | null
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsDueEscalationEntry {
  hop: number
  level: 'ON_SHIFT' | 'ON_CALL_PRIMARY' | 'ON_CALL_BACKUP' | 'PLACE_OWNER'
  personRefs: OpsPersonRef[]
  uids: string[]
  ownerRoleKey?: string
  shiftId?: string
  placeId: string
  at: number
}

export interface OpsDue {
  id: string
  workspaceId: string
  scheduleId: string
  sector: OpsSector
  templateId: string
  placeId: string
  periodKey: string
  windowStart: number
  windowEnd: number
  graceMinutes: number
  escalateEveryMinutes: number
  assigneeDutyRoleKey: string
  escalationFirstDutyRoleKey?: string | null
  status: OpsDueStatus
  scheduleRevision?: number
  recordId?: string | null
  doneAt?: number | null
  doneBy?: string | null
  skippedAt?: number | null
  skippedBy?: string | null
  skipReason?: string | null
  escalations: OpsDueEscalationEntry[]
  nextEscalationAt?: number | null
  readAccess: string[] | null
  createdAt?: unknown
  updatedAt?: unknown
}

export type OpsPatrolRoundState = 'DONE' | 'MISSED' | 'IN_PROGRESS' | 'UPCOMING'

export interface OpsPatrolRound {
  id: string
  scheduleId: string
  periodKey: string
  dues: OpsDue[]
  state: OpsPatrolRoundState
  scannedCount: number
  totalCheckpoints: number
  windowStart: number
  windowEnd: number
  graceMinutes: number
  graceEnd: number
  escalations: OpsDueEscalationEntry[]
  placeIds: string[]
  templateId: string
  assigneeDutyRoleKey: string
  escalationFirstDutyRoleKey?: string | null
}

export function groupPatrolRounds(
  dues: readonly OpsDue[],
  nowMs: number = Date.now(),
): OpsPatrolRound[] {
  const sitesDues = dues.filter((d) => {
    const def = OPS_SECTORS[d.sector]
    return Boolean(
      def && (def.qrLevels ? def.qrLevels.includes('CHECKPOINT') : def.qrLeafLevel === 'CHECKPOINT'),
    )
  })
  if (sitesDues.length === 0) return []

  const groups = new Map<string, OpsDue[]>()
  for (const d of sitesDues) {
    const key = `${d.scheduleId}__${d.periodKey}`
    const list = groups.get(key)
    if (list) {
      list.push(d)
    } else {
      groups.set(key, [d])
    }
  }

  const rounds: OpsPatrolRound[] = []
  for (const [key, dueList] of groups.entries()) {
    const [scheduleId, periodKey] = key.split('__')

    dueList.sort((a, b) => (a.placeId || '').localeCompare(b.placeId || '') || (a.id || '').localeCompare(b.id || ''))

    const first = dueList[0]
    let minWindowStart = first.windowStart
    let maxWindowEnd = first.windowEnd
    let maxGraceMinutes = first.graceMinutes ?? 0
    let scannedCount = 0
    let hasMissed = false
    let allDoneOrSkipped = true

    const allEscalations: OpsDueEscalationEntry[] = []
    const placeIds: string[] = []

    for (const d of dueList) {
      if (d.windowStart < minWindowStart) minWindowStart = d.windowStart
      if (d.windowEnd > maxWindowEnd) maxWindowEnd = d.windowEnd
      const grace = d.graceMinutes ?? 0
      if (grace > maxGraceMinutes) maxGraceMinutes = grace

      if (d.status === 'DONE' || d.status === 'LATE_DONE') {
        scannedCount++
      } else if (d.status !== 'SKIPPED') {
        allDoneOrSkipped = false
      }

      const graceEnd = d.windowEnd + grace * 60_000
      if (d.status === 'MISSED' || (d.status === 'OPEN' && nowMs > graceEnd)) {
        hasMissed = true
      }

      if (d.placeId && !placeIds.includes(d.placeId)) {
        placeIds.push(d.placeId)
      }

      if (Array.isArray(d.escalations)) {
        for (const esc of d.escalations) {
          if (!allEscalations.some((e) => e.hop === esc.hop && e.at === esc.at)) {
            allEscalations.push(esc)
          }
        }
      }
    }

    allEscalations.sort((a, b) => a.hop - b.hop || a.at - b.at)

    const maxGraceEnd = maxWindowEnd + maxGraceMinutes * 60_000

    let state: OpsPatrolRoundState
    if (hasMissed) {
      state = 'MISSED'
    } else if (allDoneOrSkipped && dueList.length > 0) {
      state = 'DONE'
    } else if (scannedCount > 0 && nowMs <= maxGraceEnd) {
      state = 'IN_PROGRESS'
    } else {
      state = 'UPCOMING'
    }

    rounds.push({
      id: key,
      scheduleId,
      periodKey,
      dues: dueList,
      state,
      scannedCount,
      totalCheckpoints: dueList.length,
      windowStart: minWindowStart,
      windowEnd: maxWindowEnd,
      graceMinutes: maxGraceMinutes,
      graceEnd: maxGraceEnd,
      escalations: allEscalations,
      placeIds,
      templateId: first.templateId,
      assigneeDutyRoleKey: first.assigneeDutyRoleKey,
      escalationFirstDutyRoleKey: first.escalationFirstDutyRoleKey ?? null,
    })
  }

  rounds.sort((a, b) => a.windowStart - b.windowStart || a.scheduleId.localeCompare(b.scheduleId))
  return rounds
}

export interface CreateScheduleInput {
  workspaceId: string
  templateId: string
  placeIds?: string[]
  placeTarget?: OpsSchedulePlaceTarget | null
  cadence: OpsScheduleCadence
  window: OpsScheduleWindowSpec
  assigneeDutyRoleKey: string
  escalationFirstDutyRoleKey?: string | null
  graceMinutes?: number
  escalateEveryMinutes?: number
  opId?: string
}

export interface UpdateScheduleInput {
  workspaceId: string
  scheduleId: string
  placeIds?: string[]
  placeTarget?: OpsSchedulePlaceTarget | null
  cadence?: OpsScheduleCadence
  window?: OpsScheduleWindowSpec
  assigneeDutyRoleKey?: string
  escalationFirstDutyRoleKey?: string | null
  graceMinutes?: number
  escalateEveryMinutes?: number
  opId?: string
}

export interface RecordCheckpointScanGeo {
  lat: number
  lng: number
  accuracy?: number
}

export interface RecordCheckpointScanInput {
  workspaceId: string
  token: string
  geo?: RecordCheckpointScanGeo | null
  note?: string | null
  photoPath?: string | null
  opId?: string
}

export interface RecordCheckpointScanResult {
  recordId: string
  dueId?: string | null
  offWindow: boolean
  dueStatus?: OpsDueStatus | null
  placeId: string
  templateId?: string
  templateVersion?: number
  replayed?: boolean
}

export interface PauseScheduleInput {
  workspaceId: string
  scheduleId: string
  opId?: string
}

export interface ResumeScheduleInput {
  workspaceId: string
  scheduleId: string
  opId?: string
}

export interface ArchiveScheduleInput {
  workspaceId: string
  scheduleId: string
  opId?: string
}

export interface SkipDueInput {
  workspaceId: string
  dueId: string
  reason: string
  opId?: string
}

// ---------------------------------------------------------------------------
// R39 A7.a Requests Engine Types & Constants Mirror
// ---------------------------------------------------------------------------

export type OpsRequestSector = OpsSector

export type OpsRequestState =
  | 'OPEN'
  | 'ACKNOWLEDGED'
  | 'IN_PROGRESS'
  | 'DONE'
  | 'VERIFIED'
  | 'REOPENED'
  | 'CANCELLED'

export const OPS_REQUEST_STATES: readonly OpsRequestState[] = [
  'OPEN',
  'ACKNOWLEDGED',
  'IN_PROGRESS',
  'DONE',
  'VERIFIED',
  'REOPENED',
  'CANCELLED',
] as const

export const OPS_REQUEST_CLOSED_STATES: ReadonlySet<OpsRequestState> = new Set<OpsRequestState>([
  'DONE',
  'VERIFIED',
  'CANCELLED',
])

export const OPS_REQUEST_OPEN_STATES: readonly OpsRequestState[] = [
  'OPEN',
  'ACKNOWLEDGED',
  'IN_PROGRESS',
  'REOPENED',
] as const

export interface OpsRequestKind {
  key: string
  labelKey: string
  label?: string
  sector: OpsRequestSector
  ackMinutes: number
  dueMinutes: number
  escalateEveryMinutes: number
  dutyRoleKey?: string
  verifyRequired: boolean
  verifierRoleKey?: string
  restricted: boolean
  viewerRoleKeys: string[]
  patientLinked: boolean
  photoEnabled?: boolean
  assetRequired?: boolean
  donePhotoRequired?: boolean
  isolateAssetOnRaise?: boolean | 'HIGH_CRITICAL'
  strictDifferentPerson?: boolean
  verifierNotRaiser?: boolean
  archived?: boolean
}

export interface OpsRequestKindOverride {
  workspaceId: string
  key: string
  label?: string
  sector: OpsRequestSector
  ackMinutes?: number
  dueMinutes?: number
  escalateEveryMinutes?: number
  dutyRoleKey?: string
  verifyRequired?: boolean
  verifierRoleKey?: string
  restricted?: boolean
  viewerRoleKeys?: string[]
  patientLinked?: boolean
  photoEnabled?: boolean
  assetRequired?: boolean
  donePhotoRequired?: boolean
  isolateAssetOnRaise?: boolean | 'HIGH_CRITICAL'
  strictDifferentPerson?: boolean
  verifierNotRaiser?: boolean
  archived?: boolean
  updatedAt?: number
  updatedByUid?: string
}

export interface OpsJobGeo {
  lat: number
  lng: number
  accuracyM: number
}

export type OpsJobGeoStatus = 'CAPTURED' | 'DENIED' | 'UNAVAILABLE'

export const JOBS_REQUEST_ALLOWED_FIELDS = [
  'customerId',
  'jobDate',
  'windowStart',
  'windowEnd',
  'revisitOf',
  'revisitIds',
  'startGeo',
  'doneGeo',
  'startGeoStatus',
  'doneGeoStatus',
] as const

export const STAYS_REQUEST_ALLOWED_FIELDS = [
  'raisedVia',
  'guestOption',
  'guestStatusHash',
  'followUpOf',
  'extraRequest',
] as const

export const OPS_GUEST_OPTIONS = [
  'TOWELS',
  'WATER',
  'TOILETRIES',
  'ROOM_CLEANING',
  'SOMETHING_BROKEN',
  'OTHER',
  'COMPLAINT',
] as const

export type OpsGuestOption = (typeof OPS_GUEST_OPTIONS)[number]

export const OPS_GUEST_OPTION_KINDS: Record<OpsGuestOption, 'GUEST_REQUEST' | 'GUEST_COMPLAINT'> = {
  TOWELS: 'GUEST_REQUEST',
  WATER: 'GUEST_REQUEST',
  TOILETRIES: 'GUEST_REQUEST',
  ROOM_CLEANING: 'GUEST_REQUEST',
  SOMETHING_BROKEN: 'GUEST_REQUEST',
  OTHER: 'GUEST_REQUEST',
  COMPLAINT: 'GUEST_COMPLAINT',
}

export const OPS_GUEST_REQUEST_BUCKETS = ['RECEIVED', 'ON_IT', 'DONE', 'CLOSED'] as const
export type OpsGuestRequestBucket = (typeof OPS_GUEST_REQUEST_BUCKETS)[number]

const GUEST_NOTE_EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/i
const GUEST_NOTE_DIGITS_RE = /\d{8,}/
const GUEST_NOTE_HTML_RE = /[<>]/

export function validateGuestRequestNote(
  note: unknown,
): { valid: true; sanitizedNote: string } | { valid: false; message: string } {
  if (note === null || note === undefined || note === '') {
    return { valid: true, sanitizedNote: '' }
  }
  if (typeof note !== 'string') {
    return { valid: false, message: 'Note must be plain text.' }
  }
  const trimmed = note.trim()
  if (trimmed.length === 0) {
    return { valid: true, sanitizedNote: '' }
  }
  if (trimmed.length > 500) {
    return { valid: false, message: 'Note must not exceed 500 characters.' }
  }
  if (GUEST_NOTE_HTML_RE.test(trimmed)) {
    return { valid: false, message: 'Note must be plain text without HTML.' }
  }
  if (GUEST_NOTE_EMAIL_RE.test(trimmed)) {
    return { valid: false, message: 'Note must not contain email addresses.' }
  }
  if (GUEST_NOTE_DIGITS_RE.test(trimmed)) {
    return { valid: false, message: 'Note must not contain 8 or more digits in a row.' }
  }
  return { valid: true, sanitizedNote: trimmed }
}

export interface OpsGuestExtraRequest {
  bookableId: string
  sessionKey?: string
  preferredTimeText?: string
  guests: number // 1-20
}

export function cleanGuestExtraRequest(raw: unknown): OpsGuestExtraRequest {
  if (!raw || typeof raw !== 'object') {
    throw new Error('extraRequest must be an object.')
  }
  const obj = raw as Record<string, unknown>
  const bookableId = typeof obj.bookableId === 'string' ? obj.bookableId.trim() : ''
  if (!bookableId || bookableId.length > 120) {
    throw new Error('bookableId is required and must be between 1 and 120 characters.')
  }
  const sessionKey =
    typeof obj.sessionKey === 'string' && obj.sessionKey.trim() ? obj.sessionKey.trim() : undefined
  const preferredTimeText =
    typeof obj.preferredTimeText === 'string' && obj.preferredTimeText.trim()
      ? obj.preferredTimeText.trim()
      : undefined
  if (preferredTimeText && preferredTimeText.length > 80) {
    throw new Error('preferredTimeText cannot exceed 80 characters.')
  }
  const guestsNum = typeof obj.guests === 'number' ? obj.guests : Number(obj.guests)
  if (!Number.isInteger(guestsNum) || guestsNum < 1 || guestsNum > 20) {
    throw new Error('guests must be an integer between 1 and 20.')
  }
  const result: OpsGuestExtraRequest = {
    bookableId,
    guests: guestsNum,
  }
  if (sessionKey) {
    result.sessionKey = sessionKey
  }
  if (preferredTimeText) {
    result.preferredTimeText = preferredTimeText
  }
  return result
}

export interface GetGuestRoomFormInput {
  token: string
}

export interface GetGuestRoomFormResult {
  propertyName: string
  placeLabel: string
  options: OpsGuestOption[]
  roomCode?: string
  whatsappLink?: string | null
  guestBookables?: Array<{
    id: string
    name: string
    extraKind?: string
  }>
}

export interface RaiseGuestRequestInput {
  token: string
  option: OpsGuestOption
  note?: string
  extraRequest?: OpsGuestExtraRequest
  opId: string
}

export interface RaiseGuestRequestResult {
  success: boolean
  statusToken: string
  requestId: string
  replayed?: boolean
}

export interface GetGuestRequestStatusInput {
  token: string
}

export interface GetGuestRequestStatusResult {
  option: OpsGuestOption
  placeLabel: string
  bucket: OpsGuestRequestBucket
  createdAt: number
  ackAt: number | null
  doneAt: number | null
  canStillAProblem: boolean
  confirmedTime?: string
  declinedReason?: string
}

export interface ConfirmGuestExtraRequestInput {
  workspaceId: string
  requestId: string
  sessionKey?: string
  opId?: string
}

export interface ConfirmGuestExtraRequestResult {
  success: boolean
  confirmed: boolean
  bookingId?: string
  confirmedTime?: string
  reason?: string
  replayed?: boolean
}

export interface DeclineGuestExtraRequestInput {
  workspaceId: string
  requestId: string
  reason: string
  opId?: string
}

export interface DeclineGuestExtraRequestResult {
  success: boolean
  requestId: string
  declinedReason?: string
  replayed?: boolean
}

export interface GuestStillAProblemInput {
  token: string
  note?: string
  opId: string
}

export interface GuestStillAProblemResult {
  success: boolean
  followUpRequestId: string
  replayed?: boolean
}

export interface SetPropertyGuestRequestsEnabledInput {
  workspaceId: string
  propertyId: string
  guestRequestsEnabled: boolean
  opId?: string
}

export interface SetPropertyGuestRequestsEnabledResult {
  success: boolean
  propertyId: string
  guestRequestsEnabled: boolean
  replayed?: boolean
}

export interface OpsRequestRaisedFor {
  kind: 'WORKER'
  workerId: string
}

export interface OpsRequest {
  id: string
  workspaceId: string
  sector: OpsRequestSector
  kind: string
  kindSnapshot: OpsRequestKind
  placeId: string
  assetId?: string
  recordId?: string
  title: string
  titleKey?: string
  titleParams?: Record<string, string>
  details?: string
  photos: string[]
  donePhotos: string[]
  raisedByUid: string
  raisedFor?: OpsRequestRaisedFor
  assigneeUid?: string
  assigneeHopLevel?: string
  state: OpsRequestState
  ackDueAt: number
  dueAt: number
  acknowledgedAt?: number
  doneAt?: number
  doneByUid?: string
  verifiedAt?: number
  verifiedByUid?: string
  selfVerified?: boolean
  escalationLevel: number
  escalations: OpsDueEscalationEntry[]
  nextEscalationAt?: number | null
  restricted: boolean
  viewerRoleKeys: string[]
  readAccess: string[] | null
  dayKey?: string
  shiftId?: string
  dutyRoleKey?: string
  reliefFor?: OpsPersonRef
  reliefPersonRef?: OpsPersonRef
  description?: string
  drawingRefs?: ConDrawingRef[]
  drawingIds?: string[]
  drawingFlags?: ConDrawingFlag[]
  linkedItem?: { type: 'REQUEST' | 'RECORD'; id: string }
  conIssueId?: string
  // Jobs fields (sector jobs only, SO-P118)
  customerId?: string
  jobDate?: string | null
  windowStart?: string | null
  windowEnd?: string | null
  revisitOf?: string
  revisitIds?: string[]
  startGeo?: OpsJobGeo | null
  doneGeo?: OpsJobGeo | null
  startGeoStatus?: OpsJobGeoStatus
  doneGeoStatus?: OpsJobGeoStatus
  // Stays fields (sector stays only, SO-P119, SO-P123)
  raisedVia?: 'GUEST_QR' | string
  guestOption?: OpsGuestOption | string
  guestStatusHash?: string
  followUpOf?: string
  extraRequest?: OpsGuestExtraRequest
  confirmedBookingId?: string
  confirmedTime?: string
  declinedReason?: string
  capacityRefusalReason?: string
  // Plant fields (sector plant only, SO-P264, SO-P266)
  downtimeReason?: PlantDowntimeReason
  downtimeNote?: string
  holdItem?: string
  quantity?: number
  unit?: string
  reason?: string
  whatNearlyHappened?: string
  category?: PlantNearMissCategory
  immediateAction?: string
  createdAt: number
  updatedAt: number
}

export const PLANT_NEAR_MISS_CATEGORIES = [
  'SLIP_TRIP',
  'MACHINE_GUARD',
  'CHEMICAL',
  'ELECTRICAL',
  'FIRE',
  'MATERIAL_HANDLING',
  'OTHER',
] as const
export type PlantNearMissCategory = (typeof PLANT_NEAR_MISS_CATEGORIES)[number]

export const PLANT_DOWNTIME_REASONS = [
  'MECHANICAL',
  'ELECTRICAL',
  'WAITING_FOR_SPARE',
  'POWER_CUT',
  'TOOLING',
  'MATERIAL',
  'OPERATOR',
  'OTHER',
] as const
export type PlantDowntimeReason = (typeof PLANT_DOWNTIME_REASONS)[number]

export type OpsRequestEventType =
  | 'RAISED'
  | 'PHOTOS_ATTACHED'
  | 'ACKNOWLEDGED'
  | 'STARTED'
  | 'ASSIGNED'
  | 'SCHEDULED'
  | 'MARKED_DONE'
  | 'VERIFIED'
  | 'REOPENED'
  | 'CANCELLED'
  | 'ESCALATED'
  | 'APPROVAL_SENT'
  | 'APPROVAL_DECIDED'
  | 'APPROVAL_ENDED'

export interface OpsRequestEvent {
  id: string
  requestId: string
  type: OpsRequestEventType
  byUid: string
  at: number
  fromState: OpsRequestState | null
  toState: OpsRequestState
  reason?: string
  hop?: OpsDueEscalationEntry
  assigneeUid?: string
  fromAssigneeUid?: string
  toAssigneeUid?: string
  schedule?: {
    jobDate: string | null
    windowStart?: string | null
    windowEnd?: string | null
  }
}

export const CUSTOM_REQUEST_KIND_KEY_REGEX = /^custom_[a-z0-9_]{1,32}$/

export const MIN_REQUEST_TITLE_LENGTH = 1
export const MAX_REQUEST_TITLE_LENGTH = 120
export const MAX_REQUEST_DETAILS_LENGTH = 2000
export const MAX_REQUEST_PHOTOS = 4
export const MIN_REQUEST_REASON_LENGTH = 3
export const MAX_REQUEST_REASON_LENGTH = 500
export const MAX_REQUEST_REOPEN_DAYS = 7
export const MIN_REQUEST_LABEL_LENGTH = 1
export const MAX_REQUEST_LABEL_LENGTH = 60
export const MIN_ACK_MINUTES = 1
export const MAX_ACK_MINUTES = 10080
export const MIN_DUE_MINUTES = 1
export const MAX_DUE_MINUTES = 43200

export const OPS_REQUEST_BUILTIN_KINDS: readonly OpsRequestKind[] = [
  // Construction (5 kinds)
  {
    key: 'SNAG',
    labelKey: 'NLSOpsRequestKindSnag',
    sector: 'construction',
    ackMinutes: 120,
    dueMinutes: 1440,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'FAULT',
    labelKey: 'NLSOpsRequestKindFault',
    sector: 'construction',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    isolateAssetOnRaise: 'HIGH_CRITICAL',
  },
  {
    key: 'HINDRANCE',
    labelKey: 'NLSOpsRequestKindHindrance',
    sector: 'construction',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'RFI',
    labelKey: 'NLSOpsRequestKindRfi',
    sector: 'construction',
    ackMinutes: 120,
    dueMinutes: 2880,
    escalateEveryMinutes: 1440,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'SELECTION',
    labelKey: 'NLSOpsRequestKindSelection',
    sector: 'construction',
    ackMinutes: 120,
    dueMinutes: 4320,
    escalateEveryMinutes: 1440,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'CHANGE_REQUEST',
    labelKey: 'NLSOpsRequestKindChangeRequest',
    sector: 'construction',
    ackMinutes: 120,
    dueMinutes: 7200,
    escalateEveryMinutes: 1440,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'SUPPLY_REQUEST',
    labelKey: 'NLSOpsRequestKindSupplyRequest',
    sector: 'construction',
    ackMinutes: 120,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'INCIDENT',
    labelKey: 'NLSOpsRequestKindIncident',
    sector: 'construction',
    ackMinutes: 30,
    dueMinutes: 480,
    escalateEveryMinutes: 30,
    verifyRequired: false,
    restricted: true,
    viewerRoleKeys: ['INCIDENT_HANDLER'],
    patientLinked: false,
  },
  {
    key: 'PM',
    labelKey: 'NLSOpsRequestKindPm',
    sector: 'construction',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  {
    key: 'CALIBRATION',
    labelKey: 'NLSOpsRequestKindCalibration',
    sector: 'construction',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  {
    key: 'DRAWING_ACK',
    labelKey: 'NLSOpsRequestKindDrawingAck',
    sector: 'construction',
    ackMinutes: 120,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'INSPECTION',
    labelKey: 'NLSOpsRequestKindInspection',
    sector: 'construction',
    ackMinutes: 120,
    dueMinutes: 1440,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'NCR',
    labelKey: 'NLSOpsRequestKindNcr',
    sector: 'construction',
    ackMinutes: 240,
    dueMinutes: 4320,
    escalateEveryMinutes: 240,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  // Hospital (6 kinds)
  {
    key: 'HOUSEKEEPING',
    labelKey: 'NLSOpsRequestKindHousekeeping',
    sector: 'hospital',
    ackMinutes: 60,
    dueMinutes: 240,
    escalateEveryMinutes: 30,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'DISCHARGE_CLEAN',
    labelKey: 'NLSOpsRequestKindDischargeClean',
    sector: 'hospital',
    ackMinutes: 30,
    dueMinutes: 120,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'SPILL',
    labelKey: 'NLSOpsRequestKindSpill',
    sector: 'hospital',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 15,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'FAULT',
    labelKey: 'NLSOpsRequestKindFault',
    sector: 'hospital',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    isolateAssetOnRaise: 'HIGH_CRITICAL',
  },
  {
    key: 'REPLENISHMENT',
    labelKey: 'NLSOpsRequestKindReplenishment',
    sector: 'hospital',
    ackMinutes: 120,
    dueMinutes: 720,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'COMPLAINT',
    labelKey: 'NLSOpsRequestKindComplaint',
    sector: 'hospital',
    ackMinutes: 60,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    verifyRequired: false,
    restricted: true,
    viewerRoleKeys: ['INCIDENT_HANDLER'],
    patientLinked: true,
  },
  {
    key: 'PM',
    labelKey: 'NLSOpsRequestKindPm',
    sector: 'hospital',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  {
    key: 'CALIBRATION',
    labelKey: 'NLSOpsRequestKindCalibration',
    sector: 'hospital',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  // Sites (4 kinds) — SO-P102
  {
    key: 'RELIEF_NEEDED',
    labelKey: 'NLSOpsRequestKindReliefNeeded',
    sector: 'sites',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 15,
    dutyRoleKey: 'ROSTER_MANAGER',
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'INCIDENT',
    labelKey: 'NLSOpsRequestKindIncident',
    sector: 'sites',
    ackMinutes: 15,
    dueMinutes: 240,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    verifierRoleKey: 'REQUEST_VERIFIER',
    restricted: true,
    viewerRoleKeys: ['INCIDENT_HANDLER'],
    patientLinked: false,
  },
  {
    key: 'CLIENT_COMPLAINT',
    labelKey: 'NLSOpsRequestKindClientComplaint',
    sector: 'sites',
    ackMinutes: 60,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'SUPPLY_REQUEST',
    labelKey: 'NLSOpsRequestKindSupplyRequest',
    sector: 'sites',
    ackMinutes: 240,
    dueMinutes: 2880,
    escalateEveryMinutes: 480,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  // Stays (7 kinds) — SO-P204, SO-P113
  {
    key: 'GUEST_REQUEST',
    labelKey: 'NLSOpsRequestKindGuestRequest',
    sector: 'stays',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 15,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'GUEST_COMPLAINT',
    labelKey: 'NLSOpsRequestKindGuestComplaint',
    sector: 'stays',
    ackMinutes: 15,
    dueMinutes: 240,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    verifierRoleKey: 'REQUEST_VERIFIER',
    restricted: true,
    viewerRoleKeys: ['FRONT_DESK'],
    patientLinked: false,
  },
  {
    key: 'MINIBAR_CHECK',
    labelKey: 'NLSOpsRequestKindMinibarCheck',
    sector: 'stays',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 15,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'LAUNDRY_SHORT',
    labelKey: 'NLSOpsRequestKindLaundryShort',
    sector: 'stays',
    ackMinutes: 60,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'FAULT',
    labelKey: 'NLSOpsRequestKindFault',
    sector: 'stays',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    isolateAssetOnRaise: 'HIGH_CRITICAL',
  },
  {
    key: 'PM',
    labelKey: 'NLSOpsRequestKindPm',
    sector: 'stays',
    ackMinutes: 120,
    dueMinutes: 1440,
    escalateEveryMinutes: 240,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  {
    key: 'CALIBRATION',
    labelKey: 'NLSOpsRequestKindCalibration',
    sector: 'stays',
    ackMinutes: 60,
    dueMinutes: 480,
    escalateEveryMinutes: 60,
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  {
    key: 'EXTRA_BOOKING',
    labelKey: 'NLSOpsRequestKindExtraBooking',
    sector: 'stays',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 15,
    dutyRoleKey: 'BOOKING_DESK',
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  // Jobs (3 kinds, SO-P118)
  {
    key: 'JOB',
    labelKey: 'NLSOpsRequestKindJob',
    sector: 'jobs',
    ackMinutes: 60,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    dutyRoleKey: 'dispatcher',
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    photoEnabled: true,
    donePhotoRequired: true,
  },
  {
    key: 'REVISIT',
    labelKey: 'NLSOpsRequestKindRevisit',
    sector: 'jobs',
    ackMinutes: 60,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    dutyRoleKey: 'dispatcher',
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    photoEnabled: true,
    donePhotoRequired: true,
  },
  {
    key: 'QUOTE_REQUEST',
    labelKey: 'NLSOpsRequestKindQuoteRequest',
    sector: 'jobs',
    ackMinutes: 240,
    dueMinutes: 2880,
    escalateEveryMinutes: 240,
    dutyRoleKey: 'dispatcher',
    verifyRequired: false,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    photoEnabled: true,
    donePhotoRequired: false,
  },
  // Plant (2 kinds, SO-P264, SO-P266)
  {
    key: 'BREAKDOWN',
    labelKey: 'NLSOpsRequestKindBreakdown',
    sector: 'plant',
    ackMinutes: 15,
    dueMinutes: 240,
    escalateEveryMinutes: 30,
    dutyRoleKey: 'MAINTENANCE',
    verifyRequired: true,
    verifierRoleKey: 'ASSET_RELEASER',
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    photoEnabled: true,
    assetRequired: true,
    isolateAssetOnRaise: true,
    strictDifferentPerson: true,
  },
  {
    key: 'QUALITY_HOLD',
    labelKey: 'NLSOpsRequestKindQualityHold',
    sector: 'plant',
    ackMinutes: 60,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    dutyRoleKey: 'QUALITY_RELEASER',
    verifyRequired: true,
    verifierRoleKey: 'QUALITY_RELEASER',
    restricted: true,
    viewerRoleKeys: ['QUALITY_RELEASER'],
    patientLinked: false,
    photoEnabled: true,
    strictDifferentPerson: true,
    verifierNotRaiser: true,
  },
  {
    key: 'NEAR_MISS',
    labelKey: 'NLSOpsRequestKindNearMiss',
    sector: 'plant',
    ackMinutes: 60,
    dueMinutes: 1440,
    escalateEveryMinutes: 120,
    dutyRoleKey: 'INCIDENT_HANDLER',
    verifyRequired: false,
    restricted: true,
    viewerRoleKeys: ['INCIDENT_HANDLER'],
    patientLinked: false,
    photoEnabled: true,
  },
  // Clinics (2 kinds, SO-P258)
  {
    key: 'COLD_CHAIN_EXCURSION',
    labelKey: 'NLSOpsRequestKindColdChainExcursion',
    sector: 'clinics',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  {
    key: 'WATER_QUALITY',
    labelKey: 'NLSOpsRequestKindWaterQuality',
    sector: 'clinics',
    ackMinutes: 30,
    dueMinutes: 120,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
    assetRequired: true,
  },
  {
    key: 'SAMPLE_REDRAW',
    labelKey: 'NLSOpsRequestKindSampleRedraw',
    sector: 'clinics',
    ackMinutes: 30,
    dueMinutes: 240,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'SAMPLE_STAGE_BREACH',
    labelKey: 'NLSOpsRequestKindSampleStageBreach',
    sector: 'clinics',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'HOME_COLLECTION',
    labelKey: 'NLSOpsRequestKindHomeCollection',
    sector: 'clinics',
    ackMinutes: 30,
    dueMinutes: 240,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: true,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'NARCOTIC_MISMATCH',
    labelKey: 'NLSOpsRequestKindNarcoticMismatch',
    sector: 'clinics',
    ackMinutes: 15,
    dueMinutes: 120,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    restricted: true,
    viewerRoleKeys: ['COMPLIANCE_MANAGER'],
    patientLinked: false,
  },
  {
    key: 'JOB_ORDER_OVERDUE',
    labelKey: 'NLSOpsRequestKindJobOrderOverdue',
    sector: 'clinics',
    ackMinutes: 30,
    dueMinutes: 240,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'DIALYZER_REUSE_FAIL',
    labelKey: 'NLSOpsRequestKindDialyzerReuseFail',
    sector: 'clinics',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    restricted: true,
    viewerRoleKeys: ['COMPLIANCE_MANAGER'],
    patientLinked: false,
  },
  {
    key: 'IVF_WITNESS_MISMATCH',
    labelKey: 'NLSOpsRequestKindIvfWitnessMismatch',
    sector: 'clinics',
    ackMinutes: 15,
    dueMinutes: 60,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    restricted: true,
    viewerRoleKeys: ['COMPLIANCE_MANAGER'],
    patientLinked: false,
  },
  {
    key: 'AUTOCLAVE_FAIL',
    labelKey: 'NLSOpsRequestKindAutoclaveFail',
    sector: 'clinics',
    ackMinutes: 15,
    dueMinutes: 120,
    escalateEveryMinutes: 30,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
  {
    key: 'TELE_DELIVERY',
    labelKey: 'NLSOpsRequestKindTeleDelivery',
    sector: 'clinics',
    ackMinutes: 30,
    dueMinutes: 240,
    escalateEveryMinutes: 60,
    verifyRequired: true,
    restricted: false,
    viewerRoleKeys: [],
    patientLinked: false,
  },
] as const

export function getBuiltInRequestKinds(
  sector?: OpsRequestSector,
): OpsRequestKind[] {
  if (!sector) return [...OPS_REQUEST_BUILTIN_KINDS]
  return OPS_REQUEST_BUILTIN_KINDS.filter((k) => k.sector === sector).map((k) => ({
    ...k,
    viewerRoleKeys: [...k.viewerRoleKeys],
  }))
}

export function getBuiltInRequestKind(
  key: string,
  sector?: OpsRequestSector,
): OpsRequestKind | undefined {
  const match = OPS_REQUEST_BUILTIN_KINDS.find(
    (k) => k.key === key && (!sector || k.sector === sector),
  )
  if (!match) return undefined
  return {
    ...match,
    viewerRoleKeys: [...match.viewerRoleKeys],
  }
}

export function validateRequestKindKey(key: unknown, isCustom?: boolean): string {
  if (typeof key !== 'string') {
    throw new Error('Request kind key must be a string.')
  }
  const cleanKey = key.trim()
  if (!cleanKey) {
    throw new Error('Request kind key cannot be empty.')
  }
  if (isCustom || cleanKey.startsWith('custom_')) {
    if (!CUSTOM_REQUEST_KIND_KEY_REGEX.test(cleanKey)) {
      throw new Error(
        `Custom request kind key "${cleanKey}" must match pattern custom_[a-z0-9_]{1,32}.`,
      )
    }
    return cleanKey
  }
  return cleanKey
}

export function validateRequestKindLabel(label: unknown): string {
  if (typeof label !== 'string') {
    throw new Error('Request kind label must be a string.')
  }
  const cleanLabel = label.trim()
  if (
    cleanLabel.length < MIN_REQUEST_LABEL_LENGTH ||
    cleanLabel.length > MAX_REQUEST_LABEL_LENGTH
  ) {
    throw new Error(
      `Request kind label must be between ${MIN_REQUEST_LABEL_LENGTH} and ${MAX_REQUEST_LABEL_LENGTH} characters.`,
    )
  }
  return cleanLabel
}

export function validateMinutesRanges(
  ackMinutes?: number,
  dueMinutes?: number,
  escalateEveryMinutes?: number,
): void {
  if (ackMinutes !== undefined) {
    if (
      !Number.isInteger(ackMinutes) ||
      ackMinutes < MIN_ACK_MINUTES ||
      ackMinutes > MAX_ACK_MINUTES
    ) {
      throw new Error(
        `ackMinutes must be an integer between ${MIN_ACK_MINUTES} and ${MAX_ACK_MINUTES}.`,
      )
    }
  }
  if (dueMinutes !== undefined) {
    if (
      !Number.isInteger(dueMinutes) ||
      dueMinutes < MIN_DUE_MINUTES ||
      dueMinutes > MAX_DUE_MINUTES
    ) {
      throw new Error(
        `dueMinutes must be an integer between ${MIN_DUE_MINUTES} and ${MAX_DUE_MINUTES}.`,
      )
    }
  }
  if (escalateEveryMinutes !== undefined) {
    if (
      !Number.isInteger(escalateEveryMinutes) ||
      escalateEveryMinutes < MIN_ESCALATE_EVERY_MINUTES ||
      escalateEveryMinutes > MAX_ESCALATE_EVERY_MINUTES
    ) {
      throw new Error(
        `escalateEveryMinutes must be an integer between ${MIN_ESCALATE_EVERY_MINUTES} and ${MAX_ESCALATE_EVERY_MINUTES}.`,
      )
    }
  }
}

export function mergeRequestKind(
  builtIn: OpsRequestKind | undefined,
  override: OpsRequestKindOverride | undefined,
): OpsRequestKind {
  if (!builtIn && !override) {
    throw new Error('Cannot merge when both builtIn and override are undefined.')
  }
  if (!builtIn && override) {
    const isCustom = override.key.startsWith('custom_')
    validateRequestKindKey(override.key, isCustom)
    const label = override.label ? validateRequestKindLabel(override.label) : override.key
    validateMinutesRanges(
      override.ackMinutes,
      override.dueMinutes,
      override.escalateEveryMinutes,
    )
    if (override.patientLinked && override.photoEnabled) {
      throw new Error('A patientLinked request kind cannot be photo-enabled.')
    }
    if (
      override.verifierRoleKey !== undefined &&
      override.verifierRoleKey !== null &&
      override.verifierRoleKey !== ''
    ) {
      if (!OPS_SECTOR_ROLES.includes(override.verifierRoleKey as OpsSectorRole)) {
        throw new Error(
          `verifierRoleKey "${override.verifierRoleKey}" must be one of OPS_SECTOR_ROLES.`,
        )
      }
    }
    if (override.viewerRoleKeys !== undefined) {
      if (!Array.isArray(override.viewerRoleKeys)) {
        throw new Error('viewerRoleKeys must be an array.')
      }
      for (const k of override.viewerRoleKeys) {
        if (!OPS_SECTOR_ROLES.includes(k as OpsSectorRole)) {
          throw new Error(`viewerRoleKey "${k}" must be one of OPS_SECTOR_ROLES.`)
        }
      }
    }
    const customResult: OpsRequestKind = {
      key: override.key,
      labelKey: '',
      label,
      sector: override.sector,
      ackMinutes: override.ackMinutes ?? 60,
      dueMinutes: override.dueMinutes ?? 480,
      escalateEveryMinutes: override.escalateEveryMinutes ?? 60,
      verifyRequired: override.verifyRequired ?? false,
      restricted: override.restricted ?? false,
      viewerRoleKeys: override.viewerRoleKeys
        ? [...override.viewerRoleKeys]
        : override.restricted
          ? ['INCIDENT_HANDLER']
          : [],
      patientLinked: override.patientLinked ?? false,
      photoEnabled: override.photoEnabled ?? (override.patientLinked ? false : true),
      assetRequired: override.assetRequired ?? false,
      donePhotoRequired: override.donePhotoRequired ?? false,
      archived: override.archived ?? false,
      ...(override.dutyRoleKey ? { dutyRoleKey: override.dutyRoleKey } : {}),
      ...(override.verifierRoleKey ? { verifierRoleKey: override.verifierRoleKey } : {}),
    }
    return customResult
  }
  if (builtIn && !override) {
    return {
      ...builtIn,
      viewerRoleKeys: [...builtIn.viewerRoleKeys],
    }
  }

  if (!builtIn) {
    throw new Error('Built-in kind or override must be provided.')
  }
  const o = override as OpsRequestKindOverride
  if (o.restricted === false && builtIn.restricted) {
    throw new Error('A built-in restricted kind cannot be made unrestricted.')
  }
  if ((o.patientLinked === false || o.photoEnabled === true) && builtIn.patientLinked) {
    throw new Error('A built-in patientLinked kind cannot be photo-enabled or unlinked.')
  }
  if (o.patientLinked && o.photoEnabled) {
    throw new Error('A patientLinked request kind cannot be photo-enabled.')
  }
  if (o.donePhotoRequired === false && builtIn.donePhotoRequired) {
    throw new Error('A built-in donePhotoRequired kind cannot be turned off.')
  }
  if (
    o.verifierRoleKey !== undefined &&
    o.verifierRoleKey !== null &&
    o.verifierRoleKey !== ''
  ) {
    if (!OPS_SECTOR_ROLES.includes(o.verifierRoleKey as OpsSectorRole)) {
      throw new Error(`verifierRoleKey "${o.verifierRoleKey}" must be one of OPS_SECTOR_ROLES.`)
    }
  }
  if (o.viewerRoleKeys !== undefined) {
    if (!Array.isArray(o.viewerRoleKeys)) {
      throw new Error('viewerRoleKeys must be an array.')
    }
    for (const k of o.viewerRoleKeys) {
      if (!OPS_SECTOR_ROLES.includes(k as OpsSectorRole)) {
        throw new Error(`viewerRoleKey "${k}" must be one of OPS_SECTOR_ROLES.`)
      }
    }
  }

  validateMinutesRanges(o.ackMinutes, o.dueMinutes, o.escalateEveryMinutes)
  const label = o.label !== undefined ? validateRequestKindLabel(o.label) : builtIn.label

  const dutyRoleKey = o.dutyRoleKey !== undefined ? o.dutyRoleKey : builtIn.dutyRoleKey
  const verifierRoleKey = o.verifierRoleKey !== undefined ? o.verifierRoleKey : builtIn.verifierRoleKey

  return {
    ...builtIn,
    label,
    ackMinutes: o.ackMinutes ?? builtIn.ackMinutes,
    dueMinutes: o.dueMinutes ?? builtIn.dueMinutes,
    escalateEveryMinutes: o.escalateEveryMinutes ?? builtIn.escalateEveryMinutes,
    ...(dutyRoleKey ? { dutyRoleKey } : {}),
    verifyRequired: o.verifyRequired ?? builtIn.verifyRequired,
    ...(verifierRoleKey ? { verifierRoleKey } : {}),
    restricted: builtIn.restricted ? true : (o.restricted ?? builtIn.restricted),
    viewerRoleKeys: o.viewerRoleKeys
      ? [...o.viewerRoleKeys]
      : [...builtIn.viewerRoleKeys],
    patientLinked: builtIn.patientLinked ? true : (o.patientLinked ?? builtIn.patientLinked),
    photoEnabled: (builtIn.patientLinked || o.patientLinked)
      ? false
      : (o.photoEnabled ?? builtIn.photoEnabled),
    assetRequired: o.assetRequired ?? builtIn.assetRequired ?? false,
    donePhotoRequired: builtIn.donePhotoRequired ? true : (o.donePhotoRequired ?? builtIn.donePhotoRequired ?? false),
    ...(builtIn.isolateAssetOnRaise !== undefined ? { isolateAssetOnRaise: builtIn.isolateAssetOnRaise } : {}),
    ...(builtIn.strictDifferentPerson !== undefined ? { strictDifferentPerson: builtIn.strictDifferentPerson } : {}),
    ...(builtIn.verifierNotRaiser !== undefined ? { verifierNotRaiser: builtIn.verifierNotRaiser } : {}),
    archived: o.archived ?? builtIn.archived ?? false,
  }
}

export function validateRequestTitle(title: unknown): string {
  if (typeof title !== 'string') {
    throw new Error('Request title must be a string.')
  }
  const cleanTitle = title.trim()
  if (
    cleanTitle.length < MIN_REQUEST_TITLE_LENGTH ||
    cleanTitle.length > MAX_REQUEST_TITLE_LENGTH
  ) {
    throw new Error(
      `Request title must be between ${MIN_REQUEST_TITLE_LENGTH} and ${MAX_REQUEST_TITLE_LENGTH} characters.`,
    )
  }
  return cleanTitle
}

export function validateRequestDetails(details: unknown): string | undefined {
  if (details === undefined || details === null || details === '') {
    return undefined
  }
  if (typeof details !== 'string') {
    throw new Error('Request details must be a string.')
  }
  const cleanDetails = details.trim()
  if (cleanDetails.length > MAX_REQUEST_DETAILS_LENGTH) {
    throw new Error(
      `Request details cannot exceed ${MAX_REQUEST_DETAILS_LENGTH} characters.`,
    )
  }
  return cleanDetails
}

export function validateRequestReason(
  reason: unknown,
  fieldName = 'Reason',
): string {
  if (typeof reason !== 'string') {
    throw new Error(`${fieldName} must be a string.`)
  }
  const cleanReason = reason.trim()
  if (
    cleanReason.length < MIN_REQUEST_REASON_LENGTH ||
    cleanReason.length > MAX_REQUEST_REASON_LENGTH
  ) {
    throw new Error(
      `${fieldName} must be between ${MIN_REQUEST_REASON_LENGTH} and ${MAX_REQUEST_REASON_LENGTH} characters.`,
    )
  }
  return cleanReason
}

export function validateRequestPhotos(
  photos: unknown,
  options?: { patientLinked?: boolean; photoEnabled?: boolean },
): string[] {
  if (photos === undefined || photos === null) {
    return []
  }
  if (!Array.isArray(photos)) {
    throw new Error('Request photos must be an array.')
  }
  if (photos.length > 0 && options?.patientLinked) {
    throw new Error('Photos are not permitted on patientLinked requests.')
  }
  if (photos.length > 0 && options?.photoEnabled === false) {
    throw new Error('Photos are disabled for this request kind.')
  }
  if (photos.length > MAX_REQUEST_PHOTOS) {
    throw new Error(`A maximum of ${MAX_REQUEST_PHOTOS} photos can be attached.`)
  }
  const result: string[] = []
  for (const p of photos) {
    if (typeof p !== 'string' || !p.trim()) {
      throw new Error('Each photo path must be a non-empty string.')
    }
    result.push(p.trim())
  }
  return result
}

export interface UpsertRequestKindInput {
  workspaceId: string
  kind: OpsRequestKindOverride
  opId?: string
}

export interface ListRequestKindsInput {
  workspaceId: string
  sector?: OpsRequestSector
  includeArchived?: boolean
}

export interface RaiseRequestInput {
  workspaceId: string
  sector: OpsRequestSector
  kind: string
  placeId: string
  assetId?: string
  recordId?: string
  title: string
  titleKey?: string
  titleParams?: Record<string, string>
  details?: string
  photos?: string[]
  raisedFor?: OpsRequestRaisedFor
  requestId?: string
  schedule?: JobScheduleInput | null
  downtimeReason?: PlantDowntimeReason
  downtimeNote?: string
  holdItem?: string
  quantity?: number
  unit?: string
  reason?: string
  whatNearlyHappened?: string
  category?: PlantNearMissCategory
  immediateAction?: string
  opId?: string
}

export interface AttachRequestPhotosInput {
  workspaceId: string
  requestId: string
  photos: string[]
  opId?: string
}

export interface AcknowledgeRequestInput {
  workspaceId: string
  requestId: string
  opId?: string
}

export interface StartRequestInput {
  workspaceId: string
  requestId: string
  geo?: OpsJobGeo | null
  geoStatus?: OpsJobGeoStatus
  opId?: string
}

export interface AssignRequestInput {
  workspaceId: string
  requestId: string
  assigneeUid: string
  opId?: string
}

export interface MarkRequestDoneInput {
  workspaceId: string
  requestId: string
  photos?: string[]
  geo?: OpsJobGeo | null
  geoStatus?: OpsJobGeoStatus
  opId?: string
}

export interface VerifyRequestInput {
  workspaceId: string
  requestId: string
  decision: 'APPROVE' | 'REJECT'
  reason?: string
  selfApprovalReason?: string
  opId?: string
}

export interface ReopenRequestInput {
  workspaceId: string
  requestId: string
  reason: string
  opId?: string
}

export interface CancelRequestInput {
  workspaceId: string
  requestId: string
  reason: string
  opId?: string
}

export interface JobScheduleInput {
  date: string
  windowStart?: string | null
  windowEnd?: string | null
}

export interface ScheduleJobInput {
  workspaceId: string
  requestId: string
  schedule: JobScheduleInput | null
  assigneeUid?: string
  opId?: string
}

export interface ScheduleJobResult {
  requestId: string
  jobDate: string | null
  windowStart: string | null
  windowEnd: string | null
  ackDueAt: number
  dueAt: number
  nextEscalationAt: number
  assigneeUid?: string
  replayed?: boolean
}

export interface RaiseRevisitInput {
  workspaceId: string
  requestId: string
  reason: string
  schedule?: JobScheduleInput | null
  opId?: string
}

export interface RaiseRevisitResult {
  requestId: string
  sourceRequestId: string
  state: OpsRequestState
  replayed?: boolean
}

export interface ListJobBoardInput {
  workspaceId: string
  date: string
}

export interface JobBoardRow {
  id: string
  kindKey: string
  kindLabelKey: string
  title: string
  state: OpsRequestState
  jobDate?: string | null
  windowStart?: string | null
  windowEnd?: string | null
  assigneeUid?: string
  assigneeDisplayName?: string
  raisedByUid: string
  placeId: string
  customerId: string
  revisitOf?: string
  revisitIds?: string[]
  createdAt: number
  dueAt: number
  siteName: string
  locality?: string | null
  city?: string | null
  serviceAreaName?: string | null
  geo?: { lat: number; lng: number } | null
  customerDisplayName: string
}

export interface ListJobBoardResult {
  scheduled: JobBoardRow[]
  unscheduled: JobBoardRow[]
}

export interface ReopenRequestInput {
  workspaceId: string
  requestId: string
  reason: string
  opId?: string
}

export interface CancelRequestInput {
  workspaceId: string
  requestId: string
  reason: string
  opId?: string
}

// ---------------------------------------------------------------------------
// Relief Candidates Helper (R39 G2.b / SO-P104)
// Mirrored from functions-common/src/ops/rosters.ts
// ---------------------------------------------------------------------------

export function personRefKey(ref: OpsPersonRef): string {
  if (ref.kind === 'MEMBER') {
    return `m_${ref.uid}`
  }
  return `w_${ref.workerId}`
}

export function isSamePersonRef(a: OpsPersonRef, b: OpsPersonRef): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'MEMBER' && b.kind === 'MEMBER') return a.uid === b.uid
  if (a.kind === 'WORKER' && b.kind === 'WORKER') return a.workerId === b.workerId
  return false
}

export interface OpsReliefCandidatePerson {
  personRef: OpsPersonRef
  name: string
  role?: string
  active?: boolean
}

export interface ListReliefCandidatesOptions {
  candidates: readonly OpsReliefCandidatePerson[]
  dayKey: string
  shiftId: string
  assignments?: readonly OpsRosterAssignment[]
  approvedLeaves?: readonly OpsRosterChange[]
  absentPersonRef?: OpsPersonRef
}

export interface OpsReliefCandidate {
  personRef: OpsPersonRef
  name: string
  isOffToday: boolean
  role?: string
}

/**
 * Pure helper to list eligible candidates for relief assignment.
 * R39 G2.b / SO-P104.
 *
 * Filters candidates:
 * - Excludes inactive candidates (active === false).
 * - Excludes the absent person being relieved (absentPersonRef).
 * - Enforces role ceiling: excludes members holding VIEWER or READONLY workspace roles.
 * - Excludes candidates on approved LEAVE covering the target dayKey.
 * - Excludes candidates already double-booked on that shift (same dayKey and shiftId).
 *
 * Sorts eligible candidates:
 * - Free / off-today candidates first (has no assigned shifts on target dayKey).
 * - Then alphabetically by candidate name.
 */
export function listReliefCandidates(
  options: ListReliefCandidatesOptions,
): OpsReliefCandidate[] {
  const {
    candidates,
    dayKey,
    shiftId,
    assignments = [],
    approvedLeaves = [],
    absentPersonRef,
  } = options

  const results: OpsReliefCandidate[] = []
  const seenPersonKeys = new Set<string>()

  for (const candidate of candidates) {
    if (candidate.active === false) {
      continue
    }

    const key = personRefKey(candidate.personRef)
    if (seenPersonKeys.has(key)) {
      continue
    }

    // Role ceiling: VIEWER or READONLY cannot be assigned relief
    if (candidate.role === 'VIEWER' || candidate.role === 'READONLY') {
      continue
    }

    // Exclude the absent person being replaced
    if (absentPersonRef && isSamePersonRef(candidate.personRef, absentPersonRef)) {
      continue
    }

    // Exclude candidate on approved leave on target dayKey
    const onLeave = approvedLeaves.some((l) => {
      if (l.type && l.type !== 'LEAVE') return false
      if (l.state && l.state !== 'APPROVED') return false
      const leavePerson =
        l.requesterPersonRef ||
        (l.requesterUid ? { kind: 'MEMBER' as const, uid: l.requesterUid } : null)
      if (!leavePerson || !isSamePersonRef(leavePerson, candidate.personRef)) {
        return false
      }
      const start = l.dayKey || ''
      const end = l.endDayKey || l.dayKey || ''
      return dayKey >= start && dayKey <= end
    })

    if (onLeave) {
      continue
    }

    // Check double-booking on this exact shift-day
    const isDoubleBooked = assignments.some(
      (a) =>
        a.dayKey === dayKey &&
        a.shiftId === shiftId &&
        isSamePersonRef(a.personRef, candidate.personRef),
    )

    if (isDoubleBooked) {
      continue
    }

    // Check whether candidate has any other shift on this dayKey
    const hasShiftToday = assignments.some(
      (a) => a.dayKey === dayKey && isSamePersonRef(a.personRef, candidate.personRef),
    )

    seenPersonKeys.add(key)
    results.push({
      personRef: candidate.personRef,
      name: candidate.name,
      isOffToday: !hasShiftToday,
      ...(candidate.role ? { role: candidate.role } : {}),
    })
  }

  // Sort: off-today first, then alphabetically by name
  results.sort((a, b) => {
    if (a.isOffToday !== b.isOffToday) {
      return a.isOffToday ? -1 : 1
    }
    const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    if (cmp !== 0) return cmp
    return personRefKey(a.personRef).localeCompare(personRefKey(b.personRef))
  })

  return results
}

export function daysBetweenKeys(startKey: string, endKey: string): number {
  const [y1, m1, d1] = startKey.split('-').map(Number)
  const [y2, m2, d2] = endKey.split('-').map(Number)
  const t1 = Date.UTC(y1, m1 - 1, d1)
  const t2 = Date.UTC(y2, m2 - 1, d2)
  return Math.round((t2 - t1) / 86400000)
}

export function enumerateDayKeys(startKey: string, endKey: string): string[] {
  const count = daysBetweenKeys(startKey, endKey)
  if (count < 0) return []
  const res: string[] = []
  for (let i = 0; i <= count; i++) {
    res.push(addDaysToKey(startKey, i))
  }
  return res
}

export interface ComputeRosterWarningsOptions {
  assignments: OpsRosterAssignment[]
  shifts: OpsShift[]
  coverageRules?: OpsCoverageRule[]
  workerSkills?: Record<string, string[]>
  approvedLeaves?: Array<{ personRef: OpsPersonRef; dayKey: string }>
  otherPublishedRosters?: Array<{
    rosterId: string
    assignments: OpsRosterAssignment[]
    periodStart: string
    periodEnd: string
  }>
  timeZone?: string
  period?: { periodStart: string; periodEnd: string }
}

function getZonedMinuteEpoch(
  dayKey: string,
  minute: number,
  timeZone: string,
): number {
  const [y, m, d] = dayKey.split('-').map(Number)
  const hour = Math.floor(minute / 60)
  const min = minute % 60
  return zonedTimeToUtc(y, m, d, hour, min, 0, 0, timeZone).getTime()
}

/**
 * Mirror of functions-common computeRosterWarnings.
 * R39 A6 / SO-P104.
 */
export function computeRosterWarnings(
  options: ComputeRosterWarningsOptions,
): OpsRosterWarning[] {
  const warnings: OpsRosterWarning[] = []
  const tz = resolveWorkspaceTimeZone(options.timeZone)
  const shiftMap = new Map<string, OpsShift>()
  for (const s of options.shifts) {
    shiftMap.set(s.id, s)
  }

  // Calculate intervals for assignments
  interface AssignmentInterval {
    assignment: OpsRosterAssignment
    startMs: number
    endMs: number
  }

  const intervals: AssignmentInterval[] = []
  for (const a of options.assignments) {
    const shift = shiftMap.get(a.shiftId)
    if (!shift) continue
    const startMs = getZonedMinuteEpoch(a.dayKey, shift.startMinute, tz)
    const endMinute =
      typeof shift.endMinute === 'number'
        ? shift.endMinute
        : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
    const endDayKey =
      endMinute <= shift.startMinute
        ? addDaysToKey(a.dayKey, 1)
        : a.dayKey
    const endMs = getZonedMinuteEpoch(endDayKey, endMinute, tz)
    intervals.push({ assignment: a, startMs, endMs })
  }

  // 1. OVERLAP: Same person, overlapping shift instants (across midnight and days)
  const byPerson = new Map<string, AssignmentInterval[]>()
  for (const item of intervals) {
    const key = personRefKey(item.assignment.personRef)
    const list = byPerson.get(key) || []
    list.push(item)
    byPerson.set(key, list)
  }

  for (const personIntervals of byPerson.values()) {
    for (let i = 0; i < personIntervals.length; i++) {
      for (let j = i + 1; j < personIntervals.length; j++) {
        const a1 = personIntervals[i]
        const a2 = personIntervals[j]
        // Overlap: A.start < B.end && B.start < A.end
        if (a1.startMs < a2.endMs && a2.startMs < a1.endMs) {
          warnings.push({
            code: 'OVERLAP',
            message: `Person is scheduled for overlapping shifts on ${a1.assignment.dayKey} and ${a2.assignment.dayKey}.`,
            personRef: a1.assignment.personRef,
            dayKey: a1.assignment.dayKey,
            shiftId: a1.assignment.shiftId,
            conflictingDayKey: a2.assignment.dayKey,
            conflictingShiftId: a2.assignment.shiftId,
          })
        }
      }
    }
  }

  // 2. DOUBLE_BOOKED: Same person in another published roster of the same workspace at the same time
  if (options.otherPublishedRosters && options.otherPublishedRosters.length > 0) {
    const otherIntervals: Array<AssignmentInterval & { rosterId: string }> = []
    for (const other of options.otherPublishedRosters) {
      for (const oa of other.assignments) {
        const shift = shiftMap.get(oa.shiftId)
        if (!shift) continue
        const startMs = getZonedMinuteEpoch(oa.dayKey, shift.startMinute, tz)
        const endMinute =
          typeof shift.endMinute === 'number'
            ? shift.endMinute
            : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
        const endDayKey =
          endMinute <= shift.startMinute
            ? addDaysToKey(oa.dayKey, 1)
            : oa.dayKey
        const endMs = getZonedMinuteEpoch(endDayKey, endMinute, tz)
        otherIntervals.push({
          assignment: oa,
          startMs,
          endMs,
          rosterId: other.rosterId,
        })
      }
    }

    for (const item of intervals) {
      const pKey = personRefKey(item.assignment.personRef)
      for (const other of otherIntervals) {
        if (personRefKey(other.assignment.personRef) === pKey) {
          if (item.startMs < other.endMs && other.startMs < item.endMs) {
            warnings.push({
              code: 'DOUBLE_BOOKED',
              message: `Person is double-booked with another published roster (${other.rosterId}) on ${other.assignment.dayKey}.`,
              personRef: item.assignment.personRef,
              dayKey: item.assignment.dayKey,
              shiftId: item.assignment.shiftId,
              otherRosterId: other.rosterId,
              conflictingDayKey: other.assignment.dayKey,
              conflictingShiftId: other.assignment.shiftId,
            })
          }
        }
      }
    }
  }

  // 3. MISSING_COVER: Fewer than minCount
  if (options.coverageRules && options.period) {
    const days = enumerateDayKeys(
      options.period.periodStart,
      options.period.periodEnd,
    )
    for (const rule of options.coverageRules) {
      if (rule.archived) continue
      for (const d of days) {
        const count = options.assignments.filter(
          (a) =>
            a.dayKey === d &&
            a.shiftId === rule.shiftId &&
            a.dutyRoleKey === rule.dutyRoleKey &&
            a.kind === 'SHIFT',
        ).length

        if (count < rule.minCount) {
          warnings.push({
            code: 'MISSING_COVER',
            message: `Shift on ${d} has insufficient coverage for duty role "${rule.dutyRoleKey}": ${count}/${rule.minCount}.`,
            placeId: rule.placeId,
            shiftId: rule.shiftId,
            dutyRoleKey: rule.dutyRoleKey,
            dayKey: d,
            minCount: rule.minCount,
            actualCount: count,
          })
        }
      }
    }
  }

  // 4. SKILL_MISSING: Assigned person lacks requiredSkill
  if (options.coverageRules && options.workerSkills) {
    for (const rule of options.coverageRules) {
      if (rule.archived || !rule.requiredSkill) continue
      const reqSkill = rule.requiredSkill.trim()
      const matchingAssignments = options.assignments.filter(
        (a) => a.shiftId === rule.shiftId && a.dutyRoleKey === rule.dutyRoleKey,
      )

      for (const ma of matchingAssignments) {
        const pKey = personRefKey(ma.personRef)
        const skills = options.workerSkills[pKey] || []
        if (!skills.includes(reqSkill)) {
          warnings.push({
            code: 'SKILL_MISSING',
            message: `Person assigned to shift on ${ma.dayKey} lacks required skill "${reqSkill}".`,
            placeId: rule.placeId,
            shiftId: rule.shiftId,
            dutyRoleKey: rule.dutyRoleKey,
            dayKey: ma.dayKey,
            personRef: ma.personRef,
            requiredSkill: reqSkill,
          })
        }
      }
    }
  }

  // 5. ON_LEAVE: An approved leave covers the assignment
  if (options.approvedLeaves && options.approvedLeaves.length > 0) {
    for (const a of options.assignments) {
      const isLeave = options.approvedLeaves.some(
        (l) => isSamePersonRef(l.personRef, a.personRef) && l.dayKey === a.dayKey,
      )
      if (isLeave) {
        warnings.push({
          code: 'ON_LEAVE',
          message: `Person is scheduled for shift on ${a.dayKey} while on approved leave.`,
          personRef: a.personRef,
          dayKey: a.dayKey,
          shiftId: a.shiftId,
        })
      }
    }
  }

  return warnings
}



/* =======================================================================
   A9 Compliance Calendar Types & Validators (SO-P66)
   ======================================================================= */

export const OPS_COMPLIANCE_CATEGORIES = [
  'LICENCE',
  'CERTIFICATE',
  'REGISTRATION',
  'INSPECTION',
  'INSURANCE',
  'OTHER',
] as const

export type OpsComplianceCategory = (typeof OPS_COMPLIANCE_CATEGORIES)[number]

export const OPS_COMPLIANCE_STATUSES = [
  'VALID',
  'DUE_SOON',
  'EXPIRED',
  'ARCHIVED',
] as const

export type OpsComplianceStatus = (typeof OPS_COMPLIANCE_STATUSES)[number]

export const OPS_COMPLIANCE_HISTORY_TYPES = [
  'CREATED',
  'UPDATED',
  'RENEWED',
  'EVIDENCE_ADDED',
  'REMINDER',
  'ESCALATED',
  'ARCHIVED',
] as const

export type OpsComplianceHistoryType = (typeof OPS_COMPLIANCE_HISTORY_TYPES)[number]

export const MIN_COMPLIANCE_TITLE_LENGTH = 1
export const MAX_COMPLIANCE_TITLE_LENGTH = 120
export const MAX_COMPLIANCE_AUTHORITY_LENGTH = 120
export const MAX_COMPLIANCE_REFERENCE_LENGTH = 80
export const MAX_COMPLIANCE_NOTES_LENGTH = 2000
export const DEFAULT_COMPLIANCE_REMINDER_DAYS = [60, 30, 7, 0] as const
export const MIN_COMPLIANCE_REMINDER_DAYS_COUNT = 1
export const MAX_COMPLIANCE_REMINDER_DAYS_COUNT = 6
export const MIN_COMPLIANCE_REMINDER_DAY = 0
export const MAX_COMPLIANCE_REMINDER_DAY = 365
export const MAX_COMPLIANCE_EVIDENCE_COUNT = 10
export const MIN_COMPLIANCE_ARCHIVE_REASON_LENGTH = 3
export const MAX_COMPLIANCE_ARCHIVE_REASON_LENGTH = 500

export interface OpsComplianceItem {
  id: string
  workspaceId: string
  sector: OpsSector
  title: string
  category: OpsComplianceCategory
  authority?: string
  reference?: string
  placeId?: string | null
  personRef?: OpsPersonRef | null
  legalRoleKey?: string | null
  validFrom?: string | null
  validTo: string
  reminderDays: number[]
  ownerUid: string
  backupUid?: string | null
  notes?: string
  evidence: string[]
  status: OpsComplianceStatus
  remindersDone: number[]
  escalationLevel: number
  readAccess?: string[] | null
  archived: boolean
  archiveReason?: string | null
  archivedAt?: number | null
  archivedByUid?: string | null
  version: number
  tickSettled?: boolean
  starterKey?: string | null
  createdAt: number
  createdByUid: string
  updatedAt: number
  updatedByUid: string
}

export interface OpsComplianceHistoryEntry {
  id: string
  workspaceId: string
  itemId: string
  type: OpsComplianceHistoryType
  byUid: string
  at: number
  details?: Record<string, unknown>
}

export function validateDayKey(raw: unknown, label = 'dayKey'): string {
  if (typeof raw !== 'string') {
    throw new Error(`${label} must be a YYYY-MM-DD string`)
  }
  const str = raw.trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    throw new Error(`${label} must be a YYYY-MM-DD string: got "${raw}"`)
  }
  const [y, m, d] = str.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) {
    throw new Error(`${label} is not a valid calendar date: "${raw}"`)
  }
  return str
}

export const cleanAssetDayKey = validateDayKey

export function dayKeyDiffDays(fromDayKey: string, toDayKey: string): number {
  validateDayKey(fromDayKey, 'fromDayKey')
  validateDayKey(toDayKey, 'toDayKey')
  const [y1, m1, d1] = fromDayKey.split('-').map(Number)
  const [y2, m2, d2] = toDayKey.split('-').map(Number)
  const fromMs = Date.UTC(y1, m1 - 1, d1)
  const toMs = Date.UTC(y2, m2 - 1, d2)
  return Math.round((toMs - fromMs) / 86400000)
}

export function addDaysToKey(dayKey: string, days: number): string {
  validateDayKey(dayKey, 'dayKey')
  const [y, m, d] = dayKey.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return dt.toISOString().slice(0, 10)
}

export function resolveWorkspaceDocTimeZone(
  wsData?: { timezone?: unknown; timeZone?: unknown } | null,
): string {
  const raw = wsData?.timezone ?? wsData?.timeZone
  return resolveWorkspaceTimeZone(raw)
}

export function computeComplianceStatus(
  item: { validTo: string; reminderDays?: number[]; archived?: boolean },
  todayKey: string,
): OpsComplianceStatus {
  if (item.archived === true) {
    return 'ARCHIVED'
  }
  const cleanValidTo = validateDayKey(item.validTo, 'validTo')
  const cleanToday = validateDayKey(todayKey, 'todayKey')

  if (cleanToday > cleanValidTo) {
    return 'EXPIRED'
  }

  const reminderDays =
    Array.isArray(item.reminderDays) && item.reminderDays.length > 0
      ? item.reminderDays
      : DEFAULT_COMPLIANCE_REMINDER_DAYS
  const largestReminder = Math.max(...reminderDays)
  const daysDiff = dayKeyDiffDays(cleanToday, cleanValidTo)

  if (daysDiff <= largestReminder) {
    return 'DUE_SOON'
  }
  return 'VALID'
}

export function validateComplianceTitle(title: unknown): string {
  if (typeof title !== 'string') {
    throw new Error('Title must be a non-empty string.')
  }
  const trimmed = title.trim()
  if (trimmed.length < MIN_COMPLIANCE_TITLE_LENGTH) {
    throw new Error('Title must be at least 1 character.')
  }
  if (trimmed.length > MAX_COMPLIANCE_TITLE_LENGTH) {
    throw new Error(`Title must be at most ${MAX_COMPLIANCE_TITLE_LENGTH} characters.`)
  }
  return trimmed
}

export function validateComplianceCategory(category: unknown): OpsComplianceCategory {
  if (typeof category !== 'string') {
    throw new Error('Category must be a valid compliance category.')
  }
  const trimmed = category.trim() as OpsComplianceCategory
  if (!OPS_COMPLIANCE_CATEGORIES.includes(trimmed)) {
    throw new Error(
      `Invalid category "${category}". Must be one of: ${OPS_COMPLIANCE_CATEGORIES.join(', ')}.`,
    )
  }
  return trimmed
}

export function validateComplianceAuthority(authority: unknown): string {
  if (authority === undefined || authority === null) {
    return ''
  }
  if (typeof authority !== 'string') {
    throw new Error('Authority must be a string.')
  }
  const trimmed = authority.trim()
  if (trimmed.length > MAX_COMPLIANCE_AUTHORITY_LENGTH) {
    throw new Error(`Authority must be at most ${MAX_COMPLIANCE_AUTHORITY_LENGTH} characters.`)
  }
  return trimmed
}

export function validateComplianceReference(reference: unknown): string {
  if (reference === undefined || reference === null) {
    return ''
  }
  if (typeof reference !== 'string') {
    throw new Error('Reference must be a string.')
  }
  const trimmed = reference.trim()
  if (trimmed.length > MAX_COMPLIANCE_REFERENCE_LENGTH) {
    throw new Error(`Reference must be at most ${MAX_COMPLIANCE_REFERENCE_LENGTH} characters.`)
  }
  return trimmed
}

export function validateComplianceNotes(notes: unknown): string {
  if (notes === undefined || notes === null) {
    return ''
  }
  if (typeof notes !== 'string') {
    throw new Error('Notes must be a string.')
  }
  const trimmed = notes.trim()
  if (trimmed.length > MAX_COMPLIANCE_NOTES_LENGTH) {
    throw new Error(`Notes must be at most ${MAX_COMPLIANCE_NOTES_LENGTH} characters.`)
  }
  return trimmed
}

export function validateComplianceDates(
  validFrom: unknown,
  validTo: unknown,
): { validFrom?: string; validTo: string } {
  const cleanValidTo = validateDayKey(validTo, 'validTo')
  let cleanValidFrom: string | undefined

  if (validFrom !== undefined && validFrom !== null && validFrom !== '') {
    cleanValidFrom = validateDayKey(validFrom, 'validFrom')
    if (cleanValidTo <= cleanValidFrom) {
      throw new Error(
        `validTo must be strictly after validFrom: validFrom=${cleanValidFrom}, validTo=${cleanValidTo}`,
      )
    }
  }

  return { validFrom: cleanValidFrom, validTo: cleanValidTo }
}

export function validateReminderDays(days: unknown): number[] {
  if (days === undefined || days === null || (Array.isArray(days) && days.length === 0)) {
    return [...DEFAULT_COMPLIANCE_REMINDER_DAYS]
  }
  if (!Array.isArray(days)) {
    throw new Error('reminderDays must be an array of numbers.')
  }
  if (days.length < MIN_COMPLIANCE_REMINDER_DAYS_COUNT) {
    throw new Error(
      `reminderDays must contain at least ${MIN_COMPLIANCE_REMINDER_DAYS_COUNT} value.`,
    )
  }
  if (days.length > MAX_COMPLIANCE_REMINDER_DAYS_COUNT) {
    throw new Error(
      `reminderDays must contain at most ${MAX_COMPLIANCE_REMINDER_DAYS_COUNT} values.`,
    )
  }

  const seen = new Set<number>()
  const parsed: number[] = []

  for (let i = 0; i < days.length; i++) {
    const val = days[i]
    if (typeof val !== 'number' || !Number.isInteger(val)) {
      throw new Error(`reminderDays[${i}] must be an integer.`)
    }
    if (val < MIN_COMPLIANCE_REMINDER_DAY || val > MAX_COMPLIANCE_REMINDER_DAY) {
      throw new Error(
        `reminderDays[${i}] must be between ${MIN_COMPLIANCE_REMINDER_DAY} and ${MAX_COMPLIANCE_REMINDER_DAY}.`,
      )
    }
    if (seen.has(val)) {
      throw new Error(`reminderDays must contain distinct values; found duplicate: ${val}.`)
    }
    seen.add(val)
    parsed.push(val)
  }

  // Sort descending
  parsed.sort((a, b) => b - a)
  return parsed
}

export function validateOwnerAndBackup(
  ownerUid: unknown,
  backupUid: unknown,
): { ownerUid: string; backupUid?: string } {
  if (typeof ownerUid !== 'string' || !ownerUid.trim()) {
    throw new Error('ownerUid must be a non-empty string.')
  }
  const cleanOwnerUid = ownerUid.trim()
  let cleanBackupUid: string | undefined

  if (backupUid !== undefined && backupUid !== null && backupUid !== '') {
    if (typeof backupUid !== 'string' || !backupUid.trim()) {
      throw new Error('backupUid must be a non-empty string when provided.')
    }
    cleanBackupUid = backupUid.trim()
    if (cleanBackupUid === cleanOwnerUid) {
      throw new Error('owner and backup must be distinct active members.')
    }
  }

  return { ownerUid: cleanOwnerUid, backupUid: cleanBackupUid }
}

const COMPLIANCE_STORAGE_PATH_REGEX =
  /^workspaces\/([^/]+)\/ops_compliance\/([^/]+)\/([^/]+)\/([^/]+)$/

export function validateComplianceEvidence(
  evidence: unknown,
  expectedWorkspaceId?: string,
  expectedItemId?: string,
): string[] {
  if (evidence === undefined || evidence === null) {
    return []
  }
  if (!Array.isArray(evidence)) {
    throw new Error('evidence must be an array of storage paths.')
  }
  if (evidence.length > MAX_COMPLIANCE_EVIDENCE_COUNT) {
    throw new Error(`evidence must contain at most ${MAX_COMPLIANCE_EVIDENCE_COUNT} items.`)
  }

  const validated: string[] = []
  for (let i = 0; i < evidence.length; i++) {
    const p = evidence[i]
    if (typeof p !== 'string' || !p.trim()) {
      throw new Error(`evidence[${i}] must be a non-empty storage path string.`)
    }
    const cleanPath = p.trim()
    const match = cleanPath.match(COMPLIANCE_STORAGE_PATH_REGEX)
    if (!match) {
      throw new Error(
        `Invalid storage path "${cleanPath}". Expected format workspaces/{ws}/ops_compliance/{itemId}/{uid}/{fileId}.`,
      )
    }
    if (expectedWorkspaceId && match[1] !== expectedWorkspaceId) {
      throw new Error(
        `evidence[${i}] storage path workspaceId "${match[1]}" does not match "${expectedWorkspaceId}".`,
      )
    }
    if (expectedItemId && match[2] !== expectedItemId) {
      throw new Error(
        `evidence[${i}] storage path itemId "${match[2]}" does not match "${expectedItemId}".`,
      )
    }
    validated.push(cleanPath)
  }
  return validated
}

export function validateRenewalValidTo(oldValidTo: string, newValidTo: unknown): string {
  const cleanOld = validateDayKey(oldValidTo, 'oldValidTo')
  const cleanNew = validateDayKey(newValidTo, 'newValidTo')
  if (cleanNew <= cleanOld) {
    throw new Error(
      `Renewal validTo "${cleanNew}" must be strictly after existing validTo "${cleanOld}".`,
    )
  }
  return cleanNew
}

export function validateComplianceArchiveReason(reason: unknown): string {
  if (typeof reason !== 'string') {
    throw new Error('Archive reason must be a string.')
  }
  const trimmed = reason.trim()
  if (trimmed.length < MIN_COMPLIANCE_ARCHIVE_REASON_LENGTH) {
    throw new Error(`Archive reason must be at least ${MIN_COMPLIANCE_ARCHIVE_REASON_LENGTH} characters.`)
  }
  if (trimmed.length > MAX_COMPLIANCE_ARCHIVE_REASON_LENGTH) {
    throw new Error(`Archive reason must be at most ${MAX_COMPLIANCE_ARCHIVE_REASON_LENGTH} characters.`)
  }
  return trimmed
}

export interface CreateComplianceItemInput {
  workspaceId: string
  sector: OpsSector
  title: string
  category: OpsComplianceCategory
  authority?: string
  reference?: string
  placeId?: string | null
  personRef?: OpsPersonRef | null
  legalRoleKey?: string | null
  validFrom?: string | null
  validTo: string
  reminderDays?: number[]
  ownerUid: string
  backupUid?: string | null
  notes?: string
  evidence?: string[]
  starterKey?: string | null
  opId?: string
}

export interface UpdateComplianceItemInput {
  workspaceId: string
  itemId: string
  title: string
  category: OpsComplianceCategory
  authority?: string
  reference?: string
  placeId?: string | null
  personRef?: OpsPersonRef | null
  legalRoleKey?: string | null
  validFrom?: string | null
  validTo: string
  reminderDays?: number[]
  ownerUid: string
  backupUid?: string | null
  notes?: string
  expectedVersion: number
  opId?: string
}

export interface RenewComplianceItemInput {
  workspaceId: string
  itemId: string
  validTo: string
  validFrom?: string | null
  reference?: string
  evidence?: string[]
  expectedVersion?: number
  opId?: string
}

export interface AttachComplianceEvidenceInput {
  workspaceId: string
  itemId: string
  evidence: string[]
  opId?: string
}

export interface ArchiveComplianceItemInput {
  workspaceId: string
  itemId: string
  reason: string
  opId?: string
}

// ---------------------------------------------------------------------------
// Assets, Work Orders and Release-to-Use (R39 A8.a / SO-P67)
// ---------------------------------------------------------------------------

export const OPS_ASSET_CRITICALITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const
export type OpsAssetCriticality = (typeof OPS_ASSET_CRITICALITIES)[number]

export const OPS_ASSET_STATUSES = [
  'AVAILABLE',
  'IN_USE',
  'ISOLATED',
  'MAINTENANCE',
  'RETIRED',
] as const
export type OpsAssetStatus = (typeof OPS_ASSET_STATUSES)[number]

export const OPS_ASSET_HISTORY_TYPES = [
  'CREATED',
  'UPDATED',
  'STATUS_CHANGED',
  'ISOLATED',
  'RELEASED',
  'RETIRED',
  'PM_RAISED',
  'CALIBRATION_RAISED',
  'PM_DONE',
  'CALIBRATION_DONE',
  'AMC_REMINDER',
  'WARRANTY_REMINDER',
  'DOWNTIME_CLOSED',
  'SERVICE_RECORDED',
] as const
export type OpsAssetHistoryType = (typeof OPS_ASSET_HISTORY_TYPES)[number]

export const MIN_ASSET_CODE_LENGTH = 1
export const MAX_ASSET_CODE_LENGTH = 40
export const MIN_ASSET_NAME_LENGTH = 1
export const MAX_ASSET_NAME_LENGTH = 120
export const MAX_ASSET_CATEGORY_LENGTH = 60
export const MAX_ASSET_MODEL_LENGTH = 80
export const MAX_ASSET_SERIAL_LENGTH = 80
export const MAX_ASSET_VENDOR_NAME_LENGTH = 120
export const MAX_ASSET_VENDOR_PHONE_LENGTH = 20
export const MAX_ASSET_VENDOR_CONTRACT_NO_LENGTH = 60
export const MIN_ASSET_INTERVAL_DAYS = 1
export const MAX_ASSET_INTERVAL_DAYS = 3650
export const MIN_ASSET_LEAD_DAYS = 0
export const MAX_ASSET_LEAD_DAYS = 60
export const DEFAULT_ASSET_LEAD_DAYS = 7
export const MIN_ASSET_REASON_LENGTH = 3
export const MAX_ASSET_REASON_LENGTH = 500
export const DEFAULT_ASSET_REMINDER_DAYS = [60, 30, 7, 0] as const

export const ASSET_REGISTRATION_KINDS = [
  'AERB',
  'PCPNDT_FORM_B',
  'DRUG_LICENCE',
  'BLOOD_BANK_LICENCE',
  'PNDT_REGISTRATION',
  'OTHER',
] as const
export type AssetRegistrationKind = (typeof ASSET_REGISTRATION_KINDS)[number]

export interface AssetRegistration {
  kind: AssetRegistrationKind
  number: string
  issuedBy?: string
  validUntil?: string
}

export const MAX_ASSET_REGISTRATIONS = 5
export const MIN_REGISTRATION_NUMBER_LENGTH = 1
export const MAX_REGISTRATION_NUMBER_LENGTH = 60
export const MAX_REGISTRATION_ISSUED_BY_LENGTH = 80

export const MIN_ASSET_TEMP_BAND_C = -200
export const MAX_ASSET_TEMP_BAND_C = 100

export interface AssetTempBand {
  min: number
  max: number
  unit: 'C'
}

export interface HosEquipmentPreset {
  key: string
  labelKey: string
  defaultCriticality: OpsAssetCriticality
  pmIntervalDays: number | null
  calibrationIntervalDays: number | null
}

export const HOS_EQUIPMENT_PRESETS: readonly HosEquipmentPreset[] = [
  {
    key: 'ventilator',
    labelKey: 'NLSOpsPresetVentilator',
    defaultCriticality: 'CRITICAL',
    pmIntervalDays: 90,
    calibrationIntervalDays: 180,
  },
  {
    key: 'defibrillator',
    labelKey: 'NLSOpsPresetDefibrillator',
    defaultCriticality: 'CRITICAL',
    pmIntervalDays: 90,
    calibrationIntervalDays: 180,
  },
  {
    key: 'infusion_pump',
    labelKey: 'NLSOpsPresetInfusionPump',
    defaultCriticality: 'HIGH',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'syringe_pump',
    labelKey: 'NLSOpsPresetSyringePump',
    defaultCriticality: 'HIGH',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'patient_monitor',
    labelKey: 'NLSOpsPresetPatientMonitor',
    defaultCriticality: 'HIGH',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'ecg_machine',
    labelKey: 'NLSOpsPresetEcgMachine',
    defaultCriticality: 'MEDIUM',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'suction_machine',
    labelKey: 'NLSOpsPresetSuctionMachine',
    defaultCriticality: 'HIGH',
    pmIntervalDays: 90,
    calibrationIntervalDays: null,
  },
  {
    key: 'oxygen_concentrator',
    labelKey: 'NLSOpsPresetOxygenConcentrator',
    defaultCriticality: 'HIGH',
    pmIntervalDays: 90,
    calibrationIntervalDays: 365,
  },
  {
    key: 'anaesthesia_workstation',
    labelKey: 'NLSOpsPresetAnaesthesiaWorkstation',
    defaultCriticality: 'CRITICAL',
    pmIntervalDays: 90,
    calibrationIntervalDays: 180,
  },
  {
    key: 'autoclave',
    labelKey: 'NLSOpsPresetAutoclave',
    defaultCriticality: 'HIGH',
    pmIntervalDays: 90,
    calibrationIntervalDays: 180,
  },
  {
    key: 'x_ray_unit',
    labelKey: 'NLSOpsPresetXRayUnit',
    defaultCriticality: 'HIGH',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'ultrasound',
    labelKey: 'NLSOpsPresetUltrasound',
    defaultCriticality: 'MEDIUM',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'pulse_oximeter',
    labelKey: 'NLSOpsPresetPulseOximeter',
    defaultCriticality: 'MEDIUM',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'bp_monitor',
    labelKey: 'NLSOpsPresetBpMonitor',
    defaultCriticality: 'MEDIUM',
    pmIntervalDays: 180,
    calibrationIntervalDays: 365,
  },
  {
    key: 'nebuliser',
    labelKey: 'NLSOpsPresetNebuliser',
    defaultCriticality: 'LOW',
    pmIntervalDays: 180,
    calibrationIntervalDays: null,
  },
  {
    key: 'hospital_bed_electric',
    labelKey: 'NLSOpsPresetHospitalBedElectric',
    defaultCriticality: 'LOW',
    pmIntervalDays: 180,
    calibrationIntervalDays: null,
  },
  {
    key: 'wheelchair',
    labelKey: 'NLSOpsPresetWheelchair',
    defaultCriticality: 'LOW',
    pmIntervalDays: 180,
    calibrationIntervalDays: null,
  },
  {
    key: 'stretcher',
    labelKey: 'NLSOpsPresetStretcher',
    defaultCriticality: 'LOW',
    pmIntervalDays: 180,
    calibrationIntervalDays: null,
  },
] as const

export interface OpsAssetIsolation {
  auto: boolean
  reason: string
  byUid: string
  at: number
  requestId?: string
}

export interface OpsAssetLastRelease {
  byUid: string
  at: number
  requestId?: string
  selfRelease?: boolean
  reason?: string
}

export interface OpsAssetVendor {
  name?: string
  phone?: string
  contractNo?: string
}

export interface OpsAssetMaintenanceSchedule {
  intervalDays: number
  nextDueKey: string
  leadDays: number
  openRequestId?: string
  skippedDueKey?: string
}

export interface OpsAsset {
  id: string
  workspaceId: string
  sector: OpsSector
  assetCode: string
  name: string
  category?: string
  model?: string
  serial?: string
  criticality: OpsAssetCriticality
  placeId: string
  readAccess?: string[] | null
  status: OpsAssetStatus
  isolation?: OpsAssetIsolation
  lastRelease?: OpsAssetLastRelease
  vendor?: OpsAssetVendor
  pm?: OpsAssetMaintenanceSchedule
  calibration?: OpsAssetMaintenanceSchedule
  amcUntil?: string
  warrantyUntil?: string
  installedOn?: string
  customerId?: string
  amcRemindersDone: number[]
  warrantyRemindersDone: number[]
  registrations?: AssetRegistration[]
  tempBand?: AssetTempBand
  nextTickKey?: string | null
  downSince?: number
  downReason?: 'FAULT' | 'PM' | 'CALIBRATION' | 'MANUAL'
  downRequestId?: string
  downtimeMinutesByMonth?: Record<string, number>
  retired: boolean
  retireReason?: string
  retiredAt?: number
  retiredBy?: string
  version: number
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

export interface OpsAssetHistoryEntry {
  id: string
  workspaceId: string
  assetId: string
  type: OpsAssetHistoryType
  byUid: string
  at: number
  details?: Record<string, unknown>
}

export interface CreateAssetInput {
  workspaceId: string
  sector?: OpsSector
  assetCode: string
  name: string
  category?: string
  model?: string
  serial?: string
  criticality: OpsAssetCriticality
  placeId: string
  vendor?: OpsAssetVendor
  pm?: OpsAssetMaintenanceSchedule
  calibration?: OpsAssetMaintenanceSchedule
  amcUntil?: string
  warrantyUntil?: string
  installedOn?: string
  registrations?: AssetRegistration[]
  tempBand?: AssetTempBand
  opId?: string
}

export interface UpdateAssetInput {
  workspaceId: string
  assetId: string
  name?: string
  category?: string
  model?: string
  serial?: string
  criticality?: OpsAssetCriticality
  placeId?: string
  vendor?: OpsAssetVendor
  pm?: OpsAssetMaintenanceSchedule | null
  calibration?: OpsAssetMaintenanceSchedule | null
  amcUntil?: string | null
  warrantyUntil?: string | null
  installedOn?: string | null
  registrations?: AssetRegistration[] | null
  tempBand?: AssetTempBand | null
  expectedVersion?: number
  opId?: string
}

export interface SetAssetStatusInput {
  workspaceId: string
  assetId: string
  toStatus: OpsAssetStatus
  expectedVersion?: number
  opId?: string
  downReason?: 'FAULT' | 'PM' | 'CALIBRATION' | 'MANUAL'
  downRequestId?: string
}

export interface IsolateAssetInput {
  workspaceId: string
  assetId: string
  reason: string
  expectedVersion?: number
  opId?: string
}

export interface ReleaseAssetInput {
  workspaceId: string
  assetId: string
  toStatus: 'AVAILABLE' | 'IN_USE'
  selfRelease?: boolean
  reason?: string
  expectedVersion?: number
  opId?: string
}

export const OPS_ASSET_SERVICE_VISIT_KINDS = [
  'PM',
  'CALIBRATION',
  'REPAIR',
  'AMC_VISIT',
  'INSTALL',
  'OTHER',
] as const
export type OpsAssetServiceVisitKind = (typeof OPS_ASSET_SERVICE_VISIT_KINDS)[number]

export interface OpsAssetServiceVisit {
  id: string
  workspaceId: string
  assetId: string
  visitDayKey: string
  kind: OpsAssetServiceVisitKind
  vendorName: string
  engineerName?: string
  summary: string
  invoiceRef?: string
  requestId?: string
  filePaths?: string[]
  createdAt: number
  createdBy: string
}

export interface RecordAssetServiceVisitInput {
  workspaceId: string
  assetId: string
  opId: string
  visitDayKey: string
  kind: OpsAssetServiceVisitKind
  vendorName?: string
  engineerName?: string
  summary: string
  invoiceRef?: string
  requestId?: string
  filePaths?: string[]
}

export const MIN_ASSET_SERVICE_SUMMARY_LENGTH = 3
export const MAX_ASSET_SERVICE_SUMMARY_LENGTH = 1000
export const MAX_ASSET_INVOICE_REF_LENGTH = 80
export const MAX_ASSET_ENGINEER_NAME_LENGTH = 120

export function cleanAssetServiceSummary(raw: unknown): string {
  if (typeof raw !== 'string') {
    throw new Error('Summary must be a string')
  }
  const trimmed = raw.trim()
  if (
    trimmed.length < MIN_ASSET_SERVICE_SUMMARY_LENGTH ||
    trimmed.length > MAX_ASSET_SERVICE_SUMMARY_LENGTH
  ) {
    throw new Error(
      `Summary must be between ${MIN_ASSET_SERVICE_SUMMARY_LENGTH} and ${MAX_ASSET_SERVICE_SUMMARY_LENGTH} characters`,
    )
  }
  return trimmed
}

export function cleanAssetInvoiceRef(raw: unknown): string | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined
  if (typeof raw !== 'string') {
    throw new Error('Invoice reference must be a string')
  }
  const trimmed = raw.trim()
  if (trimmed.length > MAX_ASSET_INVOICE_REF_LENGTH) {
    throw new Error(`Invoice reference must not exceed ${MAX_ASSET_INVOICE_REF_LENGTH} characters`)
  }
  return trimmed || undefined
}

export function validateAssetServiceFilePaths(
  filePaths: unknown,
  workspaceId: string,
  assetId: string,
  uid: string,
): string[] | undefined {
  if (filePaths === undefined || filePaths === null) return undefined
  if (!Array.isArray(filePaths)) {
    throw new Error('filePaths must be an array of strings')
  }
  if (filePaths.length > 20) {
    throw new Error('Cannot attach more than 20 files per service record')
  }
  const prefix = `workspaces/${workspaceId}/ops_assets/${assetId}/${uid}/`
  const result: string[] = []
  for (const p of filePaths) {
    if (typeof p !== 'string') {
      throw new Error('Each filePath must be a string')
    }
    const trimmed = p.trim()
    if (!trimmed.startsWith(prefix)) {
      throw new Error(`File path must start with "${prefix}": got "${trimmed}"`)
    }
    const filename = trimmed.slice(prefix.length)
    if (!filename || filename.includes('/') || filename.includes('..')) {
      throw new Error(`Invalid file path: "${trimmed}"`)
    }
    result.push(trimmed)
  }
  return result.length > 0 ? result : undefined
}

export interface RetireAssetInput {
  workspaceId: string
  assetId: string
  reason: string
  expectedVersion?: number
  opId?: string
}

export function cleanAssetCode(raw: unknown): string {
  if (typeof raw !== 'string') {
    throw new Error('Asset code must be a string')
  }
  const trimmed = raw.trim()
  if (trimmed.length < MIN_ASSET_CODE_LENGTH) {
    throw new Error(`Asset code must be at least ${MIN_ASSET_CODE_LENGTH} character`)
  }
  if (trimmed.length > MAX_ASSET_CODE_LENGTH) {
    throw new Error(`Asset code must be at most ${MAX_ASSET_CODE_LENGTH} characters`)
  }
  return trimmed
}

export function cleanAssetName(raw: unknown): string {
  if (typeof raw !== 'string') {
    throw new Error('Asset name must be a string')
  }
  const trimmed = raw.trim()
  if (trimmed.length < MIN_ASSET_NAME_LENGTH) {
    throw new Error(`Asset name must be at least ${MIN_ASSET_NAME_LENGTH} character`)
  }
  if (trimmed.length > MAX_ASSET_NAME_LENGTH) {
    throw new Error(`Asset name must be at most ${MAX_ASSET_NAME_LENGTH} characters`)
  }
  return trimmed
}

export function cleanAssetCategory(raw: unknown): string | undefined {
  if (raw === undefined || raw === null) return undefined
  if (typeof raw !== 'string') {
    throw new Error('Category must be a string')
  }
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  if (trimmed.length > MAX_ASSET_CATEGORY_LENGTH) {
    throw new Error(`Category must be at most ${MAX_ASSET_CATEGORY_LENGTH} characters`)
  }
  return trimmed
}

export function cleanAssetModel(raw: unknown): string | undefined {
  if (raw === undefined || raw === null) return undefined
  if (typeof raw !== 'string') {
    throw new Error('Model must be a string')
  }
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  if (trimmed.length > MAX_ASSET_MODEL_LENGTH) {
    throw new Error(`Model must be at most ${MAX_ASSET_MODEL_LENGTH} characters`)
  }
  return trimmed
}

export function cleanAssetSerial(raw: unknown): string | undefined {
  if (raw === undefined || raw === null) return undefined
  if (typeof raw !== 'string') {
    throw new Error('Serial must be a string')
  }
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  if (trimmed.length > MAX_ASSET_SERIAL_LENGTH) {
    throw new Error(`Serial must be at most ${MAX_ASSET_SERIAL_LENGTH} characters`)
  }
  return trimmed
}

export function cleanAssetCriticality(raw: unknown): OpsAssetCriticality {
  if (typeof raw !== 'string') {
    throw new Error('Criticality must be a string')
  }
  const trimmed = raw.trim() as OpsAssetCriticality
  if (!OPS_ASSET_CRITICALITIES.includes(trimmed)) {
    throw new Error(
      `Invalid criticality "${raw}". Must be one of: ${OPS_ASSET_CRITICALITIES.join(', ')}`,
    )
  }
  return trimmed
}

export function cleanAssetStatus(raw: unknown): OpsAssetStatus {
  if (typeof raw !== 'string') {
    throw new Error('Status must be a string')
  }
  const trimmed = raw.trim() as OpsAssetStatus
  if (!OPS_ASSET_STATUSES.includes(trimmed)) {
    throw new Error(
      `Invalid status "${raw}". Must be one of: ${OPS_ASSET_STATUSES.join(', ')}`,
    )
  }
  return trimmed
}

export function cleanAssetVendor(raw: unknown): OpsAssetVendor | undefined {
  if (raw === undefined || raw === null) return undefined
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Vendor must be an object')
  }
  const obj = raw as Record<string, unknown>
  let name: string | undefined
  if (obj.name !== undefined && obj.name !== null) {
    if (typeof obj.name !== 'string') throw new Error('Vendor name must be a string')
    const t = obj.name.trim()
    if (t.length > MAX_ASSET_VENDOR_NAME_LENGTH) {
      throw new Error(`Vendor name must be at most ${MAX_ASSET_VENDOR_NAME_LENGTH} characters`)
    }
    if (t) name = t
  }

  let phone: string | undefined
  if (obj.phone !== undefined && obj.phone !== null) {
    if (typeof obj.phone !== 'string') throw new Error('Vendor phone must be a string')
    const t = obj.phone.trim()
    if (t.length > MAX_ASSET_VENDOR_PHONE_LENGTH) {
      throw new Error(`Vendor phone must be at most ${MAX_ASSET_VENDOR_PHONE_LENGTH} characters`)
    }
    if (t) phone = t
  }

  let contractNo: string | undefined
  if (obj.contractNo !== undefined && obj.contractNo !== null) {
    if (typeof obj.contractNo !== 'string') throw new Error('Vendor contractNo must be a string')
    const t = obj.contractNo.trim()
    if (t.length > MAX_ASSET_VENDOR_CONTRACT_NO_LENGTH) {
      throw new Error(
        `Vendor contractNo must be at most ${MAX_ASSET_VENDOR_CONTRACT_NO_LENGTH} characters`,
      )
    }
    if (t) contractNo = t
  }

  if (!name && !phone && !contractNo) return undefined
  return {
    ...(name ? { name } : {}),
    ...(phone ? { phone } : {}),
    ...(contractNo ? { contractNo } : {}),
  }
}

export function cleanAssetScheduleConfig(
  raw: unknown,
  label: 'pm' | 'calibration',
): OpsAssetMaintenanceSchedule | undefined {
  if (raw === undefined || raw === null) return undefined
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`${label} schedule must be an object`)
  }
  const obj = raw as Record<string, unknown>
  if (
    typeof obj.intervalDays !== 'number' ||
    !Number.isInteger(obj.intervalDays) ||
    obj.intervalDays < MIN_ASSET_INTERVAL_DAYS ||
    obj.intervalDays > MAX_ASSET_INTERVAL_DAYS
  ) {
    throw new Error(
      `${label}.intervalDays must be an integer between ${MIN_ASSET_INTERVAL_DAYS} and ${MAX_ASSET_INTERVAL_DAYS}`,
    )
  }

  const nextDueKey = validateDayKey(obj.nextDueKey, `${label}.nextDueKey`)

  let leadDays = DEFAULT_ASSET_LEAD_DAYS
  if (obj.leadDays !== undefined && obj.leadDays !== null) {
    if (
      typeof obj.leadDays !== 'number' ||
      !Number.isInteger(obj.leadDays) ||
      obj.leadDays < MIN_ASSET_LEAD_DAYS ||
      obj.leadDays > MAX_ASSET_LEAD_DAYS
    ) {
      throw new Error(
        `${label}.leadDays must be an integer between ${MIN_ASSET_LEAD_DAYS} and ${MAX_ASSET_LEAD_DAYS}`,
      )
    }
    leadDays = obj.leadDays
  }

  const openRequestId =
    typeof obj.openRequestId === 'string' && obj.openRequestId.trim()
      ? obj.openRequestId.trim()
      : undefined

  const skippedDueKey =
    typeof obj.skippedDueKey === 'string' && obj.skippedDueKey.trim()
      ? validateDayKey(obj.skippedDueKey, `${label}.skippedDueKey`)
      : undefined

  return {
    intervalDays: obj.intervalDays,
    nextDueKey,
    leadDays,
    ...(openRequestId ? { openRequestId } : {}),
    ...(skippedDueKey ? { skippedDueKey } : {}),
  }
}

export function cleanAssetRegistrations(raw: unknown): AssetRegistration[] | undefined {
  if (raw === undefined || raw === null) return undefined
  if (!Array.isArray(raw)) {
    throw new Error('Registrations must be an array')
  }
  if (raw.length > MAX_ASSET_REGISTRATIONS) {
    throw new Error(`Cannot have more than ${MAX_ASSET_REGISTRATIONS} registrations per asset`)
  }
  const result: AssetRegistration[] = []
  for (let i = 0; i < raw.length; i++) {
    const item = raw[i]
    if (!item || typeof item !== 'object') {
      throw new Error(`Registration at index ${i} must be an object`)
    }
    const itemRec = item as Record<string, unknown>
    const kind = itemRec.kind
    if (typeof kind !== 'string' || !(ASSET_REGISTRATION_KINDS as readonly string[]).includes(kind)) {
      throw new Error(`Invalid registration kind: "${String(kind)}"`)
    }
    const numRaw = itemRec.number
    if (typeof numRaw !== 'string') {
      throw new Error(`Registration number at index ${i} must be a string`)
    }
    const numTrimmed = numRaw.trim()
    if (
      numTrimmed.length < MIN_REGISTRATION_NUMBER_LENGTH ||
      numTrimmed.length > MAX_REGISTRATION_NUMBER_LENGTH
    ) {
      throw new Error(
        `Registration number must be between ${MIN_REGISTRATION_NUMBER_LENGTH} and ${MAX_REGISTRATION_NUMBER_LENGTH} characters`,
      )
    }
    let issuedBy: string | undefined = undefined
    if (
      itemRec.issuedBy !== undefined &&
      itemRec.issuedBy !== null &&
      itemRec.issuedBy !== ''
    ) {
      if (typeof itemRec.issuedBy !== 'string') {
        throw new Error(`issuedBy at index ${i} must be a string`)
      }
      const ibTrimmed = itemRec.issuedBy.trim()
      if (ibTrimmed.length > MAX_REGISTRATION_ISSUED_BY_LENGTH) {
        throw new Error(
          `issuedBy must not exceed ${MAX_REGISTRATION_ISSUED_BY_LENGTH} characters`,
        )
      }
      if (ibTrimmed.length > 0) {
        issuedBy = ibTrimmed
      }
    }
    let validUntil: string | undefined = undefined
    if (
      itemRec.validUntil !== undefined &&
      itemRec.validUntil !== null &&
      itemRec.validUntil !== ''
    ) {
      validUntil = cleanAssetDayKey(itemRec.validUntil, `Registration validUntil at index ${i}`)
    }
    result.push({
      kind: kind as AssetRegistrationKind,
      number: numTrimmed,
      ...(issuedBy ? { issuedBy } : {}),
      ...(validUntil ? { validUntil } : {}),
    })
  }
  return result
}

export function cleanAssetTempBand(raw: unknown): AssetTempBand | undefined {
  if (raw === undefined || raw === null) return undefined
  if (typeof raw !== 'object') {
    throw new Error('tempBand must be an object')
  }
  const obj = raw as Record<string, unknown>
  if (typeof obj.min !== 'number' || isNaN(obj.min) || !isFinite(obj.min)) {
    throw new Error('tempBand.min must be a valid number')
  }
  if (typeof obj.max !== 'number' || isNaN(obj.max) || !isFinite(obj.max)) {
    throw new Error('tempBand.max must be a valid number')
  }
  if (obj.unit !== 'C') {
    throw new Error('tempBand.unit must be "C"')
  }
  if (obj.min < MIN_ASSET_TEMP_BAND_C || obj.min > MAX_ASSET_TEMP_BAND_C) {
    throw new Error(`tempBand.min must be between ${MIN_ASSET_TEMP_BAND_C} and ${MAX_ASSET_TEMP_BAND_C}`)
  }
  if (obj.max < MIN_ASSET_TEMP_BAND_C || obj.max > MAX_ASSET_TEMP_BAND_C) {
    throw new Error(`tempBand.max must be between ${MIN_ASSET_TEMP_BAND_C} and ${MAX_ASSET_TEMP_BAND_C}`)
  }
  if (obj.min >= obj.max) {
    throw new Error('tempBand.min must be strictly less than tempBand.max')
  }
  return {
    min: obj.min,
    max: obj.max,
    unit: 'C',
  }
}

export function cleanAssetReason(raw: unknown, label: string): string {
  if (typeof raw !== 'string') {
    throw new Error(`${label} reason must be a string`)
  }
  const trimmed = raw.trim()
  if (trimmed.length < MIN_ASSET_REASON_LENGTH) {
    throw new Error(
      `${label} reason must be at least ${MIN_ASSET_REASON_LENGTH} characters`,
    )
  }
  if (trimmed.length > MAX_ASSET_REASON_LENGTH) {
    throw new Error(
      `${label} reason must be at most ${MAX_ASSET_REASON_LENGTH} characters`,
    )
  }
  return trimmed
}

export function isAllowedAssetStatusMove(
  from: OpsAssetStatus,
  to: OpsAssetStatus,
  method: 'setAssetStatus' | 'isolate' | 'release' | 'retire',
): boolean {
  if (from === 'RETIRED') {
    return false
  }

  if (method === 'setAssetStatus') {
    if (from === 'AVAILABLE') {
      return to === 'IN_USE' || to === 'MAINTENANCE'
    }
    if (from === 'IN_USE') {
      return to === 'AVAILABLE' || to === 'MAINTENANCE'
    }
    return false
  }

  if (method === 'isolate') {
    if (from === 'ISOLATED') return false
    return to === 'ISOLATED'
  }

  if (method === 'release') {
    if (from !== 'ISOLATED' && from !== 'MAINTENANCE') return false
    return to === 'AVAILABLE' || to === 'IN_USE'
  }

  if (method === 'retire') {
    return to === 'RETIRED'
  }

  return false
}

export function shiftDayKey(dayKey: string, deltaDays: number): string {
  const ms = dayKeyToUtcMs(dayKey) + deltaDays * 86_400_000
  return utcMsToDayKey(ms)
}

export function computeAssetNextTickKey(
  asset: Partial<OpsAsset> | null | undefined,
): string | null {
  if (!asset || asset.retired || asset.status === 'RETIRED') {
    return null
  }

  const candidates: string[] = []

  // 1. PM
  if (
    asset.pm?.nextDueKey &&
    !asset.pm.openRequestId &&
    asset.pm.skippedDueKey !== asset.pm.nextDueKey
  ) {
    const lead = asset.pm.leadDays ?? DEFAULT_ASSET_LEAD_DAYS
    candidates.push(shiftDayKey(asset.pm.nextDueKey, -lead))
  }

  // 2. Calibration
  if (
    asset.calibration?.nextDueKey &&
    !asset.calibration.openRequestId &&
    asset.calibration.skippedDueKey !== asset.calibration.nextDueKey
  ) {
    const lead = asset.calibration.leadDays ?? DEFAULT_ASSET_LEAD_DAYS
    candidates.push(shiftDayKey(asset.calibration.nextDueKey, -lead))
  }

  // 3. AMC reminder
  if (asset.amcUntil) {
    const done = asset.amcRemindersDone ?? []
    for (const r of DEFAULT_ASSET_REMINDER_DAYS) {
      if (!done.includes(r)) {
        candidates.push(shiftDayKey(asset.amcUntil, -r))
        break
      }
    }
  }

  // 4. Warranty reminder
  if (asset.warrantyUntil) {
    const done = asset.warrantyRemindersDone ?? []
    for (const r of DEFAULT_ASSET_REMINDER_DAYS) {
      if (!done.includes(r)) {
        candidates.push(shiftDayKey(asset.warrantyUntil, -r))
        break
      }
    }
  }

  if (candidates.length === 0) {
    return null
  }

  candidates.sort()
  return candidates[0]
}

export function rollAssetDueKey(
  currentDueKey: string,
  intervalDays: number,
  doneDayKey: string,
): string {
  if (intervalDays <= 0) {
    throw new Error('intervalDays must be positive')
  }
  let next = shiftDayKey(currentDueKey, intervalDays)
  while (next <= doneDayKey) {
    next = shiftDayKey(next, intervalDays)
  }
  return next
}

/* =======================================================================
   Delivery Sector (R39 E1.a)
   ======================================================================= */

export type OpsEngagementModel = 'FIXED' | 'TIME_AND_MATERIALS' | 'RETAINER'
export const OPS_ENGAGEMENT_MODELS = ['FIXED', 'TIME_AND_MATERIALS', 'RETAINER'] as const

export type OpsEngagementStatus = 'ACTIVE' | 'PAUSED' | 'CLOSED'
export const OPS_ENGAGEMENT_STATUSES = ['ACTIVE', 'PAUSED', 'CLOSED'] as const

export type OpsMilestoneStatus = 'PLANNED' | 'DONE' | 'INVOICED' | 'PAID'
export const OPS_MILESTONE_STATUSES = ['PLANNED', 'DONE', 'INVOICED', 'PAID'] as const

export interface OpsMilestone {
  id: string
  title: string
  dueDate?: string | null
  status: OpsMilestoneStatus
  completedAt?: string | null
  invoicedAt?: string | null
  paidAt?: string | null
  statusReason?: string | null
}

export interface OpsEngagement {
  id: string
  workspaceId: string
  clientId: string
  name: string
  model: OpsEngagementModel
  status: OpsEngagementStatus
  startDate: string
  endDate?: string | null
  linkedGoalIds: string[]
  milestones: OpsMilestone[]
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsRateCardEntry {
  uid: string
  billRate: number
}

export interface OpsRetainerConfig {
  monthlyFee: number
  includedHours: number
  overageRate: number
  rolloverCapHours?: number | null
}

export interface OpsRetainerMonthFrozen {
  monthKey: string
  fee: number
  billableHours: number
  includedHours: number
  rolloverInHours: number
  rolloverOutHours: number
  overageHours: number
  overageRevenue: number
  totalRevenue: number
  closedAt: string
  closedBy: string
}

export interface OpsEngagementFinance {
  id: string
  engagementId: string
  workspaceId: string
  currency: string
  fixedPriceMinor?: number | null
  milestoneAmounts?: Record<string, number>
  retainer?: OpsRetainerConfig | null
  retainerMonths?: Record<string, OpsRetainerMonthFrozen>
  rateCard?: OpsRateCardEntry[]
  targetMarginPercent?: number | null
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export type OpsAllocationKind = 'BILLED' | 'SHADOW'
export const OPS_ALLOCATION_KINDS = ['BILLED', 'SHADOW'] as const

export interface OpsAllocation {
  id: string
  workspaceId: string
  engagementId: string
  uid: string
  hoursPerWeek?: number | null
  percent?: number | null
  from: string
  to?: string | null
  kind: OpsAllocationKind
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export type OpsMarginRollupScope = 'ENGAGEMENT' | 'CLIENT' | 'WORKSPACE'
export const OPS_MARGIN_ROLLUP_SCOPES = ['ENGAGEMENT', 'CLIENT', 'WORKSPACE'] as const

export interface OpsRetainerCurrentMonthSummary {
  monthKey: string
  billableHours: number
  includedHours: number
  rolloverInHours: number
  overageHours: number
}

export interface OpsMarginRollup {
  id: string
  workspaceId: string
  scope: OpsMarginRollupScope
  engagementId?: string | null
  clientId?: string | null
  currency: string | null
  revenueMinor: number
  costMinor: number
  actualMarginMinor: number
  actualMarginPercent: number
  unpricedHours: number
  approvedBillableHours: number
  approvedNonBillableHours: number
  forecastMarginMinor?: number | null
  forecastMarginPercent?: number | null
  targetMarginPercent?: number | null
  unpricedSpendGoalIds?: string[]
  retainerCurrentMonth?: OpsRetainerCurrentMonthSummary | null
  revenueByModel?: {
    fixedRevenueMinor: number
    tmRevenueMinor: number
    retainerRevenueMinor: number
  }
  costBreakdown?: {
    labourCostMinor: number
    expenseSpendMinor: number
  }
  computedAt: string
}

export interface CreateEngagementInput {
  workspaceId: string
  clientId: string
  name: string
  model: OpsEngagementModel
  startDate: string
  endDate?: string | null
  milestones?: Array<{
    id?: string
    title: string
    dueDate?: string | null
    status?: OpsMilestoneStatus
  }>
  opId?: string | null
}

export interface UpdateEngagementInput {
  workspaceId: string
  engagementId: string
  name?: string
  startDate?: string
  endDate?: string | null
  opId?: string | null
}

export interface SetEngagementStatusInput {
  workspaceId: string
  engagementId: string
  status: OpsEngagementStatus
  opId?: string | null
}

export interface SetEngagementMilestonesInput {
  workspaceId: string
  engagementId: string
  milestones: Array<{
    id?: string
    title: string
    dueDate?: string | null
    status: OpsMilestoneStatus
  }>
  reason?: string | null
  opId?: string | null
}

export interface SetEngagementFinanceInput {
  workspaceId: string
  engagementId: string
  currency: string
  fixedPriceMinor?: number | null
  milestoneAmounts?: Record<string, number> | null
  retainer?: OpsRetainerConfig | null
  rateCard?: OpsRateCardEntry[] | null
  targetMarginPercent?: number | null
  opId?: string | null
}

export interface UpsertAllocationInput {
  workspaceId: string
  allocationId?: string | null
  engagementId: string
  uid: string
  hoursPerWeek?: number | null
  percent?: number | null
  from: string
  to?: string | null
  kind: OpsAllocationKind
  opId?: string | null
}

export interface EndAllocationInput {
  workspaceId: string
  allocationId: string
  toDate: string
  opId?: string | null
}

export interface LinkGoalToEngagementInput {
  workspaceId: string
  goalId: string
  engagementId: string
  opId?: string | null
}

export interface UnlinkGoalFromEngagementInput {
  workspaceId: string
  goalId: string
  engagementId: string
  opId?: string | null
}

export interface RecomputeEngagementMarginInput {
  workspaceId: string
  engagementId: string
}

export interface DeliveryCsvRow {
  engagement: string
  client: string
  model: OpsEngagementModel
  month: string
  currency: string
  CLIENT_BILLABLE: number
  FIXED_BID: number
  CLIENT_NON_BILLABLE: number
  INTERNAL: number
  clientBillableHours?: number
  fixedBidHours?: number
  clientNonBillableHours?: number
  internalHours?: number
  billableHours: number
  unpricedHours: number
  revenue: number
  labourCost: number
  expenses: number
  unpricedExpenses: number
  margin: number
  marginPercent: number
}

export interface ExportDeliveryCsvInput {
  workspaceId: string
  from: string
  to: string
  engagementId?: string | null
}

export interface ExportDeliveryCsvResult {
  success: boolean
  rows: DeliveryCsvRow[]
}
// ---------------------------------------------------------------------------
// Outside Approvals (R39 A10.a / SO-P68)
// ---------------------------------------------------------------------------

export const APPROVAL_LEGAL_DISCLAIMER = 'acknowledgement, not a legal e-signature'

export const MAX_SNAPSHOT_BYTES = 50 * 1024 // 50 KB ceiling per SO-P68
export const MIN_APPROVERS = 1
export const MAX_APPROVERS = 5
export const MIN_APPROVER_NAME_LENGTH = 1
export const MAX_APPROVER_NAME_LENGTH = 80
export const DEFAULT_EXPIRES_IN_DAYS = 7
export const MIN_EXPIRES_IN_DAYS = 1
export const MAX_EXPIRES_IN_DAYS = 30
export const MAX_APPROVAL_MESSAGE_LENGTH = 500
export const MIN_REJECT_COMMENT_LENGTH = 3
export const MAX_REJECT_COMMENT_LENGTH = 1000
export const MIN_GENERAL_COMMENT_LENGTH = 1
export const MAX_GENERAL_COMMENT_LENGTH = 1000
export const MAX_APPROVE_COMMENT_LENGTH = 1000
export const MIN_REVOKE_REASON_LENGTH = 3
export const MAX_REVOKE_REASON_LENGTH = 500
export const MAX_ADMIN_ONLY_KINDS = 200
export const MAX_KIND_KEY_LENGTH = 120
export const MAX_COMMENTS_PER_APPROVER = 5
export const MAX_OTP_PER_APPROVER = 5
export const MAX_OTP_PER_WORKSPACE_DAY = 100

export const OUTSIDE_APPROVAL_REFUSAL_REASONS = [
  'REVOKED',
  'EXPIRED',
  'CLOSED',
  'ALREADY_DECIDED',
] as const

export type OutsideApprovalRefusalReason = (typeof OUTSIDE_APPROVAL_REFUSAL_REASONS)[number]

export const APPROVAL_SETTINGS_REFUSAL_REASONS = [
  'STALE_VERSION',
] as const

export type ApprovalSettingsRefusalReason = (typeof APPROVAL_SETTINGS_REFUSAL_REASONS)[number]

export type OutsideApprovalSubjectType = 'REQUEST' | 'RECORD'
export type OutsideApprovalMode = 'ALL' | 'ANY'
export type OutsideApprovalStatus = 'SENT' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'REVOKED'
export type OutsideApproverStatus = 'PENDING' | 'VIEWED' | 'APPROVED' | 'REJECTED' | 'CLOSED'
export type OutsideApprovalDecision = 'APPROVE' | 'REJECT'

export type OutsideApprovalEventType =
  | 'SENT'
  | 'LINK_REISSUED'
  | 'OTP_PREPARED'
  | 'VIEWED'
  | 'COMMENTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'REVOKED'
  | 'SIGN_IN_DELETED'
  | 'SIGN_IN_KEPT'

export interface OutsideApprover {
  approverId: string
  name: string
  phoneMasked: string
  status: OutsideApproverStatus
  decidedAt?: number
  comment?: string
}

export interface OutsideApproval {
  id: string
  workspaceId: string
  sector: OpsSector
  subjectType: OutsideApprovalSubjectType
  subjectId: string
  kindKey: string
  title: string
  snapshot: Record<string, unknown>
  snapshotHash: string
  subjectVersion: number
  mode: OutsideApprovalMode
  message: string
  expiresAt: number
  status: OutsideApprovalStatus
  approvers: OutsideApprover[]
  createdBy: string
  createdAt: number
  decidedAt?: number
  version: number
}

export interface OutsideApprovalEvent {
  id: string
  approvalId: string
  type: OutsideApprovalEventType
  at: number
  byUid?: string
  approverId?: string
  approverName?: string
  phoneMasked?: string
  label?: string
  decision?: OutsideApprovalDecision
  comment?: string
  reason?: string
  snapshotHash?: string
  subjectVersion?: number
}

export interface OutsideApprovalSettings {
  workspaceId: string
  adminOnlyKinds: string[]
  version: number
  updatedAt?: number
  updatedBy?: string
}

export interface OutsideApprovalSecretDoc {
  approvalId: string
  approverId: string
  workspaceId: string
  tokenHash: string
  phoneHmac: string
  boundUid?: string
  otpPrepared: number[]
  commentsCount: number
  decision?: OutsideApprovalDecision
  decidedAt?: number
  snapshotHash?: string
  subjectVersion?: number
  createdAt: number
  updatedAt: number
}

export interface CreateOutsideApprovalApproverInput {
  name: string
  phone: string
}

export interface CreateOutsideApprovalInput {
  workspaceId: string
  subjectType: OutsideApprovalSubjectType
  subjectId: string
  approvers: CreateOutsideApprovalApproverInput[]
  mode: OutsideApprovalMode
  expiresInDays?: number
  message?: string
  opId: string
}

export interface ReissueApproverLinkInput {
  workspaceId: string
  approvalId: string
  approverId: string
  opId: string
}

export interface RevokeOutsideApprovalInput {
  workspaceId: string
  approvalId: string
  reason: string
  opId: string
}

export interface SetApprovalSettingsInput {
  workspaceId: string
  adminOnlyKinds: string[]
  version?: number
  opId: string
}

export interface GetApprovalLinkInfoInput {
  token: string
}

export interface PrepareApprovalOtpInput {
  token: string
}

export interface OpenApprovalInput {
  token: string
}

export interface DecideApprovalInput {
  token: string
  decision: OutsideApprovalDecision
  comment?: string
  opId: string
}

export interface CommentApprovalInput {
  token: string
  comment: string
  opId: string
}

// Canonical JSON & Hashing
export function canonicalJsonStringify(val: unknown): string {
  if (val === null || typeof val !== 'object') {
    return JSON.stringify(val)
  }

  if (Array.isArray(val)) {
    const items = val.map((item) => canonicalJsonStringify(item))
    return `[${items.join(',')}]`
  }

  const entries = Object.entries(val as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))

  const formatted = entries.map(
    ([k, v]) => `${JSON.stringify(k)}:${canonicalJsonStringify(v)}`,
  )
  return `{${formatted.join(',')}}`
}

export function computeSnapshotHash(val: unknown): string {
  const str = canonicalJsonStringify(val)
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]

  let H0 = 0x6a09e667, H1 = 0xbb67ae85, H2 = 0x3c6ef372, H3 = 0xa54ff53a
  let H4 = 0x510e527f, H5 = 0x9b05688c, H6 = 0x1f83d9ab, H7 = 0x5be0cd19

  const bytes = new TextEncoder().encode(str)
  const bitLength = bytes.length * 8
  const newByteLen = ((bytes.length + 8) >> 6 << 6) + 64
  const buffer = new Uint8Array(newByteLen)
  buffer.set(bytes)
  buffer[bytes.length] = 0x80

  const view = new DataView(buffer.buffer)
  view.setBigUint64(newByteLen - 8, BigInt(bitLength))

  const W = new Uint32Array(64)
  const rrot = (x: number, n: number) => (x >>> n) | (x << (32 - n))

  for (let i = 0; i < newByteLen; i += 64) {
    for (let t = 0; t < 16; t++) {
      W[t] = view.getUint32(i + t * 4)
    }
    for (let t = 16; t < 64; t++) {
      const s0 = rrot(W[t - 15], 7) ^ rrot(W[t - 15], 18) ^ (W[t - 15] >>> 3)
      const s1 = rrot(W[t - 2], 17) ^ rrot(W[t - 2], 19) ^ (W[t - 2] >>> 10)
      W[t] = (W[t - 16] + s0 + W[t - 7] + s1) | 0
    }

    let a = H0, b = H1, c = H2, d = H3, e = H4, f = H5, g = H6, h = H7

    for (let t = 0; t < 64; t++) {
      const S1 = rrot(e, 6) ^ rrot(e, 11) ^ rrot(e, 25)
      const ch = (e & f) ^ (~e & g)
      const temp1 = (h + S1 + ch + K[t] + W[t]) | 0
      const S0 = rrot(a, 2) ^ rrot(a, 13) ^ rrot(a, 22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const temp2 = (S0 + maj) | 0

      h = g
      g = f
      f = e
      e = (d + temp1) | 0
      d = c
      c = b
      b = a
      a = (temp1 + temp2) | 0
    }

    H0 = (H0 + a) | 0
    H1 = (H1 + b) | 0
    H2 = (H2 + c) | 0
    H3 = (H3 + d) | 0
    H4 = (H4 + e) | 0
    H5 = (H5 + f) | 0
    H6 = (H6 + g) | 0
    H7 = (H7 + h) | 0
  }

  const out = new DataView(new ArrayBuffer(32))
  out.setUint32(0, H0)
  out.setUint32(4, H1)
  out.setUint32(8, H2)
  out.setUint32(12, H3)
  out.setUint32(16, H4)
  out.setUint32(20, H5)
  out.setUint32(24, H6)
  out.setUint32(28, H7)

  let hex = ''
  for (let i = 0; i < 32; i++) {
    hex += out.getUint8(i).toString(16).padStart(2, '0')
  }
  return hex
}

// Subjects Registry
export function buildApprovalKindKey(
  subjectType: OutsideApprovalSubjectType,
  subKey: string,
): string {
  return `${subjectType}:${subKey}`
}

export interface OutsideApprovalSubjectDefinition {
  readonly subjectType: OutsideApprovalSubjectType
  buildKindKey: (subKey: string) => string
  checkEligibility: (rawDoc: Record<string, unknown>) => { eligible: boolean; reason?: string }
  projectSnapshot: (rawDoc: Record<string, unknown>) => Record<string, unknown>
}

function sanitizeRecordValues(values: unknown): Record<string, unknown> {
  if (!values || typeof values !== 'object' || Array.isArray(values)) {
    return {}
  }
  const result: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(values as Record<string, unknown>)) {
    const lower = key.toLowerCase()
    if (
      lower.includes('rate') ||
      lower.includes('cost') ||
      lower.includes('price') ||
      lower.includes('salary') ||
      lower.includes('phone') ||
      lower.includes('email') ||
      lower.includes('chat') ||
      lower.includes('uid')
    ) {
      continue
    }
    result[key] = val
  }
  return result
}

export const OUTSIDE_APPROVAL_SUBJECTS: Record<
  OutsideApprovalSubjectType,
  OutsideApprovalSubjectDefinition
> = {
  REQUEST: {
    subjectType: 'REQUEST',
    buildKindKey: (subKey: string) => buildApprovalKindKey('REQUEST', subKey),
    checkEligibility: (rawDoc: Record<string, unknown>) => {
      const kindSnapshot = (rawDoc.kindSnapshot as Record<string, unknown>) || {}
      if (kindSnapshot.restricted === true || rawDoc.restricted === true) {
        return {
          eligible: false,
          reason: 'Restricted request kinds cannot be sent for outside approval.',
        }
      }
      if (kindSnapshot.patientLinked === true || rawDoc.patientLinked === true) {
        return {
          eligible: false,
          reason: 'Patient-linked request kinds cannot be sent for outside approval.',
        }
      }
      return { eligible: true }
    },
    projectSnapshot: (rawDoc: Record<string, unknown>) => {
      const projection: Record<string, unknown> = {
        id: rawDoc.id,
        subjectType: 'REQUEST',
        workspaceId: rawDoc.workspaceId,
        sector: rawDoc.sector,
        kind: rawDoc.kind,
        title: rawDoc.title,
        details: rawDoc.details ?? '',
        placeId: rawDoc.placeId,
        assetId: rawDoc.assetId,
        recordId: rawDoc.recordId,
        photos: Array.isArray(rawDoc.photos) ? rawDoc.photos : [],
        donePhotos: Array.isArray(rawDoc.donePhotos) ? rawDoc.donePhotos : [],
        state: rawDoc.state,
        createdAt: rawDoc.createdAt,
        ackDueAt: rawDoc.ackDueAt,
        dueAt: rawDoc.dueAt,
        acknowledgedAt: rawDoc.acknowledgedAt,
        doneAt: rawDoc.doneAt,
        verifiedAt: rawDoc.verifiedAt,
      }

      const serialized = canonicalJsonStringify(projection)
      if (new TextEncoder().encode(serialized).length > MAX_SNAPSHOT_BYTES) {
        throw new Error(`Snapshot exceeds maximum allowed size of 50 KB.`)
      }
      return projection
    },
  },
  RECORD: {
    subjectType: 'RECORD',
    buildKindKey: (subKey: string) => buildApprovalKindKey('RECORD', subKey),
    checkEligibility: (rawDoc: Record<string, unknown>) => {
      const tpl = (rawDoc.template as Record<string, unknown>) || {}
      if (tpl.restricted === true) {
        return {
          eligible: false,
          reason: 'Records of restricted templates cannot be sent for outside approval.',
        }
      }
      if (tpl.patientLinked === true) {
        return {
          eligible: false,
          reason: 'Records of patient-linked templates cannot be sent for outside approval.',
        }
      }
      const coSign = rawDoc.coSign as { state?: string } | undefined
      if (coSign?.state === 'AWAITING') {
        return {
          eligible: false,
          reason: 'Record awaiting co-sign cannot be sent for outside approval.',
        }
      }
      const isSubmitted = rawDoc.state === 'SUBMITTED'
      const isLocked = Boolean(rawDoc.lockedAt) || rawDoc.state === 'APPROVED' || rawDoc.state === 'LOCKED'
      if (!isSubmitted && !isLocked) {
        return {
          eligible: false,
          reason: 'Records must be submitted or locked before sending for outside approval.',
        }
      }
      return { eligible: true }
    },
    projectSnapshot: (rawDoc: Record<string, unknown>) => {
      const projection: Record<string, unknown> = {
        id: rawDoc.id,
        subjectType: 'RECORD',
        workspaceId: rawDoc.workspaceId,
        sector: rawDoc.sector,
        templateId: rawDoc.templateId,
        templateVersion: rawDoc.templateVersion,
        templateName: rawDoc.templateName ?? '',
        state: rawDoc.state,
        occurredAt: rawDoc.occurredAt,
        values: sanitizeRecordValues(rawDoc.values),
        exceptions: Array.isArray(rawDoc.exceptions) ? rawDoc.exceptions : [],
        createdAt: rawDoc.createdAt,
      }

      const serialized = canonicalJsonStringify(projection)
      if (new TextEncoder().encode(serialized).length > MAX_SNAPSHOT_BYTES) {
        throw new Error(`Snapshot exceeds maximum allowed size of 50 KB.`)
      }
      return projection
    },
  },
}

export function checkSubjectEligibility(
  subjectType: OutsideApprovalSubjectType,
  rawDoc: Record<string, unknown>,
): { eligible: boolean; reason?: string } {
  const handler = OUTSIDE_APPROVAL_SUBJECTS[subjectType]
  if (!handler) {
    return { eligible: false, reason: `Unknown subject type ${subjectType}.` }
  }
  return handler.checkEligibility(rawDoc)
}

export function projectSubjectSnapshot(
  subjectType: OutsideApprovalSubjectType,
  rawDoc: Record<string, unknown>,
): Record<string, unknown> {
  const handler = OUTSIDE_APPROVAL_SUBJECTS[subjectType]
  if (!handler) {
    throw new Error(`Unknown subject type ${subjectType}.`)
  }
  return handler.projectSnapshot(rawDoc)
}

// Phone Masking
const E164_REGEX = /^\+[1-9]\d{7,14}$/

export function isE164Phone(phone: string): boolean {
  if (typeof phone !== 'string') return false
  return E164_REGEX.test(phone.trim())
}

export function maskPhone(phone: string): string {
  const clean = phone.trim()
  if (!isE164Phone(clean)) {
    return clean.slice(-4).padStart(clean.length, '•')
  }
  let ccDigits: number
  if (clean.startsWith('+1') || clean.startsWith('+7')) {
    ccDigits = 1
  } else if (/^\+(?:35\d|37\d|38\d|42\d|50\d|59\d|67\d|68\d|69\d|85\d|88\d|96\d|97\d|99\d)/.test(clean)) {
    ccDigits = 3
  } else {
    ccDigits = 2
  }
  const countryCode = clean.slice(0, ccDigits + 1)
  const last4 = clean.slice(-4)
  return `${countryCode} ••••••${last4}`
}

// Validators
export function validateApprovers(
  raw: unknown,
): Array<{ name: string; phone: string }> {
  if (!Array.isArray(raw) || raw.length < MIN_APPROVERS || raw.length > MAX_APPROVERS) {
    throw new Error(`Between 1 and 5 approvers are required.`)
  }

  const seenPhones = new Set<string>()
  const result: Array<{ name: string; phone: string }> = []

  for (let i = 0; i < raw.length; i++) {
    const item = raw[i] as Record<string, unknown> | null
    if (!item || typeof item !== 'object') {
      throw new Error(`Approver at index ${i} is invalid.`)
    }

    const name = typeof item.name === 'string' ? item.name.trim() : ''
    if (name.length < MIN_APPROVER_NAME_LENGTH || name.length > MAX_APPROVER_NAME_LENGTH) {
      throw new Error(
        `Approver name is required and must have a maximum length of 80 characters.`,
      )
    }

    const phone = typeof item.phone === 'string' ? item.phone.trim() : ''
    if (!isE164Phone(phone)) {
      throw new Error(`Approver phone must be a valid distinct E.164 number.`)
    }

    if (seenPhones.has(phone)) {
      throw new Error(`Approver phones must be distinct E.164 numbers.`)
    }
    seenPhones.add(phone)

    result.push({ name, phone })
  }

  return result
}

export function validateApprovalMode(raw: unknown): OutsideApprovalMode {
  if (raw === 'ALL' || raw === 'ANY') {
    return raw
  }
  throw new Error(`Mode must be ALL or ANY.`)
}

export function validateExpiresInDays(raw: unknown): number {
  if (raw === undefined || raw === null) {
    return DEFAULT_EXPIRES_IN_DAYS
  }
  const days = Number(raw)
  if (!Number.isInteger(days) || days < MIN_EXPIRES_IN_DAYS || days > MAX_EXPIRES_IN_DAYS) {
    throw new Error(`expiresInDays must be an integer between 1 and 30 days.`)
  }
  return days
}

export function validateApprovalMessage(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') {
    return ''
  }
  if (typeof raw !== 'string') {
    throw new Error(`message must be a string.`)
  }
  const trimmed = raw.trim()
  if (trimmed.length > MAX_APPROVAL_MESSAGE_LENGTH) {
    throw new Error(
      `message exceeds maximum length of 500 characters.`,
    )
  }
  return trimmed
}

export function validateApprovalComment(
  raw: unknown,
  context: 'REJECT' | 'APPROVE' | 'COMMENT',
): string {
  if (context === 'APPROVE') {
    if (raw === undefined || raw === null || raw === '') return ''
    if (typeof raw !== 'string') throw new Error(`comment must be a string.`)
    const trimmed = raw.trim()
    if (trimmed.length > MAX_APPROVE_COMMENT_LENGTH) {
      throw new Error(`comment exceeds maximum length of 1000 characters.`)
    }
    return trimmed
  }

  if (context === 'REJECT') {
    if (typeof raw !== 'string') throw new Error(`comment is required for rejection.`)
    const trimmed = raw.trim()
    if (
      trimmed.length < MIN_REJECT_COMMENT_LENGTH ||
      trimmed.length > MAX_REJECT_COMMENT_LENGTH
    ) {
      throw new Error(`Rejection comment must be between 3 and 1000 characters.`)
    }
    return trimmed
  }

  if (typeof raw !== 'string') throw new Error(`comment is required.`)
  const trimmed = raw.trim()
  if (
    trimmed.length < MIN_GENERAL_COMMENT_LENGTH ||
    trimmed.length > MAX_GENERAL_COMMENT_LENGTH
  ) {
    throw new Error(`comment must be between 1 and 1000 characters.`)
  }
  return trimmed
}

export function validateApprovalRevokeReason(raw: unknown): string {
  if (typeof raw !== 'string') throw new Error(`reason is required.`)
  const trimmed = raw.trim()
  if (
    trimmed.length < MIN_REVOKE_REASON_LENGTH ||
    trimmed.length > MAX_REVOKE_REASON_LENGTH
  ) {
    throw new Error(`Revocation reason must be between 3 and 500 characters.`)
  }
  return trimmed
}

export function validateAdminOnlyKinds(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    throw new Error(`adminOnlyKinds must be an array.`)
  }
  if (raw.length > MAX_ADMIN_ONLY_KINDS) {
    throw new Error(`adminOnlyKinds exceeds maximum of 200 items.`)
  }
  const result: string[] = []
  for (const item of raw) {
    if (typeof item !== 'string' || item.trim().length === 0 || item.trim().length > MAX_KIND_KEY_LENGTH) {
      throw new Error(`Each kind key must be between 1 and 120 characters.`)
    }
    result.push(item.trim())
  }
  return result
}

// Approval State Machine
export function applyDecision(
  approval: OutsideApproval,
  approverId: string,
  decision: OutsideApprovalDecision,
  comment?: string,
  now: number = Date.now(),
): OutsideApproval {
  if (approval.status !== 'SENT') {
    throw new Error(
      `Approval is ${approval.status} and not open for decisions.`,
    )
  }

  const approverIndex = approval.approvers.findIndex(
    (a) => a.approverId === approverId,
  )
  if (approverIndex < 0) {
    throw new Error(`Approver ${approverId} not found on approval.`)
  }

  const approver = approval.approvers[approverIndex]
  if (approver.status !== 'PENDING' && approver.status !== 'VIEWED') {
    throw new Error(
      `Approver is already closed or decided (${approver.status}).`,
    )
  }

  const updatedApprovers = approval.approvers.map((a, idx) => {
    if (idx !== approverIndex) return { ...a }
    return {
      ...a,
      status: decision === 'APPROVE' ? ('APPROVED' as const) : ('REJECTED' as const),
      decidedAt: now,
      ...(comment ? { comment } : {}),
    }
  })

  let nextApprovalStatus: OutsideApprovalStatus = 'SENT'
  let decidedAt: number | undefined

  if (decision === 'REJECT') {
    nextApprovalStatus = 'REJECTED'
    decidedAt = now
    for (let i = 0; i < updatedApprovers.length; i++) {
      if (i !== approverIndex && (updatedApprovers[i].status === 'PENDING' || updatedApprovers[i].status === 'VIEWED')) {
        updatedApprovers[i].status = 'CLOSED'
      }
    }
  } else if (approval.mode === 'ANY') {
    nextApprovalStatus = 'APPROVED'
    decidedAt = now
    for (let i = 0; i < updatedApprovers.length; i++) {
      if (i !== approverIndex && (updatedApprovers[i].status === 'PENDING' || updatedApprovers[i].status === 'VIEWED')) {
        updatedApprovers[i].status = 'CLOSED'
      }
    }
  } else {
    const allApproved = updatedApprovers.every((a) => a.status === 'APPROVED')
    if (allApproved) {
      nextApprovalStatus = 'APPROVED'
      decidedAt = now
    }
  }

  return {
    ...approval,
    status: nextApprovalStatus,
    approvers: updatedApprovers,
    version: approval.version + 1,
    ...(decidedAt ? { decidedAt } : {}),
  }
}

export function approvalStatusAfterExpiry(
  approval: OutsideApproval,
  now: number,
): OutsideApproval {
  if (approval.status !== 'SENT') {
    return approval
  }
  if (approval.expiresAt > now) {
    return approval
  }

  const updatedApprovers = approval.approvers.map((a) => {
    if (a.status === 'PENDING' || a.status === 'VIEWED') {
      return { ...a, status: 'CLOSED' as const }
    }
    return { ...a }
  })

  return {
    ...approval,
    status: 'EXPIRED',
    approvers: updatedApprovers,
    version: approval.version + 1,
  }
}

// ============================================================================
// Imports & Exports (R39 A13.a, SO-P69)
// ============================================================================

export type OpsImportEntity =
  | 'PLACES'
  | 'ASSETS'
  | 'STAFF'
  | 'WORKERS'
  | 'PARTIES'
  | 'STOCK_ITEMS'
  | 'DEPARTURES'
  | 'JOB_SITES'
  | 'PLANT_MACHINES'

export const OPS_IMPORT_ENTITY_KEYS = [
  'PLACES',
  'ASSETS',
  'STAFF',
  'WORKERS',
  'PARTIES',
  'STOCK_ITEMS',
  'DEPARTURES',
  'JOB_SITES',
  'PLANT_MACHINES',
] as const

export type OpsImportErrorCode =
  | 'REQUIRED'
  | 'TOO_LONG'
  | 'BAD_NUMBER'
  | 'BAD_DATE'
  | 'BAD_VALUE'
  | 'DUPLICATE_IN_FILE'
  | 'UNKNOWN_PLACE'
  | 'UNKNOWN_PARTY'
  | 'UNKNOWN_SUPPLIER'
  | 'AMBIGUOUS_SUPPLIER'
  | 'NOT_A_MEMBER'
  | 'ROLE_NOT_ALLOWED'
  | 'QUANTITY_IGNORED'
  | 'BAD_SECTOR'
  | 'CORE_REFUSED'

export const OPS_IMPORT_ERROR_CODES = [
  'REQUIRED',
  'TOO_LONG',
  'BAD_NUMBER',
  'BAD_DATE',
  'BAD_VALUE',
  'DUPLICATE_IN_FILE',
  'UNKNOWN_PLACE',
  'UNKNOWN_PARTY',
  'UNKNOWN_SUPPLIER',
  'AMBIGUOUS_SUPPLIER',
  'NOT_A_MEMBER',
  'ROLE_NOT_ALLOWED',
  'QUANTITY_IGNORED',
  'BAD_SECTOR',
  'CORE_REFUSED',
] as const

export const OPS_IMPORT_FILE_REFUSAL_REASONS = [
  'TOO_LARGE',
  'TOO_MANY_ROWS',
  'TOO_MANY_COLUMNS',
  'CELL_TOO_LONG',
  'UNTERMINATED_QUOTE',
  'PARSE_ERROR',
  'MISSING_COLUMNS',
  'ROW_ERRORS',
  'OPID_REUSED',
  'EXPORT_TOO_LARGE',
  'NO_PLACE_LEVEL',
] as const

export type OpsImportFileRefusalReason =
  (typeof OPS_IMPORT_FILE_REFUSAL_REASONS)[number]

export interface OpsImportFileRefusalDetails {
  reason: OpsImportFileRefusalReason
  columns?: string[]
  count?: number
  [key: string]: unknown
}

export interface OpsImportColumnDef {
  key: string
  required: boolean
  type: 'string' | 'number' | 'date' | 'enum' | 'list'
  maxLength?: number
  allowedValues?: readonly string[]
  aliases: readonly string[]
  readOnly?: boolean
}

export interface OpsImportEntityDef {
  entity: OpsImportEntity
  matchKey: string
  sectorRequired: boolean
  permissions: string
  columns: readonly OpsImportColumnDef[]
  supportsSector?: (sector: OpsSector) => boolean
}

export const OPS_IMPORT_ENTITIES: Record<OpsImportEntity, OpsImportEntityDef> = {
  PLACES: {
    entity: 'PLACES',
    matchKey: 'path',
    sectorRequired: true,
    permissions: 'Owner, Admin, or Editor who is sector manager',
    supportsSector: (sector: OpsSector) => (OPS_SECTORS[sector]?.rootLevels.length ?? 0) > 0,
    columns: [
      {
        key: 'path',
        required: true,
        type: 'string',
        maxLength: 200,
        aliases: ['place_path', 'full_path', 'location', 'name', 'place_name'],
      },
      {
        key: 'code',
        required: false,
        type: 'string',
        maxLength: 40,
        aliases: ['place_code', 'short_code'],
      },
      {
        key: 'id',
        required: false,
        type: 'string',
        readOnly: true,
        aliases: ['place_id'],
      },
      {
        key: 'level',
        required: false,
        type: 'string',
        maxLength: 40,
        aliases: ['place_level', 'type'],
      },
      {
        key: 'department',
        required: false,
        type: 'string',
        maxLength: 80,
        aliases: ['department_key', 'department_name', 'dept'],
      },
      {
        key: 'siteKind',
        required: false,
        type: 'string',
        maxLength: 40,
        aliases: ['site_kind', 'kind', 'site_type'],
      },
      {
        key: 'preset',
        required: false,
        type: 'string',
        maxLength: 60,
        aliases: ['preset_name', 'preset_type', 'preset_code'],
      },
    ],
  },
  ASSETS: {
    entity: 'ASSETS',
    matchKey: 'assetCode',
    sectorRequired: false,
    permissions: 'Owner, Admin, or Editor who is sector manager',
    supportsSector: (sector: OpsSector) => isAssetSector(sector),
    columns: [
      {
        key: 'assetCode',
        required: true,
        type: 'string',
        maxLength: 40,
        aliases: ['code', 'asset_code', 'asset_tag', 'tag'],
      },
      {
        key: 'name',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['asset_name', 'title'],
      },
      {
        key: 'category',
        required: false,
        type: 'string',
        maxLength: 100,
        aliases: ['asset_category', 'type'],
      },
      {
        key: 'model',
        required: false,
        type: 'string',
        maxLength: 100,
        aliases: ['asset_model'],
      },
      {
        key: 'serial',
        required: false,
        type: 'string',
        maxLength: 100,
        aliases: ['serial_number', 'serial_no', 'serialnum'],
      },
      {
        key: 'criticality',
        required: true,
        type: 'enum',
        allowedValues: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'],
        aliases: ['crit', 'priority'],
      },
      {
        key: 'place',
        required: true,
        type: 'string',
        maxLength: 200,
        aliases: ['place_path', 'location', 'place_id'],
      },
      {
        key: 'vendor',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['vendor_name', 'vendor_party', 'supplier'],
      },
      {
        key: 'warrantyUntil',
        required: false,
        type: 'date',
        aliases: ['warranty_until', 'warranty_date', 'warranty_expiry'],
      },
      {
        key: 'amcUntil',
        required: false,
        type: 'date',
        aliases: ['amc_until', 'amc_date', 'amc_expiry'],
      },
      {
        key: 'registrationKind',
        required: false,
        type: 'enum',
        allowedValues: [...ASSET_REGISTRATION_KINDS],
        aliases: ['reg_kind', 'registration_kind', 'licence_kind', 'license_kind'],
      },
      {
        key: 'registrationNumber',
        required: false,
        type: 'string',
        maxLength: 60,
        aliases: [
          'reg_number',
          'registration_number',
          'reg_no',
          'licence_number',
          'licence_no',
          'license_number',
        ],
      },
      {
        key: 'registrationValidUntil',
        required: false,
        type: 'date',
        aliases: [
          'reg_valid_until',
          'registration_valid_until',
          'registration_expiry',
          'licence_expiry',
          'license_expiry',
        ],
      },
      {
        key: 'id',
        required: false,
        type: 'string',
        readOnly: true,
        aliases: ['asset_id'],
      },
      {
        key: 'status',
        required: false,
        type: 'enum',
        allowedValues: ['AVAILABLE', 'IN_USE', 'ISOLATED', 'MAINTENANCE', 'RETIRED'],
        readOnly: true,
        aliases: ['asset_status'],
      },
      {
        key: 'createdAt',
        required: false,
        type: 'number',
        readOnly: true,
        aliases: ['created_at'],
      },
    ],
  },
  STAFF: {
    entity: 'STAFF',
    matchKey: 'email',
    sectorRequired: false,
    permissions: 'Admin only',
    columns: [
      {
        key: 'email',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['user_email', 'member_email'],
      },
      {
        key: 'roles',
        required: false,
        type: 'list',
        allowedValues: OPS_SECTOR_ROLES,
        aliases: ['sector_roles', 'role_keys', 'roles'],
      },
      {
        key: 'uid',
        required: false,
        type: 'string',
        readOnly: true,
        aliases: ['user_id', 'member_uid'],
      },
    ],
  },
  WORKERS: {
    entity: 'WORKERS',
    matchKey: 'workerCode',
    sectorRequired: true,
    permissions: 'Owner, Admin, or Editor who is sector manager',
    columns: [
      {
        key: 'workerCode',
        required: false,
        type: 'string',
        maxLength: 40,
        aliases: ['worker_code', 'code', 'worker_id', 'badge_number'],
      },
      {
        key: 'displayName',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['name', 'worker_name', 'full_name'],
      },
      {
        key: 'party',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['party_name', 'employer', 'contractor', 'vendor'],
      },
      {
        key: 'trade',
        required: true,
        type: 'string',
        maxLength: 100,
        aliases: ['skill', 'role', 'occupation', 'trade_name'],
      },
      {
        key: 'phone',
        required: false,
        type: 'string',
        maxLength: 40,
        aliases: ['phone_number', 'mobile', 'contact_number'],
      },
      {
        key: 'skills',
        required: false,
        type: 'list',
        aliases: ['skills_list', 'certifications'],
      },
      {
        key: 'id',
        required: false,
        type: 'string',
        readOnly: true,
        aliases: ['worker_id_pk'],
      },
      {
        key: 'status',
        required: false,
        type: 'string',
        readOnly: true,
        aliases: ['worker_status'],
      },
    ],
  },
  PARTIES: {
    entity: 'PARTIES',
    matchKey: 'externalRef',
    sectorRequired: true,
    permissions: 'Owner, Admin, or Editor who is sector manager',
    columns: [
      {
        key: 'name',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['party_name', 'company_name', 'vendor_name'],
      },
      {
        key: 'kind',
        required: true,
        type: 'enum',
        allowedValues: ['CONTRACTOR', 'VENDOR', 'CLIENT', 'CONSULTANT'],
        aliases: ['party_kind', 'type'],
      },
      {
        key: 'externalRef',
        required: false,
        type: 'string',
        maxLength: 60,
        aliases: ['external_ref', 'ref', 'code'],
      },
      {
        key: 'contactName',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['contact_name', 'contact_person'],
      },
      {
        key: 'contactEmail',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['email', 'contact_email'],
      },
      {
        key: 'contactPhone',
        required: false,
        type: 'string',
        maxLength: 40,
        aliases: ['phone', 'contact_phone'],
      },
      {
        key: 'contactAddress',
        required: false,
        type: 'string',
        maxLength: 200,
        aliases: ['address', 'office_address'],
      },
      {
        key: 'id',
        required: false,
        type: 'string',
        readOnly: true,
        aliases: ['party_id'],
      },
    ],
  },
  STOCK_ITEMS: {
    entity: 'STOCK_ITEMS',
    matchKey: 'sku',
    sectorRequired: false,
    permissions: 'Stock manager / Editor or Admin',
    columns: [
      {
        key: 'sku',
        required: true,
        type: 'string',
        maxLength: 60,
        aliases: ['item_code', 'code', 'part_number'],
      },
      {
        key: 'name',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['item_name', 'title', 'product_name'],
      },
      {
        key: 'unit',
        required: true,
        type: 'string',
        maxLength: 40,
        aliases: ['uom', 'unit_of_measure'],
      },
      {
        key: 'category',
        required: false,
        type: 'string',
        maxLength: 100,
        aliases: ['item_category', 'group'],
      },
      {
        key: 'location',
        required: false,
        type: 'string',
        maxLength: 100,
        aliases: ['stock_location', 'bin', 'warehouse'],
      },
      {
        key: 'costPerUnit',
        required: false,
        type: 'number',
        aliases: ['cost', 'unit_cost', 'rate', 'price'],
      },
      {
        key: 'minThreshold',
        required: false,
        type: 'number',
        aliases: ['min_quantity', 'min_stock'],
      },
      {
        key: 'openingQuantity',
        required: false,
        type: 'number',
        aliases: ['quantity', 'initial_quantity', 'qty'],
      },
      {
        key: 'supplier',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['supplier_name', 'vendor'],
      },
      {
        key: 'barcode',
        required: false,
        type: 'string',
        maxLength: 64,
        aliases: ['ean', 'upc'],
      },
      {
        key: 'id',
        required: false,
        type: 'string',
        readOnly: true,
        aliases: ['stock_item_id'],
      },
      {
        key: 'currentQuantity',
        required: false,
        type: 'number',
        readOnly: true,
        aliases: ['in_stock', 'current_stock'],
      },
    ],
  },
  DEPARTURES: {
    entity: 'DEPARTURES',
    matchKey: 'room',
    sectorRequired: true,
    permissions: 'Owner, Admin, or FRONT_DESK',
    supportsSector: (sector: OpsSector) => sector === 'stays',
    columns: [
      {
        key: 'room',
        required: true,
        type: 'string',
        maxLength: 60,
        aliases: ['room_number', 'room_no', 'room', 'room_name', 'unit'],
      },
      {
        key: 'departure_time',
        required: true,
        type: 'string',
        maxLength: 80,
        aliases: [
          'departure',
          'departure_time',
          'depart_time',
          'dep_time',
          'check_out',
          'checkout_time',
          'checkout',
        ],
      },
      {
        key: 'property',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['property_name', 'property', 'hotel', 'building'],
      },
    ],
  },
  JOB_SITES: {
    entity: 'JOB_SITES',
    matchKey: 'phone',
    sectorRequired: true,
    permissions: 'Owner, Admin, or JOB_DISPATCHER',
    supportsSector: (sector: OpsSector) => sector === 'jobs',
    columns: [
      {
        key: 'customer_name',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['customer_name', 'customer', 'client', 'name'],
      },
      {
        key: 'phone',
        required: true,
        type: 'string',
        maxLength: 30,
        aliases: ['phone', 'mobile', 'cell', 'phone_number', 'contact_number'],
      },
      {
        key: 'email',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['email', 'email_address'],
      },
      {
        key: 'site_name',
        required: true,
        type: 'string',
        maxLength: 80,
        aliases: ['site_name', 'site', 'location', 'property'],
      },
      {
        key: 'line1',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['line1', 'address_line1', 'address1', 'street', 'address'],
      },
      {
        key: 'line2',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['line2', 'address_line2', 'address2', 'suite', 'apt'],
      },
      {
        key: 'locality',
        required: false,
        type: 'string',
        maxLength: 60,
        aliases: ['locality', 'suburb', 'neighbourhood', 'area'],
      },
      {
        key: 'city',
        required: true,
        type: 'string',
        maxLength: 60,
        aliases: ['city', 'town'],
      },
      {
        key: 'region',
        required: false,
        type: 'string',
        maxLength: 60,
        aliases: ['region', 'state', 'province'],
      },
      {
        key: 'postcode',
        required: false,
        type: 'string',
        maxLength: 12,
        aliases: ['postcode', 'zip', 'postal_code', 'pin', 'pincode'],
      },
      {
        key: 'service_area',
        required: false,
        type: 'string',
        maxLength: 80,
        aliases: ['service_area', 'servicearea', 'territory', 'zone'],
      },
    ],
  },
  PLANT_MACHINES: {
    entity: 'PLANT_MACHINES',
    matchKey: 'machine',
    sectorRequired: true,
    permissions: 'Owner, Admin, or Editor',
    supportsSector: (sector: OpsSector) => sector === 'plant',
    columns: [
      {
        key: 'plant',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['plant', 'plant_name', 'factory'],
      },
      {
        key: 'floor',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['floor', 'floor_name'],
      },
      {
        key: 'line',
        required: false,
        type: 'string',
        maxLength: 120,
        aliases: ['line', 'line_name', 'production_line'],
      },
      {
        key: 'machine',
        required: true,
        type: 'string',
        maxLength: 120,
        aliases: ['machine', 'machine_name', 'name'],
      },
      {
        key: 'code',
        required: false,
        type: 'string',
        maxLength: 40,
        aliases: ['code', 'machine_code', 'asset_code'],
      },
      {
        key: 'criticality',
        required: false,
        type: 'enum',
        allowedValues: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'],
        aliases: ['criticality', 'crit'],
      },
      {
        key: 'pm_interval_days',
        required: false,
        type: 'number',
        aliases: ['pm_interval_days', 'pm_interval', 'pm_days', 'pmintervaldays'],
      },
    ],
  },
}

export function importEntitySupportsSector(entity: OpsImportEntity, sector: OpsSector): boolean {
  const entityDef = OPS_IMPORT_ENTITIES[entity]
  if (!entityDef) return false
  if (typeof entityDef.supportsSector === 'function') {
    return entityDef.supportsSector(sector)
  }
  return true
}

export interface OpsImportRowError {
  rowNumber: number
  field: string
  code: OpsImportErrorCode
  message: string
  httpCode?: string
}

export type OpsImportJobStatus = 'RUNNING' | 'DONE' | 'PARTIAL'

export interface OpsImportJobRowResult {
  rowNumber: number
  status: 'COMPLETED' | 'ERROR' | 'SKIPPED'
  action?: 'CREATE' | 'UPDATE' | 'UNCHANGED'
  id?: string
  error?: string
  code?: OpsImportErrorCode
  httpCode?: string
}

export interface OpsImportJobDoc {
  workspaceId: string
  opId: string
  entity: OpsImportEntity
  sector?: OpsSector
  fileHash?: string
  status: OpsImportJobStatus
  totalRows: number
  validRows: number
  counts: {
    create: number
    update: number
    unchanged: number
    errors: number
  }
  rowResults: OpsImportJobRowResult[]
  createdBy: string
  createdAt: number
  updatedAt: number
  completedAt?: number
  error?: string
}

export interface ImportOpsCsvInput {
  workspaceId: string
  sector?: OpsSector
  entity: OpsImportEntity
  csvText: string
  mapping?: Record<number, string>
  dryRun: boolean
  opId?: string
}

export interface ImportOpsCsvResult {
  totalRows: number
  validRows: number
  counts: {
    create: number
    update: number
    unchanged: number
    errors: number
  }
  errors: OpsImportRowError[]
  preview: Array<Record<string, string>>
  jobId?: string
  status?: OpsImportJobStatus
  replayed?: boolean
}

export interface ExportOpsCsvInput {
  workspaceId: string
  sector?: OpsSector
  entity: OpsImportEntity
  placeId?: string
}

export interface ExportOpsCsvResult {
  csvText: string
  rowCount: number
  entity: OpsImportEntity
}

/* =======================================================================
   CSV Parser and Header Auto-Mapping (R39 A13.b, SO-P71 client mirror)
   ======================================================================= */

export class CsvParseError extends Error {
  readonly reason: OpsImportFileRefusalReason
  constructor(reason: OpsImportFileRefusalReason, message: string) {
    super(message)
    this.name = 'CsvParseError'
    this.reason = reason
    Object.setPrototypeOf(this, CsvParseError.prototype)
  }
}

export interface CsvParseLimits {
  maxByteLength?: number
  maxDataRows?: number
  maxColumns?: number
  maxCellLength?: number
}

export interface CsvParsedRow {
  rowNumber: number
  cells: string[]
}

export interface CsvParseResult {
  header: string[]
  rows: CsvParsedRow[]
}

export const DEFAULT_CSV_LIMITS: Required<CsvParseLimits> = {
  maxByteLength: 1024 * 1024,
  maxDataRows: 2000,
  maxColumns: 64,
  maxCellLength: 1000,
}

export function parseCsvText(
  text: string,
  limits: CsvParseLimits = {},
): CsvParseResult {
  if (text === null || text === undefined || typeof text !== 'string') {
    throw new CsvParseError('PARSE_ERROR', 'CSV text must be a string.')
  }

  const effectiveLimits = { ...DEFAULT_CSV_LIMITS, ...limits }

  const byteLength =
    typeof Buffer !== 'undefined'
      ? Buffer.byteLength(text, 'utf8')
      : new TextEncoder().encode(text).length
  if (byteLength > effectiveLimits.maxByteLength) {
    throw new CsvParseError(
      'TOO_LARGE',
      `CSV text exceeds 1 MB limit (${byteLength} > ${effectiveLimits.maxByteLength} bytes).`,
    )
  }

  let cleanText = text
  if (cleanText.charCodeAt(0) === 0xfeff) {
    cleanText = cleanText.slice(1)
  }

  if (cleanText.trim().length === 0) {
    return { header: [], rows: [] }
  }

  const allRows: CsvParsedRow[] = []
  let currentRow: string[] = []
  let currentCell = ''
  let inQuotes = false
  let currentLineNumber = 1
  let rowStartLineNumber = 1

  const finishCell = () => {
    if (currentCell.length > effectiveLimits.maxCellLength) {
      throw new CsvParseError(
        'CELL_TOO_LONG',
        `Cell at row ${rowStartLineNumber}, column ${currentRow.length + 1} exceeds max length of 1,000 characters.`,
      )
    }
    currentRow.push(currentCell)
    currentCell = ''
    if (currentRow.length > effectiveLimits.maxColumns) {
      throw new CsvParseError(
        'TOO_MANY_COLUMNS',
        `Row ${rowStartLineNumber} exceeds max columns limit of ${effectiveLimits.maxColumns} columns.`,
      )
    }
  }

  const finishRow = () => {
    finishCell()
    const isBlankRow = currentRow.length === 1 && currentRow[0].trim() === ''
    if (!isBlankRow) {
      allRows.push({
        rowNumber: rowStartLineNumber,
        cells: currentRow,
      })
    }
    currentRow = []
  }

  for (let i = 0; i < cleanText.length; i++) {
    const ch = cleanText[i]

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < cleanText.length && cleanText[i + 1] === '"') {
          currentCell += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        currentCell += ch
        if (ch === '\n') {
          currentLineNumber++
        }
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === ',') {
        finishCell()
      } else if (ch === '\r') {
        if (i + 1 < cleanText.length && cleanText[i + 1] === '\n') {
          i++
        }
        finishRow()
        currentLineNumber++
        rowStartLineNumber = currentLineNumber
      } else if (ch === '\n') {
        finishRow()
        currentLineNumber++
        rowStartLineNumber = currentLineNumber
      } else {
        currentCell += ch
      }
    }
  }

  if (inQuotes) {
    throw new CsvParseError(
      'UNTERMINATED_QUOTE',
      `Unterminated quoted field at row ${rowStartLineNumber}.`,
    )
  }

  if (currentRow.length > 0 || currentCell.length > 0) {
    finishRow()
  }

  if (allRows.length === 0) {
    return { header: [], rows: [] }
  }

  const header = allRows[0].cells.map((h) => h.trim())
  if (header.length > effectiveLimits.maxColumns) {
    throw new CsvParseError(
      'TOO_MANY_COLUMNS',
      `Header exceeds max columns limit of ${effectiveLimits.maxColumns} columns.`,
    )
  }

  const dataRows = allRows.slice(1)
  if (dataRows.length > effectiveLimits.maxDataRows) {
    throw new CsvParseError(
      'TOO_MANY_ROWS',
      `CSV data exceeds 2,000 data rows limit (${dataRows.length} rows).`,
    )
  }

  return {
    header,
    rows: dataRows,
  }
}

export function normalizeHeaderKey(raw: string): string {
  return raw.toLowerCase().replace(/[\s_-]+/g, '')
}

export function autoMapHeaders(
  headers: string[],
  entity: OpsImportEntity,
): { mapping: Record<number, string>; unmappedRequired: string[] } {
  const entityDef = OPS_IMPORT_ENTITIES[entity]
  if (!entityDef) {
    throw new Error(`Unknown import entity "${entity}".`)
  }

  const mapping: Record<number, string> = {}
  const mappedKeys = new Set<string>()

  for (let i = 0; i < headers.length; i++) {
    const norm = normalizeHeaderKey(headers[i])
    if (!norm) continue

    for (const col of entityDef.columns) {
      if (mappedKeys.has(col.key)) continue
      const normKey = normalizeHeaderKey(col.key)
      const matchesKey = norm === normKey
      const matchesAlias = col.aliases.some((a) => normalizeHeaderKey(a) === norm)

      if (matchesKey || matchesAlias) {
        mapping[i] = col.key
        mappedKeys.add(col.key)
        break
      }
    }
  }

  const unmappedRequired: string[] = []
  for (const col of entityDef.columns) {
    if (col.required && !mappedKeys.has(col.key)) {
      unmappedRequired.push(col.key)
    }
  }

  return { mapping, unmappedRequired }
}

export function applyMapping(
  cells: string[],
  mapping: Record<number, string>,
): Record<string, string> {
  const rowObj: Record<string, string> = {}
  for (let i = 0; i < cells.length; i++) {
    const colKey = mapping[i]
    if (colKey) {
      rowObj[colKey] = cells[i].trim()
    }
  }
  return rowObj
}

// ---------------------------------------------------------------------------
// R39 G6 — Operational Wage Sheet (mirror of functions-common/src/ops/wages.ts)
// ---------------------------------------------------------------------------

import type {
  OpsWagePersonRef,
  OpsWageRateEntry,
  OpsWageRateDoc,
  OpsWageSheetRow,
  OpsWageSheetFlag,
  OpsWageSheetTotals,
  OpsWageAttendanceInput,
  OpsWageShiftInput,
  OpsWageClockEntry,
  BuildWageSheetRowsParams,
} from '../../functions-common/src/ops/wages'

export type {
  OpsWagePersonRef,
  OpsWageRateEntry,
  OpsWageRateDoc,
  OpsWageSheetRow,
  OpsWageSheetFlag,
  OpsWageSheetTotals,
  OpsWageAttendanceInput,
  OpsWageShiftInput,
  OpsWageClockEntry,
  BuildWageSheetRowsParams,
}

export {
  computeWorkedMinutes,
  overtimeMinutes,
  dayFraction,
  rateOn,
  buildWageSheetRows,
  buildWageSheetTotals,
  buildWageSheetCsv,
  computeAttendanceFingerprint,
} from '../../functions-common/src/ops/wages'

export interface OpsWageSheetDoc {
  id: string
  workspaceId: string
  periodStart: string
  periodEnd: string
  partyId?: string | null
  state: 'LOCKED' | 'DRAFT'
  currency: string
  rows: OpsWageSheetRow[]
  totals: OpsWageSheetTotals
  attendanceFingerprint: string
  lockedBy?: string
  lockedAt?: number
  unlockHistory?: Array<{ by: string; at: number; reason: string }>
  previousRows?: OpsWageSheetRow[]
  createdAt?: unknown
  updatedAt?: unknown
}

export interface SetOpsWageRateInput {
  workspaceId: string
  personRef: OpsWagePersonRef
  effectiveFrom: string
  dayRateMinor: number
  otHourRateMinor: number
  opId?: string | null
}

export interface SetOpsWageRateResult {
  success: boolean
  personKey: string
  effectiveFrom: string
  dayRateMinor: number
  otHourRateMinor: number
  opId?: string | null
  replayed?: boolean
}

export interface PreviewOpsWageSheetInput {
  workspaceId: string
  periodStart: string
  periodEnd: string
  partyId?: string | null
}

export interface PreviewOpsWageSheetResult {
  rows: OpsWageSheetRow[]
  totals: OpsWageSheetTotals
  currency: string
  periodStart: string
  periodEnd: string
  partyId?: string | null
}

export interface LockOpsWageSheetInput {
  workspaceId: string
  periodStart: string
  periodEnd: string
  partyId?: string | null
  opId?: string | null
}

export interface LockOpsWageSheetResult {
  success: boolean
  sheetId: string
  state: 'LOCKED'
  attendanceFingerprint: string
  totals: OpsWageSheetTotals
  opId?: string | null
  replayed?: boolean
}

export interface UnlockOpsWageSheetInput {
  workspaceId: string
  sheetId: string
  reason: string
  opId?: string | null
}

export interface UnlockOpsWageSheetResult {
  success: boolean
  sheetId: string
  state: 'DRAFT'
  opId?: string | null
  replayed?: boolean
}

export interface ExportOpsWageSheetCsvInput {
  workspaceId: string
  periodStart: string
  periodEnd: string
  partyId?: string | null
  sheetId?: string | null
}

export interface ExportOpsWageSheetCsvResult {
  success: boolean
  csv: string
  filename: string
  locked: boolean
}

export interface GetOpsWageSheetStatusInput {
  workspaceId: string
  sheetId: string
}

export interface GetOpsWageSheetStatusResult {
  changedSinceLock: boolean
  state: 'LOCKED' | 'DRAFT'
  sheetId: string
  lockedAt?: number
  lockedBy?: string
}

import type {
  RequestEvidenceExportInput,
  LegalHoldTarget,
} from '../../functions-common/src/ops/evidence'

// ---------------------------------------------------------------------------
// QR Attendance & Peer Proxy Pure Helpers & Types (R39 F3 / SO-P109)
// ---------------------------------------------------------------------------

/**
 * Returns false for PENDING or REJECTED peer marks.
 * Used by all attendance readers (wages, relief planning, coverage, UI).
 */
export function isAttendanceEffective(
  doc?: { source?: string | null; proxyState?: string | null } | null,
): boolean {
  if (!doc) return false
  if (doc.proxyState === 'PENDING' || doc.proxyState === 'REJECTED') {
    return false
  }
  if (doc.source === 'PROXY_PEER' && doc.proxyState !== 'APPROVED') {
    return false
  }
  return true
}

/**
 * Quote token format used by client quotes (/q/<48 hex>).
 * Place stickers use 22-char base64url tokens.
 */
export const QUOTE_TOKEN_REGEX = /^[0-9a-f]{48}$/

export function isQuoteToken(token: unknown): boolean {
  return typeof token === 'string' && QUOTE_TOKEN_REGEX.test(token.trim().toLowerCase())
}

/**
 * Determines check-in attendance status (PRESENT or LATE).
 * Exactly start + graceMinutes = PRESENT; after start + graceMinutes = LATE.
 */
export function selfCheckInStatus(
  now: Date | number,
  shiftStart: Date | number,
  graceMinutes: number = 15,
): 'PRESENT' | 'LATE' {
  const nowMs = typeof now === 'number' ? now : now.getTime()
  const startMs = typeof shiftStart === 'number' ? shiftStart : shiftStart.getTime()
  const graceMs = Math.max(0, graceMinutes) * 60 * 1000
  return nowMs <= startMs + graceMs ? 'PRESENT' : 'LATE'
}

export interface OpsSelfCheckInMatch {
  assignment: OpsRosterAssignment
  shift: OpsShift
  dayKey: string
  shiftStartMs: number
  shiftEndMs: number
  windowStartMs: number
  windowEndMs: number
}

export interface FindSelfCheckInAssignmentOptions {
  now: Date | number
  assignments: OpsRosterAssignment[]
  shifts: OpsShift[]
  personRef: OpsPersonRef
  timeZone?: string
}

/**
 * Finds the assignment in the latest published roster covering now.
 * Shift window: start - 60 min to end.
 * Overnight shifts on their start day, checking today's and yesterday's dayKey.
 */
export function findSelfCheckInAssignment(
  options: FindSelfCheckInAssignmentOptions,
): OpsSelfCheckInMatch | null {
  const nowMs = typeof options.now === 'number' ? options.now : options.now.getTime()
  const tz = resolveWorkspaceTimeZone(options.timeZone)
  const todayKey = formatZonedDateKey(new Date(nowMs), tz)
  const yesterdayKey = addDaysToKey(todayKey, -1)
  const allowedDayKeys = new Set([todayKey, yesterdayKey])

  const shiftMap = new Map<string, OpsShift>()
  for (const s of options.shifts) {
    if (!s.archived) {
      shiftMap.set(s.id, s)
    }
  }

  const candidates: OpsSelfCheckInMatch[] = []
  for (const a of options.assignments) {
    if (a.kind !== 'SHIFT') continue
    if (!allowedDayKeys.has(a.dayKey)) continue
    if (!isSamePersonRef(a.personRef, options.personRef)) continue

    const shift = shiftMap.get(a.shiftId)
    if (!shift) continue

    const shiftStartMs = getZonedMinuteEpoch(a.dayKey, shift.startMinute, tz)
    const windowStartMs = shiftStartMs - 60 * 60 * 1000

    const endMinute =
      typeof shift.endMinute === 'number'
        ? shift.endMinute
        : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
    const crossesMidnight = endMinute <= shift.startMinute
    const endDayKey = crossesMidnight ? addDaysToKey(a.dayKey, 1) : a.dayKey
    const shiftEndMs = getZonedMinuteEpoch(endDayKey, endMinute, tz)
    const windowEndMs = shiftEndMs

    if (nowMs >= windowStartMs && nowMs <= windowEndMs) {
      candidates.push({
        assignment: a,
        shift,
        dayKey: a.dayKey,
        shiftStartMs,
        shiftEndMs,
        windowStartMs,
        windowEndMs,
      })
    }
  }

  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0]

  candidates.sort((a, b) => {
    const aActive = nowMs >= a.shiftStartMs && nowMs <= a.shiftEndMs
    const bActive = nowMs >= b.shiftStartMs && nowMs <= b.shiftEndMs
    if (aActive && !bActive) return -1
    if (!aActive && bActive) return 1
    return Math.abs(nowMs - a.shiftStartMs) - Math.abs(nowMs - b.shiftStartMs)
  })

  return candidates[0]
}

/* Callables input/result types for QR attendance & peer proxy */

export type PlaceScanCallerState =
  | 'NOT_ROSTERED'
  | 'CAN_CHECK_IN'
  | 'CHECKED_IN'
  | 'CHECKED_OUT'
  | 'MARKED_BY_OTHER'

export interface PlaceScanColleague {
  personRef: OpsPersonRef
  displayName: string
}

export interface PlaceScanContextPlace {
  id: string
  name: string
  sector: OpsSector
  level: OpsPlaceLevel
  attendanceGraceMinutes?: number | null
}

export interface PlaceScanContextShift {
  id: string
  name: string
  code: string
  startMinute: number
  endMinute: number
}

export interface GetPlaceScanContextInput {
  token: string
}

export interface PlaceScanContextResult {
  place: PlaceScanContextPlace
  isShopsOutlet: boolean
  isStaysRoom?: boolean
  staysRoomContext?: {
    roomState: OpsRoomState
    allowedActions: Array<'CHECK_OUT' | 'START_CLEAN' | 'PAUSE_CLEAN' | 'RESUME_CLEAN' | 'CLEANED'>
  }
  callerState?: PlaceScanCallerState
  shift?: PlaceScanContextShift
  dayKey?: string
  actualIn?: string | null
  actualOut?: string | null
  colleagues?: PlaceScanColleague[]
}

export interface CheckInByPlaceQrInput {
  token: string
  opId?: string
}

export interface CheckInByPlaceQrResult {
  success: boolean
  action: 'CHECK_IN' | 'CHECK_OUT'
  status?: OpsAttendanceStatus
  actualIn?: string | null
  actualOut?: string | null
  shiftId: string
  dayKey: string
  replayed?: boolean
}

export interface ProxyMarkByColleagueInput {
  token: string
  personRef: OpsPersonRef
  opId?: string
}

export interface ProxyMarkByColleagueResult {
  success: boolean
  attendanceId: string
  replayed?: boolean
}

export interface DecidePeerProxyMarkInput {
  attendanceId: string
  approve: boolean
  reason?: string
  opId?: string
}

export interface DecidePeerProxyMarkResult {
  success: boolean
  attendanceId: string
  proxyState: 'APPROVED' | 'REJECTED'
  replayed?: boolean
}

/* R39 A11.a Evidence export, retention, and legal hold mirrors (SO-P73) */

export {
  SO_P9_RESTRICTED_KINDS,
  type EvidenceSourceCollection,
  type EvidenceSourceDef,
  OPS_EVIDENCE_SOURCES,
  OPS_EVIDENCE_EXCLUSIONS,
  type RetentionStarterClass,
  OPS_RETENTION_STARTERS,
  isRestrictedEvidenceSourceItem,
  getEvidenceKindKey,
  buildTemplateEvidenceKindKey,
  buildRequestEvidenceKindKey,
  canCallerReadRestrictedEvidenceKind,
  sanitizeEvidenceFields,
  type EvidenceExportScope,
  type RequestEvidenceExportInput,
  type GetEvidenceExportDownloadInput,
  type RetentionClassConfig,
  type SetRetentionSettingsInput,
  type LegalHoldTargetType,
  type LegalHoldTarget,
  type SetLegalHoldInput,
  type RequestLegalHoldLiftInput,
  type ConfirmLegalHoldLiftInput,
  type CancelLegalHoldLiftInput,
  type RestoreFromRetentionBinInput,
  validateRequestEvidenceExportInput,
  validateGetEvidenceExportDownloadInput,
  validateSetRetentionSettingsInput,
  validateSetLegalHoldInput,
  validateRequestLegalHoldLiftInput,
  validateConfirmLegalHoldLiftInput,
  validateCancelLegalHoldLiftInput,
  validateRestoreFromRetentionBinInput,
} from '../../functions-common/src/ops/evidence'

/* R39 Stays K2.a / K2.b Room states and transition table mirrors (SO-P113) */

export {
  OPS_ROOM_OCCUPANCIES,
  type OpsRoomOccupancy,
  OPS_ROOM_HK_STATES,
  type OpsRoomHk,
  OPS_ROOM_SERVICE_STATES,
  type OpsRoomService,
  OPS_ROOM_SOURCES,
  type OpsRoomSource,
  workspaceTodayKey,
  resolveWorkspaceTimeZone,
  formatZonedDateKey,
  DEFAULT_ROOM_CLEAN_PHOTOS,
  OPS_ROOM_PAUSE_REASONS,
  type OpsRoomPauseReason,
  type OpsRoomGeoPoint,
  type OpsRoomState,
  createDefaultRoomState,
  type OpsRoomAction,
  type OpsRoomEventStateSnapshot,
  type OpsRoomEvent,
  type TransitionRoomStateOptions,
  type TransitionRoomStateResult,
  transitionRoomState,
  allowedRoomActions,
  type AllowedRoomActionsCaller,
  type AllowedRoomActionType,
  type PrepareRoomCleanRecordInput,
  type PrepareRoomCleanRecordResult,
  type CheckOutRoomInput,
  type MarkRoomOccupiedInput,
  type SetRoomDueOutInput,
  type StartRoomCleanInput,
  type MarkRoomCleanedInput,
  type PauseRoomCleanInput,
  type ResumeRoomCleanInput,
  type StaysHousekeepingReportInput,
  type StaysHousekeepingReportRow,
  type StaysHousekeepingReportResult,
  type AssignRoomsInput,
  type UnassignRoomInput,
  type ReassignRoomInput,
  type InspectRoomInput,
  type SetRoomServiceInput,
  type SetRoomTagsInput,
  type TransitionRoomStateInput,
  type TransitionRoomStateResultPayload,
} from '../../functions-common/src/ops/roomStates'
import type {
  OpsBedActionType,
  OpsBedState,
  OpsBedStateEvent,
} from '../../functions-common/src/ops/bedStates'

export {
  OPS_BED_STATES,
  type OpsBedStateValue,
  type OpsBedActor,
  type OpsBedAction,
  type OpsBedActionType,
  type OpsBedState,
  type OpsBedStateEvent,
  type OpsBedStateErrorCode,
  BedStateTransitionError,
  createDefaultBedState,
  type TransitionBedStateResult,
  transitionBedState,
} from '../../functions-common/src/ops/bedStates'

export interface SetBedStateInput {
  workspaceId: string
  placeId: string
  action: OpsBedActionType
  reason?: string
  opId?: string
}

export interface SetBedStateResult {
  success: boolean
  bedState: OpsBedState
  requestId?: string
  replayed?: boolean
}

export interface ListBedEventsInput {
  workspaceId: string
  placeId: string
  limit?: number
  cursor?: string
}

export interface ListBedEventsResult {
  events: OpsBedStateEvent[]
  nextCursor?: string
}
export type OpsExportState = 'QUEUED' | 'RUNNING' | 'READY' | 'FAILED' | 'EXPIRED'

export interface OpsExportProgress {
  source?: string
  cursor?: string
  records?: number
  files?: number
  bytes?: number
}

export interface OpsExportPart {
  path: string
  bytes: number
  sha256: string
}

export interface OpsEvidenceExportDoc {
  id: string
  workspaceId: string
  requestedBy: string
  requestedByName?: string
  scope: RequestEvidenceExportInput['scope']
  state: OpsExportState
  progress?: OpsExportProgress
  parts?: OpsExportPart[]
  manifestPath?: string
  signaturePath?: string
  keyVersion?: string
  manifestSha256?: string
  countsBySource?: Record<string, number>
  totalCorrections?: number
  error?: string
  createdAt?: unknown
  readyAt?: unknown
  expiresAt?: unknown
}

export interface OpsRetentionClassSetting {
  keepDays: number
  source?: string
  starter?: boolean
}

export interface OpsRetentionSettingsDoc {
  workspaceId: string
  classes: Record<string, OpsRetentionClassSetting>
  version: number
  updatedAt?: unknown
  updatedBy?: string
}

export type OpsLegalHoldState = 'ACTIVE' | 'LIFT_PENDING' | 'LIFTED'

export interface OpsLegalHoldDoc {
  id: string
  workspaceId: string
  target: LegalHoldTarget
  reason: string
  state: OpsLegalHoldState
  setBy: string
  setAt: unknown
  liftRequestedBy?: string
  liftReason?: string
  liftRequestedAt?: unknown
  liftedBy?: string
  liftedAt?: unknown
}

export interface OpsRetentionBinDoc {
  id: string
  workspaceId: string
  source: string
  recordId: string
  classKey?: string
  binnedAt?: unknown
  purgeAt?: unknown
  recordLabel?: string
  recordData?: Record<string, unknown>
}

export interface OpsRetentionNoticeRecord {
  source: string
  recordId: string
  classKey: string
  dueAt: unknown
  anchorDate?: unknown
  label?: string
}

export interface OpsRetentionNoticeDoc {
  id: string
  workspaceId: string
  dayKey: string
  noticeDate?: unknown
  count?: number
  records: OpsRetentionNoticeRecord[]
}

/* =======================================================================
   A16.a2 Bookings & Memberships Types (SO-P74)
   ======================================================================= */

// Customers
export interface OpsCustomerConsent {
  whatsapp: boolean
  reminders: boolean
  at: number
  source: 'DESK' | 'PUBLIC_LINK' | 'MEMBER_APP'
}

export interface OpsCustomerStrike {
  at: number
  bookingId?: string
  reason?: string
  waived?: boolean
  waivedAt?: number
  waivedBy?: string
  waiveReason?: string
}

export interface OpsCustomer {
  id: string
  workspaceId: string
  displayName: string
  sectors: OpsSector[]
  linkedUid?: string
  tags?: string[]
  status: 'ACTIVE' | 'ARCHIVED'
  consent: OpsCustomerConsent
  strikes?: OpsCustomerStrike[]
  blockedUntil?: number | null
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

export interface OpsCustomerContact {
  id: string
  workspaceId: string
  customerId: string
  phone: string
  email?: string
  notes?: string
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

export interface OpsCustomerPhoneIndex {
  workspaceId: string
  customerId: string
  phoneHmac: string
  createdAt: number
}

// Plans & Policies
export type OpsBookingPlanKind = 'MEMBERSHIP' | 'PACK' | 'DROP_IN'

export const OPS_BOOKING_PLAN_KINDS: readonly OpsBookingPlanKind[] = [
  'MEMBERSHIP',
  'PACK',
  'DROP_IN',
] as const

export interface OpsBookingPrice {
  amountMinor: number
  currency: string
}

export interface OpsBookingNoShowPolicy {
  strikes: number
  windowDays: number
  blockDays: number
}

export interface OpsBookingPlanPolicy {
  cancelWindowHours: number
  lateCancelUsesCredit: boolean
  noShow: OpsBookingNoShowPolicy
  bookAheadDays: number
  freezeMaxDays: number
}

export const DEFAULT_OPS_BOOKING_NO_SHOW_POLICY: OpsBookingNoShowPolicy = {
  strikes: 3,
  windowDays: 30,
  blockDays: 7,
}

export const DEFAULT_OPS_BOOKING_PLAN_POLICY: OpsBookingPlanPolicy = {
  cancelWindowHours: 12,
  lateCancelUsesCredit: true,
  noShow: DEFAULT_OPS_BOOKING_NO_SHOW_POLICY,
  bookAheadDays: 14,
  freezeMaxDays: 30,
}

export interface OpsBookingPlan {
  id: string
  workspaceId: string
  sector: OpsSector
  name: string
  kind: OpsBookingPlanKind
  periodDays?: number
  sessionsTotal?: number
  validDays?: number
  weeklyCap?: number
  monthlyCap?: number
  bookableIds: string[]
  price: OpsBookingPrice
  policy: OpsBookingPlanPolicy
  active: boolean
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

export type OpsBookingPlanSnapshot = Pick<
  OpsBookingPlan,
  | 'id'
  | 'sector'
  | 'name'
  | 'kind'
  | 'periodDays'
  | 'sessionsTotal'
  | 'validDays'
  | 'weeklyCap'
  | 'monthlyCap'
  | 'bookableIds'
  | 'price'
  | 'policy'
>

// Memberships & Dues
export type OpsMembershipState = 'ACTIVE' | 'FROZEN' | 'EXPIRED' | 'CANCELLED'

export const OPS_MEMBERSHIP_STATES: readonly OpsMembershipState[] = [
  'ACTIVE',
  'FROZEN',
  'EXPIRED',
  'CANCELLED',
] as const

export interface OpsMembershipFreeze {
  from: string
  to: string
  reason?: string
}

export interface OpsMembership {
  id: string
  workspaceId: string
  sector: OpsSector
  customerId: string
  planId: string
  planSnapshot: OpsBookingPlanSnapshot
  state: OpsMembershipState
  validFrom: string
  validTo: string
  sessionsUsed: number
  sessionsHeld: number
  freeze?: OpsMembershipFreeze
  renewedFromId?: string
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

export type OpsMembershipLedgerType =
  | 'GRANT'
  | 'HOLD'
  | 'RELEASE'
  | 'USE'
  | 'LATE_CANCEL_USE'
  | 'EXTEND'
  | 'FREEZE'
  | 'UNFREEZE'
  | 'CANCEL'
  | 'WAIVE'

export const OPS_MEMBERSHIP_LEDGER_TYPES: readonly OpsMembershipLedgerType[] = [
  'GRANT',
  'HOLD',
  'RELEASE',
  'USE',
  'LATE_CANCEL_USE',
  'EXTEND',
  'FREEZE',
  'UNFREEZE',
  'CANCEL',
  'WAIVE',
] as const

export interface OpsMembershipLedgerEntry {
  id: string
  workspaceId: string
  membershipId: string
  type: OpsMembershipLedgerType
  deltaHeld: number
  deltaUsed: number
  reason?: string
  bookingId?: string
  byUid: string
  at: number
}

export type OpsBookingDueState = 'DUE' | 'RECORDED_PAID' | 'WAIVED'

export const OPS_BOOKING_DUE_STATES: readonly OpsBookingDueState[] = [
  'DUE',
  'RECORDED_PAID',
  'WAIVED',
] as const

export interface OpsBookingDue {
  id: string
  workspaceId: string
  sector: OpsSector
  customerId: string
  membershipId?: string
  amountMinor: number
  currency: string
  dueKey: string
  state: OpsBookingDueState
  receiptRef?: string
  waiveReason?: string
  recordedBy: string
  at: number
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

// Bookables & Sessions
export type OpsBookableKind = 'CLASS' | 'SLOT' | 'APPOINTMENT' | 'RESOURCE'

export const OPS_BOOKABLE_KINDS: readonly OpsBookableKind[] = [
  'CLASS',
  'SLOT',
  'APPOINTMENT',
  'RESOURCE',
] as const

export const OPS_STAYS_EXTRA_KINDS = [
  'SPA',
  'POOL',
  'GYM',
  'MEETING',
  'BANQUET',
  'ACTIVITY',
  'SHUTTLE',
  'DAY_PASS',
] as const

export type OpsStaysExtraKind = (typeof OPS_STAYS_EXTRA_KINDS)[number]

export interface OpsBookingResources {
  doctor?: OpsPersonRef
  roomPlaceId?: string
  assetId?: string
}

export interface StaysSuggestedExtraDef {
  extraKind: OpsStaysExtraKind
  name: string
  kind: OpsBookableKind
  durationMin: number
  capacity: number
  pattern: BookingSeriesPattern
  guestBookable: boolean
  publicForVisitors: boolean
}

export const STAYS_SUGGESTED_EXTRA_DEFS: Record<OpsStaysExtraKind, StaysSuggestedExtraDef> = {
  SPA: {
    extraKind: 'SPA',
    name: 'Spa & Wellness',
    kind: 'SLOT',
    durationMin: 60,
    capacity: 2,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '09:00',
      durationMin: 60,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: true,
    publicForVisitors: false,
  },
  POOL: {
    extraKind: 'POOL',
    name: 'Swimming Pool',
    kind: 'SLOT',
    durationMin: 60,
    capacity: 20,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '06:00',
      durationMin: 60,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: true,
    publicForVisitors: false,
  },
  GYM: {
    extraKind: 'GYM',
    name: 'Fitness Centre',
    kind: 'SLOT',
    durationMin: 60,
    capacity: 15,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '06:00',
      durationMin: 60,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: true,
    publicForVisitors: false,
  },
  MEETING: {
    extraKind: 'MEETING',
    name: 'Meeting Room',
    kind: 'SLOT',
    durationMin: 60,
    capacity: 10,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '08:00',
      durationMin: 60,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: true,
    publicForVisitors: false,
  },
  BANQUET: {
    extraKind: 'BANQUET',
    name: 'Banquet Hall',
    kind: 'RESOURCE',
    durationMin: 240,
    capacity: 100,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '09:00',
      durationMin: 240,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: true,
    publicForVisitors: false,
  },
  ACTIVITY: {
    extraKind: 'ACTIVITY',
    name: 'Resort Activity',
    kind: 'SLOT',
    durationMin: 90,
    capacity: 15,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '10:00',
      durationMin: 90,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: true,
    publicForVisitors: false,
  },
  SHUTTLE: {
    extraKind: 'SHUTTLE',
    name: 'Airport / Station Shuttle',
    kind: 'SLOT',
    durationMin: 60,
    capacity: 8,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '07:00',
      durationMin: 60,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: true,
    publicForVisitors: false,
  },
  DAY_PASS: {
    extraKind: 'DAY_PASS',
    name: 'Day Pass',
    kind: 'SLOT',
    durationMin: 480,
    capacity: 30,
    pattern: {
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      startTime: '09:00',
      durationMin: 480,
      fromDate: '2026-01-01',
      toDate: '2030-12-31',
    },
    guestBookable: false,
    publicForVisitors: true,
  },
}

export const STAYS_PRESET_SUGGESTED_EXTRAS: Record<StaysPreset, readonly StaysSuggestedExtraDef[]> = {
  BUDGET_HOTEL: [],
  BUSINESS_HOTEL: [
    STAYS_SUGGESTED_EXTRA_DEFS.MEETING,
    STAYS_SUGGESTED_EXTRA_DEFS.GYM,
    STAYS_SUGGESTED_EXTRA_DEFS.SHUTTLE,
  ],
  RESORT_HERITAGE: [
    STAYS_SUGGESTED_EXTRA_DEFS.SPA,
    STAYS_SUGGESTED_EXTRA_DEFS.POOL,
    STAYS_SUGGESTED_EXTRA_DEFS.ACTIVITY,
    STAYS_SUGGESTED_EXTRA_DEFS.DAY_PASS,
    STAYS_SUGGESTED_EXTRA_DEFS.BANQUET,
  ],
  HOMESTAY_VILLA: [
    STAYS_SUGGESTED_EXTRA_DEFS.ACTIVITY,
  ],
  SERVICED_APARTMENT: [
    STAYS_SUGGESTED_EXTRA_DEFS.GYM,
    STAYS_SUGGESTED_EXTRA_DEFS.POOL,
  ],
  HOSTEL: [
    STAYS_SUGGESTED_EXTRA_DEFS.ACTIVITY,
    STAYS_SUGGESTED_EXTRA_DEFS.SHUTTLE,
  ],
}

export interface OpsBookable {
  id: string
  workspaceId: string
  sector: OpsSector
  placeId: string
  readAccess: string[] | null
  kind: OpsBookableKind
  extraKind?: OpsStaysExtraKind
  guestBookable?: boolean
  publicForVisitors?: boolean
  name: string
  durationMin: number
  capacity: number
  pattern: BookingSeriesPattern
  staff: OpsPersonRef[]
  holds: string[]
  bookAheadDays: number
  public: boolean
  planIds: string[]
  resources?: OpsBookingResources
  isolation?: boolean
  active: boolean
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

export interface OpsBookingSession {
  id: string
  workspaceId: string
  sector: OpsSector
  bookableId: string
  sessionKey: string
  date: string
  startTime: string
  durationMin: number
  capacity: number
  booked: number
  waitlisted: number
  cancelled?: boolean
  readAccess?: string[] | null
  createdAt: number
  updatedAt: number
}

// Bookings
export type OpsBookingState =
  | 'BOOKED'
  | 'WAITLISTED'
  | 'CANCELLED'
  | 'LATE_CANCELLED'
  | 'ATTENDED'
  | 'NO_SHOW'

export const OPS_BOOKING_STATES: readonly OpsBookingState[] = [
  'BOOKED',
  'WAITLISTED',
  'CANCELLED',
  'LATE_CANCELLED',
  'ATTENDED',
  'NO_SHOW',
] as const

export type OpsBookingSource = 'DESK' | 'MEMBER_APP' | 'PUBLIC_LINK' | 'STAFF'

export const OPS_BOOKING_SOURCES: readonly OpsBookingSource[] = [
  'DESK',
  'MEMBER_APP',
  'PUBLIC_LINK',
  'STAFF',
] as const

export interface OpsBookingCheckIn {
  at: number
  by: string
  method: 'STAFF_SCAN' | 'PLACE_SCAN' | 'DESK_OTP' | 'MANUAL'
  reason?: string
}

export interface OpsBookingInHouse {
  roomPlaceId: string
  guests: number
  roomLabel?: string
}

export interface OpsBooking {
  id: string
  workspaceId: string
  sector: OpsSector
  customerId?: string
  inHouse?: OpsBookingInHouse
  bookableId: string
  sessionKey: string
  startAt: number
  date: string
  startTime: string
  membershipId?: string
  state: OpsBookingState
  waitlistPosition?: number | null
  source: OpsBookingSource
  cancelReason?: string
  checkIn?: OpsBookingCheckIn
  resources?: OpsBookingResources
  opId?: string
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

// Input Types
export interface UpsertCustomerInput {
  workspaceId: string
  customerId?: string
  displayName: string
  sectors: OpsSector[]
  phone: string
  email?: string
  notes?: string
  tags?: string[]
  consent?: Partial<OpsCustomerConsent>
  opId?: string
}

export interface ArchiveCustomerInput {
  workspaceId: string
  customerId: string
  reason?: string
  opId?: string
}

export interface FindCustomerByPhoneInput {
  workspaceId: string
  phone: string
}

export interface LinkMyCustomerRecordInput {
  workspaceId: string
  opId?: string
}

export interface CreateBookingPlanInput {
  workspaceId: string
  sector: OpsSector
  name: string
  kind: OpsBookingPlanKind
  periodDays?: number
  sessionsTotal?: number
  validDays?: number
  weeklyCap?: number
  monthlyCap?: number
  bookableIds?: string[]
  price?: OpsBookingPrice
  policy?: Partial<OpsBookingPlanPolicy>
  opId?: string
}

export interface UpdateBookingPlanInput {
  workspaceId: string
  planId: string
  name?: string
  periodDays?: number
  sessionsTotal?: number
  validDays?: number
  weeklyCap?: number
  monthlyCap?: number
  bookableIds?: string[]
  price?: OpsBookingPrice
  policy?: Partial<OpsBookingPlanPolicy>
  active?: boolean
  opId?: string
}

export interface ArchiveBookingPlanInput {
  workspaceId: string
  planId: string
  opId?: string
}

export interface GrantMembershipInput {
  workspaceId: string
  customerId: string
  planId: string
  validFrom?: string
  recordDue?: boolean
  opId?: string
}

export interface RenewMembershipInput {
  workspaceId: string
  membershipId: string
  recordDue?: boolean
  opId?: string
}

export interface FreezeMembershipInput {
  workspaceId: string
  membershipId: string
  fromDate: string
  toDate: string
  reason?: string
  opId?: string
}

export interface UnfreezeMembershipInput {
  workspaceId: string
  membershipId: string
  reason?: string
  opId?: string
}

export interface CancelMembershipInput {
  workspaceId: string
  membershipId: string
  reason?: string
  opId?: string
}

export interface RecordDuePaidInput {
  workspaceId: string
  dueId: string
  receiptRef?: string
  opId?: string
}

export interface WaiveDueInput {
  workspaceId: string
  dueId: string
  reason: string
  opId?: string
}

export interface CreateBookableInput {
  workspaceId: string
  sector: OpsSector
  placeId: string
  kind: OpsBookableKind
  extraKind?: OpsStaysExtraKind
  guestBookable?: boolean
  publicForVisitors?: boolean
  name: string
  durationMin: number
  capacity: number
  pattern: BookingSeriesPattern
  staff: OpsPersonRef[]
  holds?: string[]
  bookAheadDays?: number
  public?: boolean
  planIds?: string[]
  resources?: OpsBookingResources
  isolation?: boolean
  opId?: string
}

export interface UpdateBookableInput {
  workspaceId: string
  bookableId: string
  name?: string
  placeId?: string
  durationMin?: number
  capacity?: number
  pattern?: BookingSeriesPattern
  staff?: OpsPersonRef[]
  holds?: string[]
  bookAheadDays?: number
  public?: boolean
  planIds?: string[]
  active?: boolean
  extraKind?: OpsStaysExtraKind
  guestBookable?: boolean
  publicForVisitors?: boolean
  resources?: OpsBookingResources
  isolation?: boolean
  opId?: string
}

export interface ArchiveBookableInput {
  workspaceId: string
  bookableId: string
  opId?: string
}

export interface BookSessionInput {
  workspaceId: string
  bookableId: string
  date: string
  customerId?: string
  inHouse?: OpsBookingInHouse
  membershipId?: string
  resources?: OpsBookingResources
  waitlist?: boolean
  opId?: string
}

export interface BookInHouseExtraInput {
  workspaceId: string
  bookableId: string
  date: string
  inHouse?: OpsBookingInHouse
  roomPlaceId?: string
  guests?: number
  waitlist?: boolean
  opId?: string
}

export interface CancelBookingInput {
  workspaceId: string
  bookingId: string
  reason?: string
  opId?: string
}

export interface MoveBookingInput {
  workspaceId: string
  bookingId: string
  targetBookableId: string
  targetDate: string
  resources?: OpsBookingResources
  allowIsolationChange?: boolean
  waitlist?: boolean
  opId?: string
}

export interface LeaveWaitlistInput {
  workspaceId: string
  bookingId: string
  reason?: string
  opId?: string
}

export interface WaiveStrikeInput {
  workspaceId: string
  customerId: string
  strikeIndex?: number
  reason: string
  opId?: string
}

export interface ClearBookingBlockInput {
  workspaceId: string
  customerId: string
  reason: string
  opId?: string
}

export function normalizeE164Phone(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const trimmed = raw.trim()
  if (!trimmed) return ''
  const digits = trimmed.replace(/\D/g, '')
  if (!digits) return ''
  return `+${digits}`
}

export function cleanCustomerDisplayName(raw: unknown): string {
  if (typeof raw !== 'string') {
    throw new Error('displayName is required and must be a string.')
  }
  const trimmed = raw.trim()
  if (trimmed.length < 1 || trimmed.length > 120) {
    throw new Error('displayName must be between 1 and 120 characters.')
  }
  return trimmed
}

export function cleanCustomerTags(raw: unknown): string[] {
  if (raw === undefined || raw === null) return []
  if (!Array.isArray(raw)) {
    throw new Error('tags must be an array.')
  }
  if (raw.length > 10) {
    throw new Error('tags cannot contain more than 10 entries.')
  }
  return raw.map((t) => {
    if (typeof t !== 'string') throw new Error('Tag must be a string.')
    const tr = t.trim()
    if (tr.length < 1 || tr.length > 40) throw new Error('Tag must be 1 to 40 characters.')
    return tr
  })
}

export function validateCustomerSectors(sectors: unknown): OpsSector[] {
  if (!Array.isArray(sectors) || sectors.length === 0) {
    throw new Error('sectors must be a non-empty array.')
  }
  const clean: OpsSector[] = []
  for (const s of sectors) {
    if (!isCustomerSector(s)) {
      throw new Error(`Sector "${String(s)}" does not support customers.`)
    }
    clean.push(s)
  }
  return Array.from(new Set(clean))
}

export function cleanPlanName(raw: unknown): string {
  if (typeof raw !== 'string') throw new Error('Plan name is required and must be a string.')
  const trimmed = raw.trim()
  if (trimmed.length < 1 || trimmed.length > 120) {
    throw new Error('Plan name must be between 1 and 120 characters.')
  }
  return trimmed
}

export function cleanBookableName(raw: unknown): string {
  if (typeof raw !== 'string') throw new Error('Bookable name is required and must be a string.')
  const trimmed = raw.trim()
  if (trimmed.length < 1 || trimmed.length > 120) {
    throw new Error('Bookable name must be between 1 and 120 characters.')
  }
  return trimmed
}

export function cleanBookableDuration(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isInteger(n) || n < 5 || n > 720) {
    throw new Error('durationMin must be an integer between 5 and 720.')
  }
  return n
}

export function cleanBookableCapacity(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isInteger(n) || n < 1 || n > 500) {
    throw new Error('capacity must be an integer between 1 and 500.')
  }
  return n
}

export function cleanBookableKind(raw: unknown): OpsBookableKind {
  if (typeof raw !== 'string') throw new Error('kind is required and must be a string.')
  const upper = raw.trim().toUpperCase()
  if (!OPS_BOOKABLE_KINDS.includes(upper as OpsBookableKind)) {
    throw new Error(`Invalid bookable kind "${raw}". Must be one of: ${OPS_BOOKABLE_KINDS.join(', ')}`)
  }
  return upper as OpsBookableKind
}

export function cleanStaysExtraKind(raw: unknown): OpsStaysExtraKind {
  if (typeof raw !== 'string') throw new Error('extraKind is required and must be a string.')
  const upper = raw.trim().toUpperCase()
  if (!OPS_STAYS_EXTRA_KINDS.includes(upper as OpsStaysExtraKind)) {
    throw new Error(`Invalid extraKind "${raw}". Must be one of: ${OPS_STAYS_EXTRA_KINDS.join(', ')}`)
  }
  return upper as OpsStaysExtraKind
}

export function cleanReason(raw: unknown, min = 3, max = 500, label = 'Reason'): string {
  if (typeof raw !== 'string') throw new Error(`${label} is required and must be a string.`)
  const trimmed = raw.trim()
  if (trimmed.length < min || trimmed.length > max) {
    throw new Error(`${label} must be between ${min} and ${max} characters.`)
  }
  return trimmed
}

export function cleanCustomerCallOutcome(raw: unknown): OpsCustomerCallOutcome {
  if (typeof raw !== 'string') throw new Error('outcome is required and must be a string.')
  const upper = raw.trim().toUpperCase()
  if (!OPS_CUSTOMER_CALL_OUTCOMES.includes(upper as OpsCustomerCallOutcome)) {
    throw new Error(`Invalid call outcome "${raw}". Must be one of: ${OPS_CUSTOMER_CALL_OUTCOMES.join(', ')}`)
  }
  return upper as OpsCustomerCallOutcome
}

export function cleanCustomerCallNote(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') return ''
  if (typeof raw !== 'string') throw new Error('Note must be a string.')
  const trimmed = raw.trim()
  if (trimmed.length > 500) {
    throw new Error('Note cannot exceed 500 characters.')
  }
  return trimmed
}

// ---------------------------------------------------------------------------
// 7. Queue Tokens
// ---------------------------------------------------------------------------

export type OpsQueueTokenState =
  | 'WAITING'
  | 'CALLED'
  | 'SERVED'
  | 'SKIPPED'
  | 'ON_HOLD'

export const OPS_QUEUE_TOKEN_STATES: readonly OpsQueueTokenState[] = [
  'WAITING',
  'CALLED',
  'SERVED',
  'SKIPPED',
  'ON_HOLD',
] as const

export type OpsQueueTokenHoldKind = 'DILATION' | 'OBSERVATION'

export const OPS_QUEUE_TOKEN_HOLD_KINDS: readonly OpsQueueTokenHoldKind[] = [
  'DILATION',
  'OBSERVATION',
] as const

export interface OpsQueueToken {
  id: string
  workspaceId: string
  placeId: string
  dayKey: string
  n: number
  state: OpsQueueTokenState
  customerId?: string
  bookingId?: string
  issuedAt: number
  issuedBy: string
  calledAt?: number
  calledBy?: string
  servedAt?: number
  holdUntil?: number
  holdKind?: OpsQueueTokenHoldKind
  holdMinutes?: number
  heldAt?: number
  heldBy?: string
}

export interface HoldQueueTokenInput {
  workspaceId: string
  tokenId: string
  holdKind: OpsQueueTokenHoldKind
  minutes?: number
  opId?: string
}

export interface IssueQueueTokenInput {
  workspaceId: string
  placeId: string
  customerId?: string
  bookingId?: string
  opId?: string
}

export interface CallNextTokenInput {
  workspaceId: string
  placeId: string
  opId?: string
}

export interface MarkTokenServedInput {
  workspaceId: string
  tokenId: string
  opId?: string
}

export interface SkipTokenInput {
  workspaceId: string
  tokenId: string
  opId?: string
}

export interface GetQueueBoardInput {
  workspaceId: string
  placeId: string
}

export interface ListQueueTokensInput {
  workspaceId: string
  placeId: string
}

// ---------------------------------------------------------------------------
// 8. QR Attendance & Check-in
// ---------------------------------------------------------------------------

export interface OpsCustomerQr {
  customerId: string
  workspaceId: string
  tokenHash: string
  expiresAt: number
  updatedAt: number
}

export interface GetMyBookingQrInput {
  workspaceId: string
}

export interface CheckInByCustomerQrInput {
  workspaceId: string
  token: string
  placeId: string
  opId?: string
}

export interface BookingCheckInByPlaceQrInput {
  placeToken: string
  overrideTurnaroundReason?: string
  opId?: string
}

export interface PrepareBookingCheckInOtpInput {
  workspaceId: string
  bookingId: string
  opId?: string
}

export interface CheckInByPhoneOtpInput {
  workspaceId: string
  idToken: string
  bookingId: string
  opId?: string
}

export interface ManualCheckInInput {
  workspaceId: string
  bookingId: string
  reason: string
  overrideTurnaroundReason?: string
  opId?: string
}

// ---------------------------------------------------------------------------
// 9. Public Booking Link
// ---------------------------------------------------------------------------

export interface OpsPublicBookingLink {
  hash: string
  workspaceId: string
  sectors: OpsSector[]
  enabled: boolean
  createdAt: number
  createdBy: string
  revokedAt?: number
}

export interface OpsPublicBookingSettings {
  currentHash: string | null
  enabled: boolean
  sectors: OpsSector[]
  updatedAt: number
  updatedBy: string
}

export interface SetPublicBookingLinkInput {
  workspaceId: string
  enabled: boolean
  sectors?: OpsSector[]
  opId?: string
}

export interface GetPublicBookingInfoInput {
  token: string
}

export interface PublicBookSessionInput {
  token: string
  bookableId: string
  sessionKey: string
  name?: string
  consent: {
    whatsapp: boolean
    reminders: boolean
  }
  opId?: string
}

export interface PublicCancelBookingInput {
  token: string
  bookingId: string
  reason?: string
  opId?: string
}

export interface ListMyPublicBookingsInput {
  token: string
}

// ---------------------------------------------------------------------------
// 10. Desk Lists & Customer Calls
// ---------------------------------------------------------------------------

export type OpsCustomerCallOutcome =
  | 'REACHED'
  | 'NO_ANSWER'
  | 'NOT_INTERESTED'
  | 'RENEWING'

export const OPS_CUSTOMER_CALL_OUTCOMES: readonly OpsCustomerCallOutcome[] = [
  'REACHED',
  'NO_ANSWER',
  'NOT_INTERESTED',
  'RENEWING',
] as const

export interface OpsCustomerCall {
  id: string
  workspaceId: string
  customerId: string
  outcome: OpsCustomerCallOutcome
  note?: string
  calledAt: number
  calledBy: string
}

export interface ListRenewalsDueInput {
  workspaceId: string
  sector: OpsSector
}

export interface ListChurnCallsInput {
  workspaceId: string
  sector: OpsSector
  days: 14 | 30
}

export interface RecordCustomerCallInput {
  workspaceId: string
  customerId: string
  outcome: OpsCustomerCallOutcome
  note?: string
  opId?: string
}

export function validatePrepareBookingCheckInOtpInput(raw: unknown): PrepareBookingCheckInOtpInput {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Input must be an object.')
  }
  const obj = raw as Record<string, unknown>
  const cleanField = (val: unknown, label: string, max = 120): string => {
    if (typeof val !== 'string') throw new Error(`${label} must be a string.`)
    const trimmed = val.trim()
    if (!trimmed || trimmed.length > max) throw new Error(`${label} must be between 1 and ${max} characters.`)
    return trimmed
  }
  const workspaceId = cleanField(obj.workspaceId, 'workspaceId', 120)
  const bookingId = cleanField(obj.bookingId, 'bookingId', 120)
  const opId = obj.opId !== undefined ? cleanField(obj.opId, 'opId', 120) : undefined
  return { workspaceId, bookingId, opId }
}

/* R39 K8.a Stays statutory registers and licences starter catalogue mirrors (SO-D203, SO-P205) */

export interface StaysStarterRegisterDef {
  slug: string
  title: string
  summary: string
  source: string
  edition: string
  fields: OpsTemplateField[]
  cadence: OpsScheduleCadence
  window: OpsScheduleWindowSpec
  target: 'PROPERTY' | 'ROOM' | 'AREA'
  dutyRoleKey: string
}

export interface StaysStarterLicenceDef {
  key: string
  title: string
  authority: string
  category: OpsComplianceCategory
  country: 'IN' | 'GLOBAL'
}

export interface StaysStarterChecklistTemplateDef {
  slug: string
  title: string
  summary: string
  source: string
  edition: string
  fields: OpsTemplateField[]
}

export interface GetStaysStarterStatusInput {
  workspaceId: string
  propertyId: string
}

export interface StaysStarterRegisterStatus {
  slug: string
  title: string
  templateId: string
  version: number | null
  edited: boolean
  scheduleId: string | null
  paused: boolean
  cadence: OpsScheduleCadence
  window: OpsScheduleWindowSpec
  target: 'PROPERTY' | 'ROOM' | 'AREA'
  dutyRoleKey: string
  source?: string
  edition?: string
}

export interface StaysStarterLicenceStatus {
  key: string
  title: string
  authority: string
  category: OpsComplianceCategory
  country: 'IN' | 'GLOBAL'
  complianceItem: {
    id: string
    title: string
    status: OpsComplianceStatus
    validTo: string
    ownerUid: string
    backupUid?: string | null
    authority?: string
  } | null
  linkedHidden?: boolean
}

export interface GetStaysStarterStatusResult {
  propertyId: string
  preset: StaysPreset
  registers: StaysStarterRegisterStatus[]
  licences: StaysStarterLicenceStatus[]
}

export interface GetPlantStarterStatusInput {
  workspaceId: string
  plantId: string
}

export interface PlantStarterRegisterStatus {
  slug: string
  title: string
  templateId: string
  version: number | null
  edited: boolean
  scheduleId: string | null
  paused: boolean
  cadence: OpsScheduleCadence
  window: OpsScheduleWindowSpec
  target: 'PLANT' | 'LINE' | 'MACHINE'
  dutyRoleKey: string
  source?: string
  edition?: string
}

export interface GetPlantStarterStatusResult {
  plantId: string
  preset: PlantPreset
  registers: PlantStarterRegisterStatus[]
}

export interface SetPlantNearMissExportPeopleInput {
  workspaceId: string
  enabled: boolean
  opId?: string
}

export interface SetPlantNearMissExportPeopleResult {
  ok: boolean
  nearMissExportPeople: boolean
  replayed?: boolean
}

// ---------------------------------------------------------------------------
// Shared-device mode types & validators (R39 A17.a, SO-P75, Q53–Q58)
// ---------------------------------------------------------------------------

export type OpsSharedDeviceState = 'ACTIVE' | 'REVOKED'

export type OpsSharedDeviceSessionEndReason =
  | 'SWITCH'
  | 'IDLE'
  | 'SIGN_OUT'
  | 'REVOKED'

export interface OpsSharedDevice {
  deviceId: string
  workspaceId: string
  placeId: string
  label: string
  secretHash: string
  idleLockMinutes: number
  state: OpsSharedDeviceState
  enrolledBy: string
  enrolledAt: unknown
  lastSeenAt: unknown
  revokedAt?: unknown | null
  revokedBy?: string | null
}

export interface OpsSharedDevicePublic {
  deviceId: string
  workspaceId: string
  placeId: string
  label: string
  idleLockMinutes: number
  state: OpsSharedDeviceState
  enrolledBy: string
  enrolledAt: unknown
  lastSeenAt: unknown
}

export interface OpsSharedDeviceSession {
  sessionId: string
  workspaceId: string
  deviceId: string
  uid: string
  startedAt: unknown
  endedAt?: unknown | null
  endReason?: OpsSharedDeviceSessionEndReason | null
}

export interface OpsDevicePin {
  workspaceId: string
  uid: string
  pinHash: string
  setAt: unknown
  resetBy?: string | null
}

export interface OpsDevicePinAttempt {
  deviceId: string
  uid: string
  failedAttempts: number
  firstFailedAt: number
  lastFailedAt: number
  lockedUntil?: number | null
  lockedAlertEmitted?: boolean
}

export interface SharedDevicePersonSummary {
  uid: string
  displayName: string
  initials: string
  hasPin: boolean
}

export interface CreateDeviceEnrolCodeInput {
  workspaceId: string
  placeId: string
  label: string
  opId?: string
}

export interface CreateDeviceEnrolCodeResult {
  code: string
  expiresAt: number
  replayed?: boolean
}

export interface EnrolSharedDeviceInput {
  code: string
}

export interface EnrolSharedDeviceResult {
  deviceId: string
  deviceSecret: string
  workspaceId: string
  placeId: string
  label: string
  idleLockMinutes: number
}

export interface RevokeSharedDeviceInput {
  workspaceId: string
  deviceId: string
  opId?: string
}

export interface RevokeSharedDeviceResult {
  success: boolean
  deviceId: string
  replayed?: boolean
}

export interface SetSharedDeviceSettingsInput {
  workspaceId: string
  deviceId: string
  idleLockMinutes: number
  label?: string
  opId?: string
}

export interface SetSharedDeviceSettingsResult {
  success: boolean
  deviceId: string
  idleLockMinutes: number
  label?: string
  replayed?: boolean
}

export interface ListSharedDevicesInput {
  workspaceId: string
}

export interface ListSharedDevicesResult {
  devices: OpsSharedDevicePublic[]
}

export interface ListSharedDeviceSessionsInput {
  workspaceId: string
  deviceId?: string
  limit?: number
}

export interface ListSharedDeviceSessionsResult {
  sessions: OpsSharedDeviceSession[]
}

export interface SetMySharedDevicePinInput {
  workspaceId: string
  pin: string
  opId?: string
}

export interface SetMySharedDevicePinResult {
  success: boolean
  replayed?: boolean
}

export interface SetSharedDevicePinByPhoneInput {
  deviceId: string
  deviceSecret: string
  phoneIdToken: string
  pin: string
  opId?: string
}

export interface SetSharedDevicePinByPhoneResult {
  success: boolean
  uid: string
  replayed?: boolean
}

export interface ResetSharedDevicePinInput {
  workspaceId: string
  uid: string
  opId?: string
}

export interface ResetSharedDevicePinResult {
  success: boolean
  replayed?: boolean
}

export interface ListSharedDevicePeopleInput {
  deviceId: string
  deviceSecret: string
}

export interface ListSharedDevicePeopleResult {
  people: SharedDevicePersonSummary[]
}

export interface SharedDeviceSwitchInInput {
  deviceId: string
  deviceSecret: string
  uid: string
  pin: string
  opId?: string
}

export interface SharedDeviceSwitchInResult {
  customToken: string
  sessionId: string
  lastOnlineSwitchInAt: number
}

export interface SharedDeviceSwitchOutInput {
  deviceId: string
  deviceSecret: string
  sessionId: string
  reason: 'SWITCH' | 'IDLE' | 'SIGN_OUT'
}

export interface SharedDeviceSwitchOutResult {
  success: boolean
}

export type SharedDeviceQueuedItemKind =
  | 'REGISTER_RECORD'
  | 'REGISTER_RECORD_PHOTOS'
  | 'ATTENDANCE_CHECK_IN'
  | 'CHECKPOINT_SCAN'
  | 'SKIP_DUE'
  | 'RAISE_REQUEST'
  | 'START_REQUEST'
  | 'MARK_REQUEST_DONE'
  | 'ATTENDANCE_ROLL'
  | 'CHECKLIST_TOGGLE'
  | 'CHECKLIST_COMMENT'
  | 'CHECKLIST_PROOF'
  | 'CHECKLIST_VOICE_MEMO'
  | 'STOCK_SCAN_SUGGESTION'
  | 'STOCK_COUNT_ROUND'
  | 'TIME_CLOCK'
  | 'BMW_SAVE'
  | 'DPR_SAVE'
  | 'CON_ISSUE_CREATE'
  | 'CON_ISSUE_ATTACH_PHOTOS'

export interface SharedDeviceRecordPhotosPayload {
  recordId: string
  bindings: Array<{
    fieldKey: string
    fileId: string
  }>
}

export interface SharedDeviceChecklistTogglePayload {
  checklistItemId: string
  isCompleted?: boolean
  workflowStatus?: 'ASSIGNED' | 'ACCEPTED' | 'ASSESSING' | 'ONGOING' | 'COMPLETED' | 'NOT_REQUIRED' | 'POSTPONED'
  pageKey?: string | null
  assignedAt?: string | null
}

export interface SharedDeviceChecklistCommentPayload {
  checklistItemId: string
  text?: string
  body?: string
  pageKey?: string | null
  assignedAt?: string | null
}

export interface SharedDeviceChecklistProofPayload {
  checklistItemId: string
  fileId: string
  pageKey?: string | null
  assignedAt?: string | null
  workflowStatus?: 'ASSIGNED' | 'ACCEPTED' | 'ASSESSING' | 'ONGOING' | 'COMPLETED' | 'NOT_REQUIRED' | 'POSTPONED'
}

export interface SharedDeviceChecklistVoiceMemoPayload {
  checklistItemId: string
  fileId: string
  durationMs: number
  pageKey?: string | null
  assignedAt?: string | null
}

export interface SharedDeviceStockScanPayload {
  stockItemId: string
  kind: 'SCAN' | 'COUNT' | 'RECEIVED'
  barcode?: string | null
  direction?: 'IN' | 'OUT'
  quantity?: number
  countedQuantity: number
  systemQuantityAtScan: number
  note?: string | null
  locationId?: string | null
  transferId?: string | null
}

export interface SharedDeviceStockCountRoundPayload {
  roundId: string
  suggestions: Array<{
    stockItemId: string
    countedQuantity: number
    systemQuantityAtScan: number
    sku?: string
    barcode?: string | null
    note?: string | null
  }>
}

export interface SharedDeviceTimeClockPayload {
  action: 'START' | 'STOP'
  kind?: 'CLOCK' | 'TIMER'
  clientGeneratedId: string
  nodeId?: string | null
  geo?: { latitude: number; longitude: number; accuracy?: number } | null
  note?: string | null
  billable?: boolean
}

export interface SharedDeviceQueuedItem {
  personUid: string
  offlineSessionId: string
  at: number
  kind: SharedDeviceQueuedItemKind
  payload: Record<string, unknown>
  opId: string
}

export interface IssueSharedDeviceUploadUrlInput {
  deviceId: string
  deviceSecret: string
  personUid: string
  offlineSessionId: string
  opId: string
  artifactType:
    | 'HELPER_CHECKLIST_PROOF'
    | 'HELPER_CHECKLIST_VOICE_MEMO'
    | 'OPS_RECORD_PHOTO'
    | 'OPS_REQUEST_PHOTO'
    | 'CON_ISSUE_PHOTO'
  artifactId: string
  fileName: string
  contentType: string
  sizeBytes: number
  checksumSha256: string
}

export interface IssueSharedDeviceUploadUrlResult {
  reservationId: string
  uploadUrl: string
  objectPath: string
  expiresInSeconds: number
  replayed?: boolean
}

export interface FinalizeSharedDeviceUploadInput {
  deviceId: string
  deviceSecret: string
  reservationId: string
}

export interface FinalizeSharedDeviceUploadResult {
  fileId: string
  status: string
  objectPath: string
  downloadUrl?: string
}

export interface SubmitSharedDeviceQueuedInput {
  deviceId: string
  deviceSecret: string
  items: SharedDeviceQueuedItem[]
}

export interface SharedDeviceQueuedItemResult {
  opId: string
  ok: boolean
  code?: string
  result?: unknown
}

export interface SubmitSharedDeviceQueuedResult {
  results: SharedDeviceQueuedItemResult[]
}

export const SHARED_DEVICE_IDLE_LOCK_MINUTES_MIN = 1
export const SHARED_DEVICE_IDLE_LOCK_MINUTES_MAX = 15
export const SHARED_DEVICE_IDLE_LOCK_MINUTES_DEFAULT = 2
export const SHARED_DEVICE_ENROL_CODE_TTL_MS = 10 * 60 * 1000 // 10 minutes
export const SHARED_DEVICE_LOCKOUT_MAX_ATTEMPTS = 5
export const SHARED_DEVICE_LOCKOUT_WINDOW_MS = 15 * 60 * 1000 // 15 minutes
export const SHARED_DEVICE_LOCKOUT_DURATION_MS = 15 * 60 * 1000 // 15 minutes
export const SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS = 12 * 60 * 60 * 1000 // 12 hours
export const SHARED_DEVICE_MAX_QUEUED_ITEMS = 50

export function validateDevicePin(pin: unknown): { valid: boolean; error?: string } {
  if (typeof pin !== 'string' || !/^\d{4}$/.test(pin)) {
    return { valid: false, error: 'PIN must be exactly 4 digits.' }
  }

  if (pin[0] === pin[1] && pin[1] === pin[2] && pin[2] === pin[3]) {
    return { valid: false, error: 'PIN cannot be all the same digit.' }
  }

  const d0 = Number(pin[0])
  const d1 = Number(pin[1])
  const d2 = Number(pin[2])
  const d3 = Number(pin[3])

  if (d1 - d0 === 1 && d2 - d1 === 1 && d3 - d2 === 1) {
    return { valid: false, error: 'PIN cannot be an ascending run.' }
  }

  if (d0 - d1 === 1 && d1 - d2 === 1 && d2 - d3 === 1) {
    return { valid: false, error: 'PIN cannot be a descending run.' }
  }

  return { valid: true }
}

export function isSharedDeviceToken(
  auth?: { token?: Record<string, unknown> } | null,
): boolean {
  return Boolean(auth?.token?.sharedDevice === true)
}

export function validateIdleLockMinutes(minutes: unknown): number {
  const m = Number(minutes)
  if (!Number.isInteger(m) || m < SHARED_DEVICE_IDLE_LOCK_MINUTES_MIN || m > SHARED_DEVICE_IDLE_LOCK_MINUTES_MAX) {
    throw new Error(
      `idleLockMinutes must be an integer between ${SHARED_DEVICE_IDLE_LOCK_MINUTES_MIN} and ${SHARED_DEVICE_IDLE_LOCK_MINUTES_MAX}.`,
    )
  }
  return m
}

export function validateDeviceLabel(label: unknown): string {
  if (typeof label !== 'string' || !label.trim()) {
    throw new Error('Device label is required.')
  }
  const trimmed = label.trim()
  if (trimmed.length > 60) {
    throw new Error('Device label must not exceed 60 characters.')
  }
  return trimmed
}

/* R39 K12.a + K12.b Stays PMS connector framework mirrors (SO-D201, SO-D207, SO-D212, SO-P201) */
export {
  PMS_EVENT_TYPES,
  type PmsEventType,
  PMS_ADAPTER_TYPES,
  type PmsAdapterType,
  type ReducedPmsEvent,
  reduceGenericPmsEvent,
  type MappedPmsAction,
  mapPmsEventToRoomAction,
  PMS_OUTBOX_TYPES,
  type OpsPmsOutboxType,
  PMS_OUTBOX_STATES,
  type OpsPmsOutboxState,
  PMS_OUTBOX_BACKOFF_MINUTES,
  type OpsPmsOutboxItem,
  computePmsOutboxBackoff,
  type OpsRoomPmsSync,
  isRoomNotSyncedToPms,
  type StaysPmsWriteBackConfig,
  type StaysPmsConnectionMetadata,
  type ReducedPmsRoomRecord,
  type ReducedEzeeRoomRecord,
  type ReducedHotelogixRoomRecord,
  type DiffPmsRoomSnapshotsOptions,
  type DiffEzeeSnapshotsOptions,
  type HotelogixRequestEnvelope,
  type BuildHotelogixEnvelopeOptions,
  type HotelogixClassifiedResponse,
  type ReduceHotelogixStateOptions,
  reduceEzeeInhouse,
  diffPmsRoomSnapshots,
  diffEzeeSnapshots,
  deterministicPmsEventId,
  formatHotelogixGmtDatetime,
  buildHotelogixEnvelope,
  classifyHotelogixResponse,
  reduceHotelogixState,
  type ReducedStayflexiRow,
  type ReducedPmsReportRow,
  type StayflexiColumnMap,
  type PmsReportColumnMap,
  type ReportUploadAdapter,
  STAYFLEXI_HEADER_SYNONYMS,
  DJUBO_HEADER_SYNONYMS,
  IDS_NEXT_HEADER_SYNONYMS,
  REPORT_UPLOAD_HEADER_SYNONYMS,
  autoDetectStayflexiColumns,
  autoDetectReportColumns,
  reduceStayflexiReportRows,
  reduceReportUploadRows,
  type ReduceReportUploadRowsOptions,
  type DiffStayflexiReportsOptions,
  type DiffReportUploadsOptions,
  diffStayflexiReports,
  diffReportUploads,
  type ImportStaysPmsReportInput,
  type ImportStaysPmsReportResult,
} from '../../functions-common/src/ops/pms'

/* R39 K7.a Stays Lost & Found mirrors (SO-D207, SO-D294, SO-D295, SO-D296, SO-D297, SO-P115) */
export {
  DEFAULT_HOLD_DAYS,
  MIN_HOLD_DAYS,
  MAX_HOLD_DAYS,
  DEFAULT_VALUABLE_HOLD_DAYS,
  MIN_VALUABLE_HOLD_DAYS,
  MAX_VALUABLE_HOLD_DAYS,
  LF_CATEGORIES,
  type LostFoundCategory,
  VALUABLE_CATEGORIES,
  LF_ITEM_STATUSES,
  type LostFoundItemStatus,
  LF_CLAIM_STATUSES,
  type LostFoundClaimStatus,
  DISPOSAL_METHODS,
  type LostFoundDisposalMethod,
  LF_RETURN_MODES,
  type LostFoundReturnMode,
  LF_DESK_VERDICTS,
  type LostFoundDeskVerdict,
  LF_EVENT_TYPES,
  type LostFoundEventType,
  MIN_ITEM_DESCRIPTION_LENGTH,
  MAX_ITEM_DESCRIPTION_LENGTH,
  MAX_ITEM_PHOTOS,
  MAX_BIN_LABEL_LENGTH,
  MIN_CLAIMANT_NAME_LENGTH,
  MAX_CLAIMANT_NAME_LENGTH,
  MIN_GUEST_DESCRIPTION_LENGTH,
  MAX_GUEST_DESCRIPTION_LENGTH,
  MAX_COURIER_TRACKING_REF_LENGTH,
  CLAIM_LINK_EXPIRY_DAYS,
  CLAIM_PURGE_DAYS,
  CLAIM_LINK_EXPIRY_MS,
  CLAIM_PURGE_MS,
  MIN_DISPOSAL_NOTE_LENGTH,
  MAX_DISPOSAL_NOTE_LENGTH,
  MIN_CANCEL_REASON_LENGTH,
  MAX_CANCEL_REASON_LENGTH,
  MAX_COURIER_ADDRESS_LINES,
  MAX_COURIER_ADDRESS_LINE_LENGTH,
  PIN_CODE_REGEX,
  type LostFoundSettingsDoc,
  type LostFoundItemDisposal,
  type LostFoundItemDoc,
  type LostFoundEventDoc,
  type LostFoundClaimDoc,
  type LostFoundCourierAddress,
  type LostFoundClaimPiiDoc,
  type LostFoundClaimSecretsDoc,
  type LogFoundItemInput,
  type RecordFoundItemPhotoInput,
  type MoveFoundItemBinInput,
  type StartLostFoundClaimInput,
  type ReissueLostFoundClaimLinkInput,
  type CancelLostFoundClaimInput,
  type VerifyLostFoundClaimInput,
  type CompleteLostFoundReturnInput,
  type ProposeLostFoundDisposalInput,
  type SignLostFoundDisposalInput,
  type WithdrawLostFoundDisposalInput,
  type SetLostFoundSettingsInput,
  type GetLostFoundClaimantInput,
  type GetLostClaimLinkInfoInput,
  type PrepareLostClaimOtpInput,
  type OpenLostClaimInput,
  type DescribeLostClaimInput,
  buildLostFoundSettingsKey,
  isValuableCategory,
  computeHoldUntil,
  validateLostFoundSettings,
  validateCourierAddress,
} from '../../functions-common/src/ops/lostFound'

// Members (Editor, Viewer): the phone item gates their phone tab only (A14.b). It never widens what their role already reads on the desk.
export {
  OPS_PHONE_ITEMS,
  OPS_PHONE_LEGACY_HELPER_GRANT,
  OPS_PHONE_RECOMMENDED_SETS,
  type OpsPhoneSurface,
  type OpsPhoneItemKey,
  type OpsPhoneItem,
  type OpsPhoneAccessDoc,
  type OpsPhoneAccessPersonDoc,
  type ResolveOpsPhoneItemsInput,
  isItemModuleActive,
  computeLegacyHelperGrant,
  resolveOpsPhoneItems,
} from '../../functions-common/src/ops/phoneItems'

// Construction (R39 B1.a + B1.b)
import type {
  ConApprovers,
  ConDrawing,
  ConDrawingDiscipline,
  ConDrawingFlag,
  ConDrawingRef,
  ConDrawingStatus,
  ConPackage,
  ConProjectStatus,
} from '../../functions-common/src/ops/construction'

export {
  CON_PROJECT_STATUSES,
  CON_DRAWING_DISCIPLINES,
  CON_DRAWING_STATUSES,
  CON_REVISION_STATUSES,
  type ConProjectStatus,
  type ConPackage,
  type ConIndentApprover,
  type ConApprovers,
  type ConProject,
  type ConDrawingDiscipline,
  type ConDrawingStatus,
  type ConRevisionStatus,
  type ConDrawing,
  type ConDrawingRevision,
  type ConDrawingRef,
  type ConDrawingFlag,
  isValidPackageKey,
  validateConProjectCode,
  isValidIsoDate,
  validateProjectDates,
  isValidHttpsUrl,
  validateConPackage,
  validateConPackages,
  validateConApprovers,
  validateConDrawingNumber,
  validateConDrawingRevision,
  validateConDrawingRef,
} from '../../functions-common/src/ops/construction'

export interface CreateConProjectInput {
  workspaceId: string
  placeId: string
  name: string
  code: string
  status?: ConProjectStatus
  clientPartyId?: string | null
  startDate?: string | null
  endDate?: string | null
  packages?: ConPackage[]
  approvers?: Partial<ConApprovers>
  opId?: string
}

export interface UpdateConProjectInput {
  workspaceId: string
  projectId: string
  name?: string
  code?: string
  status?: ConProjectStatus
  clientPartyId?: string | null
  startDate?: string | null
  endDate?: string | null
}

export interface SetConProjectPackagesInput {
  workspaceId: string
  projectId: string
  packages: ConPackage[]
}

export interface SetConProjectApproversInput {
  workspaceId: string
  projectId: string
  approvers: ConApprovers
}

export interface ListConProjectsInput {
  workspaceId: string
  status?: ConProjectStatus
}

export interface GetConProjectInput {
  workspaceId: string
  projectId: string
}

export interface CreateConDrawingInput {
  workspaceId: string
  projectId: string
  number: string
  title: string
  discipline: ConDrawingDiscipline
  packageKey?: string | null
  initialRevision: string
  link: string
  note?: string | null
  opId?: string
}

export interface IssueConDrawingRevisionInput {
  workspaceId: string
  drawingId: string
  revision: string
  link: string
  note?: string | null
  opId?: string
}

export interface WithdrawConDrawingInput {
  workspaceId: string
  drawingId: string
}

export interface ListConDrawingsInput {
  workspaceId: string
  projectId: string
  discipline?: ConDrawingDiscipline
  packageKey?: string
  status?: ConDrawingStatus
}

export interface ListConDrawingRevisionsInput {
  workspaceId: string
  drawingId: string
}

export interface ConDrawingRow extends ConDrawing {
  revisionCount: number
}

import type {
  ConDprShift,
  ConDprLine,
  ConDiaryKind,
  ConDprBlock,
} from '../../functions-common/src/ops/conDpr'

export {
  CON_DPR_SHIFTS,
  CON_DIARY_KINDS,
  type ConDprShift,
  type ConDiaryKind,
  type ConDprGang,
  type ConDprEquipment,
  type ConDprLine,
  type ConDprTotals,
  type ConDprBlock,
  type ConDiaryEntry,
  validateConDprShift,
  validateConDiaryKind,
  buildConDprRecordId,
  validateConDprGang,
  validateConDprEquipment,
  validateConDprLine,
  validateConDprLines,
  computeConDprTotals,
  validateConDiaryText,
} from '../../functions-common/src/ops/conDpr'

export interface SaveDprDraftInput {
  workspaceId: string
  projectId: string
  reportDate: string
  shift: ConDprShift
  lines: ConDprLine[]
  weather?: string | null
  safetyExceptions?: boolean
  safetyNotes?: string | null
  keyReceiptsNotes?: string | null
  inspectionRefsNotes?: string | null
  opId?: string
}

export interface SubmitDprInput {
  workspaceId: string
  projectId: string
  reportDate: string
  shift: ConDprShift
  lines: ConDprLine[]
  weather?: string | null
  safetyExceptions?: boolean
  safetyNotes?: string | null
  keyReceiptsNotes?: string | null
  inspectionRefsNotes?: string | null
  opId?: string
}

export interface ReviewDprInput {
  workspaceId: string
  recordId: string
  notes?: string | null
  selfApprovalReason?: string | null
  opId?: string
}

export interface ReturnDprInput {
  workspaceId: string
  recordId: string
  reason: string
  opId?: string
}

export interface ApproveDprInput {
  workspaceId: string
  recordId: string
  notes?: string | null
  selfApprovalReason?: string | null
  opId?: string
}

export interface GetDprInput {
  workspaceId: string
  recordId: string
}

export interface GetDprResult {
  record: OpsRecord | null
  dpr: ConDprBlock | null
  corrections: OpsRecordCorrection[]
  canEdit: boolean
  canSubmit: boolean
  canReview?: boolean
  canReturn?: boolean
  canApprove?: boolean
}

export interface ListDprsInput {
  workspaceId: string
  projectId: string
  startDate?: string | null
  endDate?: string | null
  state?: OpsRecordState | null
  limit?: number
  startAfterRecordId?: string
}

export interface AddDiaryEntryInput {
  workspaceId: string
  projectId: string
  placeId?: string | null
  kind: ConDiaryKind
  text: string
  occurredAt?: number | null
  offline?: boolean
  photoPaths?: string[]
  requestId?: string | null
  supersedesId?: string | null
  opId?: string
}

export interface ListDiaryEntriesInput {
  workspaceId: string
  projectId: string
  day?: string | null
  limit?: number
}

// ---------------------------------------------------------------------------
// R39 B4.a Construction Quality & Snags (Inspections, Pour Cards, Tests)
// ---------------------------------------------------------------------------

export const CON_POUR_CARD_KINDS = ['POUR', 'PRE_CONCEALMENT'] as const
export type ConPourCardKind = (typeof CON_POUR_CARD_KINDS)[number]

export const CON_HOLD_STATES = ['HELD', 'INSPECTION_REQUESTED', 'RELEASED', 'REJECTED'] as const
export type ConHoldState = (typeof CON_HOLD_STATES)[number]

export const CON_CHECKLIST_RESULTS = ['OK', 'NOT_OK', 'NA'] as const
export type ConChecklistResult = (typeof CON_CHECKLIST_RESULTS)[number]

export const CON_INSPECTION_OUTCOMES = ['PASS', 'FAIL'] as const
export type ConInspectionOutcome = (typeof CON_INSPECTION_OUTCOMES)[number]

export const CON_TEST_TYPES = [
  'CUBE_7D',
  'CUBE_28D',
  'SLUMP',
  'STEEL_TENSILE',
  'SOIL_COMPACTION',
  'OTHER',
] as const
export type ConTestType = (typeof CON_TEST_TYPES)[number]

export const CON_TEST_OUTCOMES = ['PENDING', 'PASS', 'FAIL'] as const
export type ConTestOutcome = (typeof CON_TEST_OUTCOMES)[number]

export const MAX_POUR_ELEMENT_LENGTH = 120
export const MIN_POUR_CHECKLIST_ITEMS = 1
export const MAX_POUR_CHECKLIST_ITEMS = 40
export const MAX_CHECKLIST_LABEL_LENGTH = 120
export const MAX_CHECKLIST_NOTE_LENGTH = 500
export const MAX_CHECKLIST_PHOTOS = 6
export const MAX_OUTCOME_HISTORY_ITEMS = 50
export const MAX_OUTCOME_NOTES_LENGTH = 1000
export const MIN_FAIL_NOTES_LENGTH = 3

export const MAX_TEST_SAMPLE_ID_LENGTH = 40
export const MAX_TEST_OTHER_LABEL_LENGTH = 60
export const MAX_TEST_SPEC_UNIT_LENGTH = 16
export const MAX_TEST_RESULTS = 20
export const MAX_TEST_LAB_REF_LENGTH = 80
export const MAX_TEST_PHOTOS = 6

export interface ConPourChecklistItem {
  itemKey: string
  label: string
  result: ConChecklistResult
  note?: string | null
  photoPaths?: string[]
}

export const CON_STARTER_POUR_CHECKLIST: readonly ConPourChecklistItem[] = [
  {
    itemKey: 'formwork',
    label: 'Formwork alignment, stability, and sealing',
    result: 'OK',
  },
  {
    itemKey: 'reinforcement_cover',
    label: 'Reinforcement bars, spacing, and clear cover',
    result: 'OK',
  },
  {
    itemKey: 'embedded_services',
    label: 'Embedded conduits, sleeves, and fixtures',
    result: 'OK',
  },
  {
    itemKey: 'cleaning',
    label: 'Debris cleared and surface prepared',
    result: 'OK',
  },
  {
    itemKey: 'levels',
    label: 'Levels, lines, and benchmark verification',
    result: 'OK',
  },
] as const

export interface ConInspectionOutcomeHistory {
  requestId: string
  outcome: ConInspectionOutcome
  inspectorUid: string
  recordedAt: number
  notes?: string | null
  photoPaths?: string[]
  ncrRequestId?: string | null
}

export interface ConPourCardBlock {
  projectId: string
  placeId: string
  kind: ConPourCardKind
  element: string
  plannedAt: number
  checklist: ConPourChecklistItem[]
  drawingRefs?: ConDrawingRef[]
  inspectorUid: string
  inspectionRequestId?: string | null
  holdState: ConHoldState
  outcomeHistory: ConInspectionOutcomeHistory[]
}

export interface ConTestSpec {
  min?: number
  max?: number
  unit: string
}

export interface ConTestResultEntry {
  value: number
  enteredBy: string
  enteredAt: number
  labRef?: string | null
  photoPaths?: string[]
}

export interface ConTestDoc {
  id: string
  workspaceId: string
  projectId: string
  sampleId: string
  testType: ConTestType
  otherLabel?: string | null
  placeId: string
  pourCardId?: string | null
  castAt: number
  dueDay: string
  spec: ConTestSpec
  results: ConTestResultEntry[]
  outcome: ConTestOutcome
  ncrRequestId?: string | null
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
}

export interface SaveConPourCardInput {
  workspaceId: string
  projectId: string
  cardId?: string
  placeId: string
  kind: ConPourCardKind
  element: string
  plannedAt: number
  checklist: ConPourChecklistItem[]
  drawingRefs?: ConDrawingRef[]
  inspectorUid: string
  opId?: string
}

export interface RequestConInspectionInput {
  workspaceId: string
  cardId: string
  opId?: string
}

export interface RecordConInspectionOutcomeInput {
  workspaceId: string
  requestId: string
  outcome: ConInspectionOutcome
  notes?: string | null
  photoPaths?: string[]
  opId?: string
}

export interface RecordConInspectionOutcomeOutput {
  requestId: string
  outcome: ConInspectionOutcome
  holdState: ConHoldState
  ncrRequestId?: string | null
  requestClosed?: boolean
  replayed?: boolean
}

export interface ConInspectionOutcomeDoc {
  id: string
  workspaceId: string
  projectId: string
  pourCardId: string
  inspectorUid: string
  outcome: ConInspectionOutcome
  notes?: string | null
  photoPaths?: string[]
  ncrRequestId?: string | null
  recordedAt: number
  requestCloseError?: {
    code: string
    at: number
  } | null
}

export interface ReassignConInspectorInput {
  workspaceId: string
  cardId: string
  newInspectorUid: string
  reason?: string
  opId?: string
}

export interface GetConPourCardInput {
  workspaceId: string
  cardId: string
}

export interface ListConPourCardsInput {
  workspaceId: string
  projectId: string
  holdState?: ConHoldState | null
  placeId?: string | null
  limit?: number
  startAfterPlannedAt?: number
  startAfterCardId?: string
}

export interface CreateConTestInput {
  workspaceId: string
  projectId: string
  sampleId: string
  testType: ConTestType
  otherLabel?: string | null
  placeId: string
  pourCardId?: string | null
  castAt: number
  dueDay?: string | null
  spec: ConTestSpec
  opId?: string
}

export interface AddConTestResultInput {
  workspaceId: string
  testId: string
  value: number
  labRef?: string | null
  photoPaths?: string[]
  opId?: string
}

export interface ListConTestsInput {
  workspaceId: string
  projectId: string
  outcome?: ConTestOutcome | null
  overdueOnly?: boolean
  limit?: number
  startAfterDueDay?: string
  startAfterTestId?: string
}

// ---------------------------------------------------------------------------
// R39 B5.a Construction Issues & Approvals (Hindrances, RFIs, Selections, Changes)
// ---------------------------------------------------------------------------

export {
  CON_ISSUE_KINDS,
  CON_ISSUE_STATES,
  CON_RESPONSIBLE_PARTY_TYPES,
  CON_IMPACT_LEVELS,
  CON_DECISION_VIAS,
  MIN_CON_ISSUE_TITLE_LENGTH,
  MAX_CON_ISSUE_TITLE_LENGTH,
  MAX_CON_ISSUE_DESCRIPTION_LENGTH,
  MAX_CON_ISSUE_NOTE_LENGTH,
  MAX_CON_ISSUE_REVISED_INSTRUCTION_LENGTH,
  MAX_CON_ISSUE_CONTRACT_NOTICE_LENGTH,
  MIN_CON_ISSUE_REASON_LENGTH,
  MAX_CON_ISSUE_REASON_LENGTH,
  MAX_CON_ISSUE_PHOTOS,
  CON_ISSUE_PHOTO_CONTENT_TYPES,
  type ConIssuePhotoContentType,
  MAX_CON_ISSUE_PHOTO_BYTES,
  MIN_SELECTION_OPTIONS,
  MAX_SELECTION_OPTIONS,
  MAX_OPTION_LABEL_LENGTH,
  MAX_OPTION_NOTE_LENGTH,
  type ConIssueKind,
  type ConIssueState,
  type ConResponsiblePartyType,
  type ConImpactLevel,
  type ConDecisionVia,
  type ConResponsibleParty,
  type ConIssueImpact,
  type ConSelectionOption,
  type ConDrawingReference,
  type ConIssue,
  type ConIssueEdit,
  type ConIssueResponse,
  type ConIssueDecision,
  type ConIssueDetailResult,
  type CreateConIssueInput,
  type UpdateConIssueInput,
  type RespondConIssueInput,
  type WithdrawConIssueInput,
  type CloseConIssueInput,
  type DecideConIssueInput,
  type SendConIssueForApprovalInput,
  type GetConIssueInput,
  type ListConIssuesInput,
  type ListConIssueDecisionsInput,
  type AttachConIssuePhotosInput,
  type GetConIssuePhotoUrlsInput,
  type ConIssueSignedPhoto,
  type GetConIssuePhotoUrlsResult,
  validateConIssuePhotoPath,
  validateConIssueKind,
  validateConIssueTitle,
  validateConIssueDescription,
  validateConResponsibleParty,
  validateConImpact,
  validateConSelectionOptions,
  validateConPhotoPaths,
  validateConContractNoticeRef,
  validateFirstNoticedAt,
  validateResponseDueAt,
  formatConIssueRegisterNo,
} from '../../functions-common/src/ops/conIssues'

// R39 B6.a Construction Measurement & Bill-Support Packs Mirror
export type {
  ConPartyType,
  ConParty,
  UpsertConPartyInput,
  ListConPartiesInput,
  StandardMeasurementUnit,
  ConMeasurementState,
  ConInspectionRef,
  ConSourceLink,
  ConMeasurementRevision,
  ConMeasurementCorrection,
  ConMeasurement,
  ConMeasureTotal,
  SaveConMeasurementInput,
  SubmitConMeasurementInput,
  AcceptConMeasurementInput,
  DisputeConMeasurementInput,
  CorrectConMeasurementInput,
  CancelConMeasurementInput,
  GetConMeasurementInput,
  ListConMeasurementsInput,
  ListDisputedConMeasurementsInput,
  GetConMeasureTotalsInput,
} from '../../functions-common/src/ops/conMeasurements'

export {
  BANNED_MONEY_KEYS,
  assertNoMoneyKeys,
  CON_PARTY_TYPES,
  STANDARD_MEASUREMENT_UNITS,
  CON_MEASUREMENT_STATES,
  toThousandths,
  fromThousandths,
  buildMeasureTotalDocId,
  validateConPartyName,
  validateConPartyType,
  validateConPartyTrade,
  validateConPartyExternalRef,
  validateConPartyProjectIds,
  validateWorkOrderRef,
  validateItemRef,
  validateItemDescription,
  validateMeasurementUnit,
  validateMeasuredDay,
  validatePeriodLabel,
  validateMeasurementQuantity,
  validateInspectionRefs,
  validateSourceLinks,
  validateDisputeReason,
  validateCorrectionReason,
  validateCancellationReason,
  validateDiscrepancyNote,
  validateUpsertConPartyInput,
  validateListConPartiesInput,
  validateSaveConMeasurementInput,
  validateSubmitConMeasurementInput,
  validateAcceptConMeasurementInput,
  validateDisputeConMeasurementInput,
  validateCorrectConMeasurementInput,
  validateCancelConMeasurementInput,
  validateGetConMeasurementInput,
  validateListConMeasurementsInput,
  validateListDisputedConMeasurementsInput,
  validateGetConMeasureTotalsInput,
} from '../../functions-common/src/ops/conMeasurements'

export type {
  ConReportKind,
  GetConReportInput,
  ConReportProjectSummary,
  ConReportResult,
  ConReportStructuredResult,
  ConDprReportRow,
  ConDprReportTotals,
  ConDprIsoWeekRollup,
  HindranceAgeBucket,
  ConHindranceReportRow,
  ConHindranceReportTotals,
  OverdueQualityCategory,
  ConOverdueQualityReportRow,
  ConOverdueQualityReportTotals,
  ConAttendanceReportRow,
  ConAttendanceReportTotals,
  ConHandoverEvidenceExportSummary,
  ConHandoverSourceCount,
  ConHandoverIndexTotals,
} from '../../functions-common/src/ops/conReports'

export {
  CON_REPORT_KINDS,
  MAX_CON_REPORT_RANGE_DAYS,
  MAX_CON_REPORT_ROWS,
  SO_D14_ATTENDANCE_PROVISIONAL_NOTICE,
  validateGetConReportInput,
  computeIsoWeek,
  buildApprovedDprReportContent,
  buildHindranceAgeingReportContent,
  buildOverdueQualityReportContent,
  buildAttendanceProvisionalReportContent,
  buildHandoverIndexReportContent,
  buildConReportContent,
} from '../../functions-common/src/ops/conReports'

export type {
  ConStarterTemplateSlug,
  ConStarterTemplateDefinition,
  ConStarterTemplateStatus,
  GetConStarterStatusInput,
  GetConStarterStatusResult,
  ConSnagRoomType,
} from '../../functions-common/src/ops/conStarters'

export {
  CON_STARTER_SLUGS,
  CON_STARTER_TEMPLATES,
  CON_STARTER_MEP_CHECKLIST,
  CON_SNAG_ROOM_TYPES,
  CON_SNAG_CATEGORIES_BY_ROOM_TYPE,
  getSnagCategoriesForRoomType,
  buildConTemplateId,
  LEGACY_CON_DPR_TEMPLATE_ID,
  LEGACY_CON_POUR_CARD_TEMPLATE_ID,
  isConDprTemplateId,
  isConPourCardTemplateId,
} from '../../functions-common/src/ops/conStarters'

// Hospital People & Shifts
export type {
  HospitalCredentialTag,
  HospitalPrivilegeTag,
  HospitalSkillTag,
  SkillMixRuleResult,
  SkillMixMatrixOptions,
} from '../../functions-common/src/ops/hosPeople'
export {
  HOSPITAL_STAFF_GROUPS,
  HOSPITAL_CREDENTIAL_TAGS,
  HOSPITAL_PRIVILEGE_TAGS,
  HOSPITAL_SKILL_TAGS,
  isValidStaffGroup,
  isValidHospitalSkillTag,
  computeSkillMixMatrix,
} from '../../functions-common/src/ops/hosPeople'

// Roster diff helper
export type {
  RosterVersionDiff,
  RosterVersionChange,
} from '../../functions-common/src/ops/rosters'
export {
  diffRosterVersions,
} from '../../functions-common/src/ops/rosters'

// Hospital Starters & Compliance Starters
export type {
  HosStarterTemplateDef,
  HosStarterTemplateSlug,
  HospitalComplianceStarterDef,
} from '../../functions-common/src/ops/hosStarters'
export {
  HOS_STARTER_SOURCE,
  HOS_STARTER_EDITION,
  HOS_STARTER_JURISDICTION,
  buildHosTemplateId,
  HOS_FIRE_DRILL_FIELDS,
  HOS_FIRE_EQUIPMENT_MONTHLY_FIELDS,
  HOS_ELECTRICAL_INSPECTION_FIELDS,
  HOS_KITCHEN_LOG_FIELDS,
  HOS_LAUNDRY_LOG_FIELDS,
  HOSPITAL_STARTER_TEMPLATES,
  HOS_STARTER_SLUGS,
  HOSPITAL_COMPLIANCE_STARTERS,
  isKnownHospitalStarterLicenceKey,
} from '../../functions-common/src/ops/hosStarters'

// Clinics Starters & Cold Chain
import type {
  TempLogColumnMapping as TempLogColumnMappingImport,
  TempExcursionWindow as TempExcursionWindowImport,
} from '../../functions-common/src/ops/tempLogs'

export type {
  CliStarterTemplateDef,
  ClinicsStarterTemplateSlug,
} from '../../functions-common/src/ops/cliStarters'
export {
  CLI_STARTER_SOURCE,
  CLI_STARTER_EDITION,
  CLI_STARTER_JURISDICTION,
  buildCliTemplateId,
  CLI_FRIDGE_FIELDS,
  CLI_VACCINE_ILR_FIELDS,
  CLI_DEEP_FREEZER_FIELDS,
  CLI_BLOOD_PRBC_FIELDS,
  CLI_PLATELETS_FIELDS,
  CLI_FFP_FIELDS,
  CLI_LN2_TANK_FIELDS,
  CLI_TRANSIT_BOX_FIELDS,
  CLI_DIALYSIS_RO_DAILY_FIELDS,
  CLI_DIALYSIS_RO_MONTHLY_FIELDS,
  CLINICS_STARTER_TEMPLATES,
  CLINICS_STARTER_SLUGS,
  getClinicsTemplatesForPreset,
} from '../../functions-common/src/ops/cliStarters'

// Temperature Logs & Cold Chain Excursions (R39 M6.a, SO-P258)
export type {
  TempLogColumnMapping,
  TempLogParsedReading,
  TempExcursionWindow,
  TempDaySummary,
  ParseTempLogResult,
} from '../../functions-common/src/ops/tempLogs'
export {
  MAX_TEMP_LOG_BYTES,
  MAX_TEMP_LOG_ROWS,
  EXCURSION_MERGE_WINDOW_MS,
  parseTimestampInTz,
  parseTempLogCsv,
} from '../../functions-common/src/ops/tempLogs'

export interface EnsureClinicsStarterSetInput {
  workspaceId: string
}

export interface EnsureClinicsStarterSetResult {
  createdTemplates: string[]
  skippedTemplates: string[]
  createdPlans?: string[]
  skippedPlans?: string[]
}

export interface ImportTempLogInput {
  workspaceId: string
  assetId: string
  csvContent: string
  columnMapping: TempLogColumnMappingImport
}

export interface ImportTempLogResult {
  importId: string
  totalReadings: number
  excursionsCount: number
  requestsRaised: number
  windows: TempExcursionWindowImport[]
  replayed: boolean
}

// Biomedical Waste (BMW)
export type {
  BmwCategory,
  BmwSettingsDoc,
  BmwCollector,
  BmwCorrection,
  BmwDayState,
  BmwBag,
  BmwDayDoc,
  BmwPickupDoc,
  BmwStatusDay,
  BmwStatusResult,
  BmwMonthSummary,
  BmwSummaryResult,
  BmwHandoverDoc,
  StartBmwHandoverInput,
  StartBmwHandoverResult,
  GetBmwHandoverInfoInput,
  GetBmwHandoverInfoResult,
  PrepareBmwHandoverOtpInput,
  PrepareBmwHandoverOtpResult,
  ConfirmBmwHandoverInput,
  ConfirmBmwHandoverResult,
  AttachBmwPickupReceiptInput,
  AttachBmwPickupReceiptResult,
} from '../../functions-common/src/ops/hosBmw'
export {
  BMW_CATEGORIES,
  BMW_DEFAULT_STORAGE_HOURS,
  BMW_MIN_STORAGE_HOURS,
  BMW_MAX_STORAGE_HOURS,
  BMW_DEFAULT_UNUSUAL_KG,
  BMW_MIN_UNUSUAL_KG,
  BMW_MAX_UNUSUAL_KG,
  BMW_MANIFEST_REF_REGEX,
  MAX_BMW_COLLECTORS,
  BMW_E164_PHONE_REGEX,
  BMW_BARCODE_REGEX,
  MAX_BMW_BAGS_PER_DAY,
  MAX_BMW_BAG_KG,
  BMW_PHOTO_PATH_REGEX,
  isValidBmwWeight,
  validateBmwWeights,
  validateBmwBagCounts,
  hasUnusualWeight,
  validateManifestRef,
  sanitizeCsvFormulaPrefix,
  isValidBmwPhotoPath,
  validateBmwCollector,
  validateBmwCollectors,
  isValidBmwBarcode,
  validateBmwBag,
  validateBmwBags,
} from '../../functions-common/src/ops/hosBmw'

// Staff Training & Health Check Status
export type {
  ImmunisationStatus,
  OpsStaffStatusDoc,
  AnnualTrainingStatus,
} from '../../functions-common/src/ops/hosStaffStatus'
export {
  IMMUNISATION_STATUSES,
  computeAnnualTrainingStatus,
  isValidImmunisationStatus,
  isValidIsoDayKey,
} from '../../functions-common/src/ops/hosStaffStatus'

// Hospital Operations Incidents & CAPA
export type {
  IncidentKind,
  IncidentSeverity,
  IncidentState,
  IncidentCapaItem,
  OpsIncidentDoc,
} from '../../functions-common/src/ops/hosIncidents'
export {
  INCIDENT_KINDS,
  INCIDENT_SEVERITIES,
  INCIDENT_STATES,
  isValidIncidentKind,
  isValidIncidentSeverity,
  isValidIncidentState,
  canTransitionIncidentState,
} from '../../functions-common/src/ops/hosIncidents'

// Homestay and villa owner feed & digests (SO-P121, K9.a)
export type {
  OwnerFeedAction,
  OwnerDigestChannel,
  OwnerDigestSendStatus,
  OpsOwnerFeedOwner,
  OpsOwnerFeedOwnerPii,
  OpsOwnerFeedDoc,
  OpsOwnerFeedPiiDoc,
  OpsOwnerFeedSecretsDoc,
  OpsOwnerDigestSentEntry,
  OpsOwnerDigestReadyRoom,
  OpsOwnerDigestAttendancePerson,
  OpsOwnerDigestStockItem,
  OpsOwnerDigestIssue,
  OpsOwnerDigestApproval,
  OpsOwnerDigestSnapshot,
  OpsOwnerDigestDoc,
  OpsOwnerFeedOwnerInput,
  SetOwnerFeedInput,
  RemoveFeedOwnerInput,
  ReissueOwnerFeedLinkInput,
  GetOwnerFeedInput,
  ListOwnerDigestsInput,
  SendOwnerDigestNowInput,
  GetOwnerFeedLinkInfoInput,
  PrepareOwnerFeedOtpInput,
  OpenOwnerFeedInput,
} from '../../functions-common/src/ops/ownerFeed'
export {
  MAX_FEED_OWNERS,
  MIN_FEED_OWNERS,
  MAX_OWNER_NAME_LENGTH,
  MIN_OWNER_NAME_LENGTH,
  MAX_STOCK_LOCATION_IDS,
  DEFAULT_SEND_AT_LOCAL,
  MAX_DIGEST_SNAPSHOT_BYTES,
  MAX_READY_ROOMS_IN_SNAPSHOT,
  MAX_PHOTOS_PER_READY_ROOM,
  MAX_STOCK_ITEMS_IN_SNAPSHOT,
  MAX_ISSUES_IN_SNAPSHOT,
  MAX_DIGESTS_HISTORY,
  DIGEST_PURGE_DAYS,
  OWNER_FEED_ACTIONS,
  maskEmail,
  isValidSendAtLocal,
  validateSendAtLocal,
  validateOwnerName,
  validateOwnerPhone,
  validateOwnerEmail,
  validateStockLocationIds,
  validateFeedOwnersInput,
  buildOwnerFeedDocKey,
  buildOwnerFeedSecretDocKey,
  buildOwnerDigestDocKey,
} from '../../functions-common/src/ops/ownerFeed'

export interface GetPlantDowntimeReportInput {
  workspaceId: string
  plantId: string
  fromDay: string
  toDay: string
}

export interface PlantMachineDowntimeSummary {
  machineId: string
  machineName: string
  assetCode?: string
  lineName?: string
  breakdownCount: number
  downtimeMinutes: number
  mainReason: PlantDowntimeReason | null
}

export interface PlantDowntimeReasonSummary {
  reason: PlantDowntimeReason
  breakdownCount: number
  downtimeMinutes: number
}

export interface PlantDowntimeReportItem {
  requestId: string
  machineId: string
  machineName: string
  assetCode?: string
  lineName?: string
  downtimeReason: PlantDowntimeReason
  downtimeNote?: string
  downSince: number
  releasedAt: number | null
  downtimeMinutes: number
  isOpen: boolean
}

export interface GetPlantDowntimeReportResult {
  workspaceId: string
  plantId: string
  plantName: string
  fromDay: string
  toDay: string
  totalBreakdowns: number
  totalDowntimeMinutes: number
  machines: PlantMachineDowntimeSummary[]
  reasons: PlantDowntimeReasonSummary[]
  items: PlantDowntimeReportItem[]
}
/* =======================================================================
   R39 K11.b Stays Group View & Template Push (SO-P125)
   ======================================================================= */
export {
  MAX_GROUP_PROPERTIES_LIMIT,
  computeStaysScorecard,
} from '../../functions-common/src/ops/staysGroup'
export type {
  StaysPropertyReadiness,
  StaysPropertyOpenRequests,
  StaysPropertyMissedChecks,
  StaysPropertyLicences,
  StaysPropertyScorecard,
  StaysGroupPropertyCard,
  StaysGroupTotals,
  StaysGroupViewResult,
  GetStaysGroupViewInput,
  PushStaysGroupTemplateInput,
  ComputeStaysScorecardInput,
  PushStaysTemplatePropertyStatus,
  PushStaysGroupTemplatePropertyResult,
  PushStaysGroupTemplateCounts,
  PushStaysGroupTemplateResult,
} from '../../functions-common/src/ops/staysGroup'

export const OPS_GET_STAYS_GROUP_VIEW_ACTION_ID = 'ops.getStaysGroupView'
export const OPS_PUSH_STAYS_GROUP_TEMPLATE_ACTION_ID = 'ops.pushStaysGroupTemplate'

export {
  CLI_LEGAL_ROLE_KEYS,
  type CliLegalRoleKey,
  type CliLegalRoleDef,
  CLI_LEGAL_ROLES,
  isLegalRoleKey,
  CLI_PRESET_LEGAL_ROLES,
} from '../../functions-common/src/ops/cliLegalRoles'

export type {
  SampleBagStage,
  SampleBagRejectedItem,
  SampleBag,
  SampleRunner,
  SampleSettingsDoc,
  SampleHandoverDoc,
  CreateSampleBagInput,
  AttachColdBoxPhotoInput,
  StartSampleHandoverInput,
  StartSampleHandoverResult,
  GetSampleHandoverInfoInput,
  GetSampleHandoverInfoResult,
  PrepareSampleHandoverOtpInput,
  PrepareSampleHandoverOtpResult,
  ConfirmSampleHandoverInput,
  ConfirmSampleHandoverResult,
  ReceiveSamplesAtLabInput,
  ReceiveSamplesAtLabResult,
  CreateHomeCollectionJobInput,
  CreateHomeCollectionJobResult,
  GetHomeCollectionAddressInput,
  GetHomeCollectionAddressResult,
  HomeCollectionEventKind,
  RecordRunnerPingInput,
  RecordRunnerPingResult,
} from '../../functions-common/src/ops/cliSamples'

export {
  SAMPLE_BAG_STAGES,
  SAMPLE_BARCODE_REGEX,
  SAMPLE_PHOTO_PATH_REGEX,
  SAMPLE_RUNNER_PHONE_REGEX,
  DEFAULT_SAMPLE_STAGE_MINUTES,
  isValidSampleBarcode,
  isValidSamplePhotoPath,
  isValidRunnerPhone,
  ALLOWED_SAMPLE_STAGE_TRANSITIONS,
  canTransitionSampleStage,
  getSampleStageLimitMinutes,
} from '../../functions-common/src/ops/cliSamples'

export type {
  OpsCliNarcSchedule,
  OpsCliNarcUnit,
  OpsCliNarcMoveKind,
  OpsCliNarcCountState,
  OpsCliNarcItem,
  OpsCliNarcMove,
  OpsCliNarcCount,
  CreateNarcItemInput,
  ArchiveNarcItemInput,
  RecordNarcMoveInput,
  StartNarcCountInput,
  SubmitNarcTallyInput,
  GetRxPhotoUrlInput,
  ExportNarcRegisterInput,
} from '../../functions-common/src/ops/cliNarcotics'

export {
  OPS_CLI_NARC_SCHEDULES,
  OPS_CLI_NARC_UNITS,
  OPS_CLI_NARC_MOVE_KINDS,
  OPS_CLI_NARC_COUNT_STATES,
  OPS_CLI_NARC_SCHEDULES as NARC_SCHEDULES,
  OPS_CLI_NARC_UNITS as NARC_UNITS,
  OPS_CLI_NARC_MOVE_KINDS as NARC_MOVE_KINDS,
  OPS_CLI_NARC_COUNT_STATES as NARC_COUNT_STATES,
  isValidNarcSchedule,
  isValidNarcUnit,
  isValidNarcMoveKind,
  isValidNarcCountState,
  validateNarcRxPhotoPath,
} from '../../functions-common/src/ops/cliNarcotics'

export type {
  SpokeScoreComponents,
  SpokeScoreResult,
  GetHubSpokeScoresInput,
  GetHubSpokeScoresResult,
} from '../../functions-common/src/ops/cliHubSpoke'

export {
  SPOKE_SCORE_WEIGHTS,
  calculateSpokeScore,
} from '../../functions-common/src/ops/cliHubSpoke'

export type {
  OpsCliJobOrderKind,
  OpsCliJobOrderStage,
  OpsCliJobOrder,
  CreateJobOrderInput,
  UpdateJobOrderStageInput,
  SweepOverdueJobOrdersInput,
  OpsCliDialyzerStatus,
  OpsCliDialyzer,
  RegisterDialyzerInput,
  RecordDialyzerUseInput,
  OpsCliIvfWitnessStep,
  OpsCliIvfWitnessStepType,
  OpsCliIvfWitnessStatus,
  OpsCliIvfWitness,
  CreateIvfWitnessStepInput,
  ConfirmIvfWitnessStepInput,
} from '../../functions-common/src/ops/cliLoops'

export {
  OPS_CLI_JOB_ORDER_KINDS,
  OPS_CLI_JOB_ORDER_STAGES,
  OPS_CLI_DIALYZER_STATUSES,
  OPS_CLI_IVF_WITNESS_STEPS,
  OPS_CLI_IVF_WITNESS_STATUSES,
  DEFAULT_MAX_DIALYZER_REUSES,
  MIN_MAX_DIALYZER_REUSES,
  MAX_MAX_DIALYZER_REUSES,
  isValidJobOrderKind,
  isValidJobOrderStage,
  isAllowedJobOrderTransition,
  isValidDialyzerStatus,
  isValidIvfWitnessStep,
  isValidIvfWitnessStatus,
  validateJobOrderPhotoPath,
} from '../../functions-common/src/ops/cliLoops'

export type {
  OpsCliTeleConsentState,
  OpsCliTeleSession,
  OpsCliTeleSessionSummary,
  CreateTeleSessionInput,
  GetTeleSessionPublicInfoInput,
  GetTeleSessionPublicInfoResult,
  PrepareTeleConsentOtpInput,
  PrepareTeleConsentOtpResult,
  SubmitTeleConsentInput,
  StartTeleSessionInput,
  CreateTeleOrderKind,
  CreateTeleOrderInput,
} from '../../functions-common/src/ops/cliTele'

export {
  OPS_CLI_TELE_CONSENT_STATES,
  DEFAULT_TELE_CONSENT_VERSION,
  TELE_SESSION_LINK_EXPIRY_MS,
  MAX_TELE_CONSENT_OTP_ATTEMPTS,
  isValidTeleConsentState,
  isAllowedTeleConsentTransition,
  maskPhoneNumber,
  assertZeroPatientData,
} from '../../functions-common/src/ops/cliTele'











